// Data-owning card for the Workflow Engine (WFL) runtime surface. Mirrors ComposeCard: this
// component owns ALL data-fetching/mutations for the workflow surface — WorkflowPanel is the thin
// shell (ReplyStrip + HistoryMenu header) around it, exactly like DraftPanel wraps ComposeCard.
//
// Rendered only as a side effect of a SAM tool call (get_engagement_workflow_state,
// create_engagement, execute_workflow_transition, list_pending_approvals, decide_workflow_approval)
// — there is no dedicated tenant-admin /workflow page/route. `engagementId` absent renders the
// approvals-only view (list_pending_approvals has no engagement scope).
import { rejectionNotice } from './rejectionNotice';
import { detailMatchesOption } from '@/components/Estimates/detailMatchesOption';
import { TRANSITION_READY_HINTS } from './transitionHints';
import { shortBlockedReason } from '@/components/Assessments/blockingMessages';
import { restrictedReasonText, shouldShowRestrictedReason } from './blockedReason';
import { visibleApprovals } from './visibleApprovals';
import { CLOSED_CARD_NOTICE, closedCardClass } from './closedAppearance';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ProposalSurfaceContext, ProposalSurfaceInteractPayload } from '@/components/SAM/samActionTypes';
import type { TFunction } from 'i18next';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '@/hooks';
import { cn } from '@/utils/cn';
import { apiErrorMessage, getErrorDetail, getMessageCode } from '@/utils/apiError';
import { Button } from '@/components/UI/Button';
import { Spinner } from '@/components/UI/Spinner';
import { CardSection } from '@/components/UI/CardSection';
import { Modal } from '@/components/UI/Modal';
import { sv, type Rec } from '@/components/chat/ChatPills';
import { useSamSectionContext, useSamSurfaceContext } from '@/hooks';
import { workspaceApi, useGetContactsQuery } from '@/redux/internal-services/workspaceApi';
import {
  type TaskFull,
  useGetMeetingsListQuery,
  useGetPropertyMediaQuery,
  useGetPropertyNotesQuery,
  useLinkContactToPropertyMutation,
  useCreateContactMutation,
} from '@/redux/internal-services/workspaceApiExtensions';
import { useGetCurrentQuickQuoteQuery } from '@/redux/internal-services/quickQuotesApi';
import { useListNotesByContextQuery } from '@/redux/internal-services/notesApi';
import TaskCard from '@/components/Tasks/TaskCard';
import PropertyRoofEstimateSection from '@/components/QuickQuote/PropertyRoofEstimateSection';
import AssessmentScheduleForm from '@/components/Assessments/AssessmentScheduleForm';
import ProposalAcceptanceForm from '@/components/Proposals/ProposalAcceptanceForm';
import PmAssignmentSelect from '@/components/Jobs/PmAssignmentSelect';
import JobCardDisplayCard from '@/components/Jobs/JobCardDisplayCard';
import JobSchedulingForm from '@/components/Jobs/JobSchedulingForm';
import { daysSpanned } from '@/components/Jobs/jobDuration';
import QaSummaryPanel from '@/components/Jobs/QaSummaryPanel';
import QaFailForm from '@/components/Jobs/QaFailForm';
import QaInspectModal from '@/components/Jobs/QaInspectModal';
import { jobsApi, useAssignPmMutation, useNotifyCustomerOfHoldMutation, useUpdateHoldDetailsMutation, useGetJobCardQuery, useGetQaSummaryQuery, useGetScheduleQuery, useListInvoicesQuery } from '@/redux/internal-services/jobsApi';
import type { Invoice } from '@/redux/internal-services/jobsApi';
import { PrepareInvoiceModal } from '@/components/Workflow/PrepareInvoiceModal';
import { useCanPrepareInvoice } from '@/components/Workflow/InvoiceLifecycleList';
import { BillingSettlementPanel } from '@/components/Workflow/BillingSettlementPanel';
import { isActiveInvoice } from '@/components/Workflow/invoiceStatus';
import { subscribeEngagementSignal } from '@/components/SAM/samSignals';
import { useDownloadJobCardPdf } from '@/components/Jobs/useDownloadJobCardPdf';
import AssessmentCaptureCard from '@/components/Assessments/AssessmentCaptureCard';
import SiteBoardSection from '@/components/Jobs/SiteBoardSection';
import { NotesModal } from '@/components/Jobs/NotesModal';
import MaterialListModal from '@/components/Jobs/MaterialListModal';
import { useGetCurrentEngagementAssessmentQuery } from '@/redux/internal-services/assessmentsApi';
import { useGetPropertyDetailQuery } from '@/redux/internal-services/workspaceApiExtensions';
import WorkspaceMeetingCard from '@/components/Meetings/WorkspaceMeetingCard';
import ProposalDisplayCard from '@/components/Proposals/ProposalDisplayCard';
import MaterialBasisPicker, {
  type BasisCode, buildGenerationMethod,
} from '@/components/Estimates/MaterialBasisPicker';
import {
  useListEstimatesForEngagementQuery,
  useDeleteEstimateOptionMutation, useGetEstimateQuery, useGetEstimateValidationQuery,
  useGenerateEstimateMutation,
  useListEstimateVersionsQuery,
  useForkEstimateVersionMutation,

  estimatesApi,
} from '@/redux/internal-services/estimatesApi';
import EstimateEditorModal from '@/components/Estimates/EstimateEditorModal';
import { proposalsApi, useGetCurrentProposalQuery } from '@/redux/internal-services/proposalsApi';
import {
  useDecideApprovalMutation,
  useExecuteWorkflowTransitionMutation,
  useGetEngagementByIdQuery,
  useGetEngagementWorkflowStateQuery,
  useGetWorkflowCustomizationQuery,
  useListAvailableTransitionsQuery,
  useGetWorkflowStatusNarrationQuery,
  useListEngagementApprovalsQuery,
  useListPendingApprovalsQuery,
  usePutEngagementOnHoldMutation,
  useResumeEngagementMutation,
  useCloseEngagementEarlyMutation,
  useUpdateEngagementDataMutation,
  usePreviewGovernanceOptionsQuery,
  type ApprovalRequestOut,
  type AvailableTransition,
} from '@/redux/internal-services/workflowEngineApi';
import { StageRail } from './StageRail';

// Maps well-known engagement data field names to the system role code used to look up eligible users.
const ROLE_LOOKUP_FIELDS: Record<string, string> = {
  project_manager_id: 'project_manager',
  pm_id: 'project_manager',
  crew_id: 'field_crew',
  sales_agent_id: 'sales_agent',
  // Sprint 6 JOB-FEAT-05 — qa_responsible_assignment_required gate on finish_field_work.
  qa_responsible_user_id: 'project_manager',
};

const RoleLookupSelect: React.FC<{ role: string; value: string; onChange: (v: string) => void }> = ({ role, value, onChange }) => {
  const { data, isLoading } = usePreviewGovernanceOptionsQuery(role);
  const options = data?.options ?? [];
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-lg border border-ui-border bg-ui-input px-2.5 py-1.5 text-xs text-ui-text focus:border-ui-ring focus:outline-none focus:ring-1 focus:ring-ui-ring"
    >
      <option value="">{isLoading ? 'Loading…' : 'Select a user…'}</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
};

/** Inline picker that resolves the `add_contact_participant` gate — either link an existing
 *  tenant contact or create a new one (name + phone) and link them in one step. */
const ContactLinkSelect: React.FC<{ propertyId: string; engagementId: string; onLinked: () => void }> = ({ propertyId, engagementId, onLinked }) => {
  const { t } = useTranslation();
  const { data: contacts = [], isLoading } = useGetContactsQuery();
  const [linkContact, { isLoading: linking }] = useLinkContactToPropertyMutation();
  const [createContact, { isLoading: creating }] = useCreateContactMutation();

  const [mode, setMode] = useState<'existing' | 'new'>('existing');
  const [selected, setSelected] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [formError, setFormError] = useState('');

  const doLink = async (contactId: string) => {
    await linkContact({ propertyId, contact_id: contactId, relationship_type: 'engagement_contact', source_engagement_id: engagementId }).unwrap();
    onLinked();
  };

  const handleExisting = async () => {
    if (!selected) return;
    setFormError('');
    try { await doLink(selected); } catch { setFormError(t('workflow.contactLinkFailed', 'Failed to link contact.')); }
  };

  const handleCreate = async () => {
    if (!firstName.trim() && !lastName.trim()) {
      setFormError(t('workflow.contactNameRequired', 'First or last name is required.'));
      return;
    }
    if (!phone.trim()) {
      setFormError(t('workflow.contactPhoneRequired', 'Phone number is required.'));
      return;
    }
    setFormError('');
    try {
      const created = await createContact({
        first_name: firstName.trim() || undefined,
        last_name: lastName.trim() || undefined,
        phones: [{ value: phone.trim(), is_primary: true }],
      }).unwrap();
      await doLink(created.contact_id);
    } catch { setFormError(t('workflow.contactCreateFailed', 'Failed to create contact.')); }
  };

  const inputCls = 'w-full rounded-lg border border-ui-border bg-ui-input px-2.5 py-1.5 text-xs text-ui-text focus:border-ui-ring focus:outline-none focus:ring-1 focus:ring-ui-ring';

  return (
    <div className="mt-1.5 space-y-2 rounded-lg border border-ui-border bg-ui-subtle p-2.5">
      {/* Mode tabs */}
      <div className="flex gap-1 rounded-md border border-ui-border bg-ui-input p-0.5">
        {(['existing', 'new'] as const).map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => { setMode(m); setFormError(''); }}
            className={cn(
              'flex-1 rounded px-2 py-1 text-xs font-medium transition-colors',
              mode === m ? 'bg-accent-fill text-on-accent' : 'text-ui-text2 hover:text-ui-text',
            )}
          >
            {m === 'existing' ? t('workflow.existingContact', 'Existing') : t('workflow.newContact', 'New contact')}
          </button>
        ))}
      </div>

      {mode === 'existing' ? (
        <select
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          className={inputCls}
        >
          <option value="">{isLoading ? t('common.loading', 'Loading…') : t('workflow.selectContact', 'Select a contact…')}</option>
          {contacts.map((c) => (
            <option key={c.contact_id} value={c.contact_id}>
              {[c.first_name, c.last_name].filter(Boolean).join(' ') || c.email || c.phone}
            </option>
          ))}
        </select>
      ) : (
        <div className="space-y-1.5">
          <div className="grid grid-cols-2 gap-1.5">
            <input
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder={t('workflow.firstName', 'First name')}
              className={inputCls}
            />
            <input
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder={t('workflow.lastName', 'Last name')}
              className={inputCls}
            />
          </div>
          <input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder={t('workflow.phone', 'Phone number')}
            className={inputCls}
          />
        </div>
      )}

      {formError && <p className="text-xs text-danger-text">{formError}</p>}

      <div className="flex justify-end">
        <Button
          size="sm"
          loading={linking || creating}
          disabled={mode === 'existing' ? !selected : (!firstName.trim() && !lastName.trim()) || !phone.trim()}
          onClick={() => void (mode === 'existing' ? handleExisting() : handleCreate())}
        >
          {t('workflow.addContact', 'Add contact')}
        </Button>
      </div>
    </div>
  );
};

// Transition categories that end the engagement's path down this branch (cancel/reject the
// engagement) rather than moving it forward — surfaced as a red/danger action so a user doesn't
// tap it by mistake, mirroring the destructive-button convention used elsewhere in this file
// (Close Engagement Early).
const TERMINAL_CATEGORIES = new Set(['cancel', 'reject']);

// JOB-FEAT-04/05 — mockup's second "Exception · reason" badge, keyed by status_code. Frontend-
// only lookup (see the render site's own comment on why) — values are the short reason text, not
// re-derived from anything the backend returns.
const EXCEPTION_STATUS_LABELS: Record<string, string> = {
  job_execution_on_hold: 'on hold',
};


// Status codes where the job execution summary (crew/date/materials) is shown — everything from
// the moment field work starts through billing and close. Not shown during the sales/prep pipeline
// before the crew is even assigned.
const JOB_EXEC_STATUSES = new Set([
  'job_execution', 'job_execution_on_hold', 'job_executed',
  'qa_inspected', 'job_completed', 'billing_settled',
  'post_job_in_progress', 'closed',
]);

// Job statuses from Job Scheduled onward, where the pre-job Project handoff / prep sections give way
// to the job's own cards. (Billing is NOT tied to these — it shows wherever the workflow definition
// attaches the `billing_settlement_panel` module; see BILLING_MODULE.)
const POST_SCHEDULE_STATUSES = new Set([
  'job_scheduled', 'job_prepared', 'job_execution', 'job_execution_on_hold',
  'job_executed', 'qa_inspected', 'job_completed', 'billing_settled',
  'post_job_in_progress', 'closed',
]);

// The Billing Settlement panel is a workflow business module (WFL POST side): it renders only at
// statuses whose definition `module_displays` binds this code (seeded Job Initiated → Closed;
// platform admins move it per workflow, tenants can toggle a binding off). The API refuses invoice
// writes at any other status, so the panel and the rules can never disagree.
const BILLING_MODULE = 'billing_settlement_panel';
// Project handoff summary + Job Card — workflow business modules too (seeded at Job Initiated).
const HANDOFF_MODULE = 'project_handoff_panel';
const JOB_CARD_MODULE = 'job_card_panel';
// Site Board (COM-FEAT-02): shown only where the workflow binds it — the API refuses reads/posts
// at any other status, so the feed and its rules can never disagree.
const SITE_BOARD_MODULE = 'site_board_panel';
// Layout only (WHERE on the card, not WHETHER): the statuses whose stage card embeds the Site Board in
// its own collapsible. Everywhere else the workflow binds it, it renders standalone.
const SITE_BOARD_IN_STAGE_CARD = new Set(['job_scheduled', 'job_prepared', 'job_execution', 'job_executed', 'qa_inspected', 'job_completed']);

/** Settled = at least one active invoice, every active one Paid, and (when the contract value is
 *  known) nothing left to invoice. Derived from the invoices — not from a workflow status code. */
function invoicesSettled(invoices: Invoice[], billable: number | null | undefined): boolean {
  const active = invoices.filter(isActiveInvoice);
  if (active.length === 0 || active.some((i) => i.status !== 'Paid')) return false;
  const invoiced = active.reduce((sum, i) => sum + (i.amount ?? 0), 0);
  return billable == null || invoiced >= billable - 0.005;
}

// Statuses where a dedicated collapsible (Schedule prep / Readiness confirmed) is shown instead of
// the individual project-handoff / QA / execution-summary / site-board standalone sections.
const SCHEDULE_CARD_STATUSES = new Set(['job_scheduled', 'job_prepared']);

// Statuses where the "Execution updates" / "Execution summary" collapsible replaces the standalone
// Execution Summary and Site Board sections.
const EXECUTION_CARD_STATUSES = new Set(['job_execution', 'job_execution_on_hold', 'job_executed', 'qa_inspected', 'job_completed', 'billing_settled']);

// Sales-process status codes (workflow_definition_seed.py) reached BEFORE a real measurement
// exists — Assessment Completed is where the actual estimate/measurement takes over, so the
// Quick Quote stays useful up through Assessment Scheduled and disappears from there on.
// Data-driven inline-capture-form registry for TransitionResolution.action_type ===
// 'business_capture' (backend: enums.GateConditionType.REQUIRED_BUSINESS_CAPTURE). A transition
// blocked by a `required_business_capture` gate names a governance-action registry key
// (resolution.action_key); if the gate author also opted in with capture_ui_enabled AND this map
// has an entry for that key, clicking the transition renders the form here (collect the data
// FIRST, actually do the thing, then fire the transition) instead of just showing the blocked
// reason. Replaces the old SCHEDULE_ASSESSMENT_CODE hardcoded transition_code === 'schedule_assessment'
// special-case — any workflow/transition can now opt into this UX purely via gate config, and a
// customized tenant workflow using a different transition_code for the same step gets it too.
// Every entry must accept exactly {engagementId, onScheduled, onCancel} (AssessmentScheduleForm's
// existing shape) so this registry can render any of them generically.
const BUSINESS_CAPTURE_COMPONENTS: Record<
  string,
  React.FC<{ engagementId: string; onScheduled: () => void; onCancel: () => void }>
> = {
  meeting_schedule_for_assessment: AssessmentScheduleForm,
  proposal_accepted_option: ProposalAcceptanceForm,
};

// Display half of the 'meeting_schedule_for_assessment' business module (see
// BUSINESS_MODULE_DISPLAY_COMPONENTS below) — finds the meeting by engagement context tag, not a
// stored id (no separate "assessment session" record exists per the module's own design), same
// query AssessmentScheduleForm's own submit creates a row for.
const AssessmentMeetingDisplayCard: React.FC<{ engagementId: string }> = ({ engagementId }) => {
  // `meeting_type: 'assessment'` matters: without it this took the most recent meeting of ANY
  // type on the engagement, so a later follow-up or proposal-review meeting rendered as the
  // assessment's. Kept identical to AssessmentCaptureCard's own lookup and to the backend's
  // (WorkflowService, meeting_type="assessment") — three places asking the same question have to
  // ask it the same way.
  const { data: meetings } = useGetMeetingsListQuery(
    {
      context_type: 'engagement', context_entity_id: engagementId, meeting_type: 'assessment',
      sort_by: 'start_time', sort_dir: -1, limit: 1,
    },
  );
  const meeting = meetings?.data?.[0];
  if (!meeting) return null;
  return <WorkspaceMeetingCard meeting={meeting} />;
};



// Display for the 'estimate_summary_panel' business module — "Estimate & proposal prep".
// Line-level editing (price/qty overrides, margin/discount, recalculate) has a real REST path
// (app/api/v1/endpoints/estimates.py's /lines/*, /commercial-terms, /alternate-items and
// /suppliers routes) wrapping the SAME EstimateService methods app.domain.estimates.agent_tools
// already exposed to SAM — no new business logic, just a UI onto it. "Save as new version"
// (fork_new_version) still has no REST/SAM exposure anywhere, so edits here update the current
// DRAFT version in place, same as the SAM tools already do.
//
// Collapses behind a one-line summary like AssessmentCaptureCard's "Assessment meeting &
// capture" card — same "tap to expand" pattern, folding in the source-file/capture-count meta
// row and the relevant open tasks so this is one entry point instead of three.
const EstimateSummaryDisplayCard: React.FC<{
  engagementId: string; propertyId?: string; tasks?: TaskFull[]; onOpenAssessment?: () => void;
  estimateOpenSignal?: number; previewOpenSignal?: number;
  /** SAM's proposal_surface_interact tool, surface='view_estimate' — option switch, section
   *  expand/collapse, item open, scroll, close, all applied to the estimate editor modal. */
  estimateInteractSignal?: ProposalSurfaceInteractPayload & { tick: number } | null;
  /** Same tool, surface='preview_proposal' — forwarded to the nested ProposalDisplayCard, since
   *  at proposal_prepared stage the preview modal lives there, not in a standalone module. */
  previewInteractSignal?: ProposalSurfaceInteractPayload & { tick: number } | null;
  /** Reports the estimate surface's visible state on every change, whoever caused it (SAM or a
   *  manual click) — see WorkflowCard's own onProposalSurfaceContext for why this exists. */
  onSurfaceContext?: (state: {
    estimate_open: boolean; option_id?: string; option_label?: string;
    materials_expanded: boolean; services_expanded: boolean; preview_open: boolean;
    totals?: ProposalSurfaceContext['estimate_totals'];
    materials?: ProposalSurfaceContext['estimate_materials'];
    services?: ProposalSurfaceContext['estimate_services'];
  }) => void;
}> = ({ engagementId, propertyId, tasks, onOpenAssessment, estimateOpenSignal, previewOpenSignal, estimateInteractSignal, previewInteractSignal, onSurfaceContext }) => {
  const { t } = useTranslation();
  const { data: estimates, isLoading: estimatesLoading } = useListEstimatesForEngagementQuery(engagementId);
  // For the header only: how many of this set's options the proposal draft actually carries.
  // Shares the cache with ProposalDisplayCard's own query, so this adds no extra request.
  const { data: proposal } = useGetCurrentProposalQuery(engagementId, { skip: !engagementId });
  // Every option in this engagement's estimate_set (Good/Better/Best, alternates — see
  // estimates/models.py's module docstring) is a sibling, not a version history of one estimate.
  // `estimates[0]` used to be treated as THE estimate; this card now shows every option as a tab,
  // defaulting to the first, and everything below still operates on "whichever option tab is
  // active" (kept the name `latestEstimateCode` to minimize the diff — it now carries an
  // `option_id`, meaning "active option", not "most recent").
  const [selectedEstimateCode, setSelectedEstimateCode] = useState<string | undefined>(undefined);
  const activeEstimate = (selectedEstimateCode && estimates?.find((e) => e.option_id === selectedEstimateCode)) || estimates?.[0];
  const latestEstimateCode = activeEstimate?.option_id;
  const { data: estimateDetail } = useGetEstimateQuery(
    { estimateCode: latestEstimateCode as string, engagementId },
    { skip: !latestEstimateCode },
  );
  const versionCode = estimateDetail?.version.estimate_id;
  const { data: validation } = useGetEstimateValidationQuery(versionCode as string, { skip: !versionCode });

  // Same source-file/photo/voice-note/note counts AssessmentCaptureCard's own footer chips
  // use — reads the same current assessment, so the two cards agree on what was captured.
  const { data: assessment } = useGetCurrentEngagementAssessmentQuery(engagementId);
  const { data: notes = [] } = useListNotesByContextQuery(
    assessment ? { contextType: 'assessment', entityId: assessment.assessment_id } : { contextType: 'assessment', entityId: '' },
    { skip: !assessment },
  );
  const { data: propertyMedia = [] } = useGetPropertyMediaQuery(propertyId as string, { skip: !propertyId });
  const photoCount = propertyMedia.filter((m) => m.engagement_id === engagementId && m.media_type === 'photo').length;
  const voiceNoteCount = notes.filter((n) => n.source === 'voice').length;
  const documentCount = assessment?.measurement_report ? 1 : 0;

  const [expanded, setExpanded] = useState(false);
  // The estimate's own chips/actions fold behind one line — see the entry button below.
  const [estimateOpen, setEstimateOpen] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const estimateSectionRef = useRef<HTMLDivElement>(null);
  // The proposal row's "View estimate": open the estimate AND bring it into view. Setting the open
  // flag alone did nothing visible once the estimate was already open (QA #8).
  const revealEstimate = () => {
    // Proposal Prepared: the estimate is frozen, so "View estimate" opens it read-only right away.
    if (isProposalPrepared) { startEdit(); return; }
    setEstimateOpen(true);
    requestAnimationFrame(() => estimateSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  };

  // SAM's proposal_prepared_open tool (and get_estimate/update_estimate_line/swap_estimate_material/
  // set_estimate_pricing/recalculate_estimate) bumps this signal → expand the whole collapsed card
  // into view, not just the inner estimateOpen flag, same as AssessmentCaptureCard's own signal —
  // without setExpanded/scrollIntoView here the card silently stayed collapsed while SAM answered.
  useEffect(() => {
    if (estimateOpenSignal) {
      setEstimateOpen(true);
      setExpanded(true);
      rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [estimateOpenSignal]);
  const [previewOpenFromSignal, setPreviewOpenFromSignal] = useState(0);
  // Same gap as estimateOpenSignal above, for the proposal preview: get_current_proposal/
  // toggle_proposal_section/set_recommended_proposal_option bump this to open the nested
  // ProposalDisplayCard's preview modal, but without setExpanded/scrollIntoView here the OUTER
  // "Estimate set & proposal draft" card stayed collapsed the whole time — the modal opened
  // behind a fold the user still had to tap open themselves to see anything.
  useEffect(() => {
    if (previewOpenSignal) {
      // The estimate editor is a z-modal dialog and the preview a z-overlay one, so a preview opened
      // while the editor is up appeared BEHIND it (QA #31): close the editor first.
      setEditMode(false);
      setPreviewOpenFromSignal((v) => v + 1);
      setExpanded(true);
      rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [previewOpenSignal]);
  // A simple history list (version_no/status/total/date), not a way to browse an old version's
  // full line items inline — opening the estimate always shows the CURRENT version's lines,
  // matching every other action on this card. Only fetched once the picker is actually opened.
  const [versionsOpen, setVersionsOpen] = useState(false);
  // Fetched once the card is actually expanded (not gated further on the versions panel itself
  // being open) so the "Versions (N)" button shows a real count before the user clicks it —
  // but not fetched for every collapsed card on the page, matching this component's other
  // load-on-expand choices (e.g. the map in PropertyRoofEstimateSection).
  const { data: versions } = useListEstimateVersionsQuery(
    { estimateCode: latestEstimateCode as string, engagementId },
    { skip: !latestEstimateCode || !expanded },
  );
  const [forkVersion, { isLoading: forking }] = useForkEstimateVersionMutation();
  const [forkError, setForkError] = useState('');
  const onForkVersion = async () => {
    if (!latestEstimateCode) return;
    setForkError('');
    try {
      await forkVersion({ estimateCode: latestEstimateCode, engagementId }).unwrap();
    } catch (e) {
      setForkError(apiErrorMessage(e, t, t('workflow.estimateForkFailed', 'Could not create a new version to edit.')));
    }
  };
  // "+ Add option" — a new sibling option (estimate_set.estimate_options[]) against a WBS
  // template the user picks (e.g. a cheaper/premium alternate), reusing the same
  // generate-estimate endpoint the auto-generated
  // first option came from. WBS templates only fetched once the picker is actually open.
  const dispatch = useDispatch();
  // Deleting a priced option loses real work, so it is confirmed in a dialog that NAMES the
  // option rather than an inline tap target on a tab that already carries a name and a price.
  const [optionToDelete, setOptionToDelete] = useState<{ id: string; label: string } | null>(null);
  const [deleteOptionError, setDeleteOptionError] = useState('');
  const [deleteEstimateOption, { isLoading: deletingOption }] = useDeleteEstimateOptionMutation();

  const confirmDeleteOption = async () => {
    if (!optionToDelete) return;
    setDeleteOptionError('');
    try {
      await deleteEstimateOption({ engagementId, optionId: optionToDelete.id }).unwrap();
      // The option is gone from the proposal too (the backend rebuilds the draft), and that
      // document lives in a different api slice, so its cache has to be told.
      dispatch(proposalsApi.util.invalidateTags([{ type: 'Proposal', id: engagementId }]));
      if (selectedEstimateCode === optionToDelete.id) setSelectedEstimateCode(undefined);
      setOptionToDelete(null);
    } catch (e) {
      setDeleteOptionError(apiErrorMessage(e, t, t('workflow.estimateDeleteOptionFailed', 'Could not delete this option.')));
    }
  };

  const [showAddOption, setShowAddOption] = useState(false);
  const [newOptionLabel, setNewOptionLabel] = useState('');
  const [addOptionError, setAddOptionError] = useState('');
  // The new option's material selection basis, set in the same dialog as its name — see
  // MaterialBasisPicker. PREFERRED_MANUFACTURER matches the editor's own default for an option
  // that expresses no basis, so opening the dialog and pressing Create is the previous behaviour.
  const [newOptionBasis, setNewOptionBasis] = useState<BasisCode>('PREFERRED_MANUFACTURER');
  const [newOptionSupplier, setNewOptionSupplier] = useState<string | null>(null);
  const [newOptionManufacturer, setNewOptionManufacturer] = useState<string | null>(null);
  // Dismissing clears the whole draft — name, basis and any error — so reopening starts clean
  // rather than resuming a half-made option the user already walked away from.
  const closeAddOption = () => {
    setShowAddOption(false);
    setAddOptionError('');
    setNewOptionLabel('');
    setNewOptionBasis('PREFERRED_MANUFACTURER');
    setNewOptionSupplier(null);
    setNewOptionManufacturer(null);
  };
  const [generateEstimate, { isLoading: addingOption }] = useGenerateEstimateMutation();
  const submitAddOption = async () => {
    if (!newOptionLabel.trim()) return;
    setAddOptionError('');
    try {
      // No wbs_code: the backend resolves the tenant's default package for this engagement type.
      const created = await generateEstimate({
        engagement_id: engagementId,
        estimate_option_label: newOptionLabel.trim(),
        // The basis is part of CREATING the option, not an afterthought: the whole reason a set
        // holds several options is that each one answers the price differently ("GAF via ABC
        // Supply" beside "lowest cost"), so the option is generated against the chosen basis
        // rather than generated on the default and then re-priced.
        generation_method: buildGenerationMethod(newOptionBasis, newOptionSupplier, newOptionManufacturer),
      }).unwrap();
      setSelectedEstimateCode(created.estimate.option_id);
      setNewOptionLabel('');
      setShowAddOption(false);
      // Creating an option is the start of working on it, not the end — drop the user straight
      // into the editor rather than leaving them to find it.
      setEditMode(true);
    } catch (e) {
      setAddOptionError(apiErrorMessage(e, t, t('workflow.estimateAddOptionFailed', 'Could not create this option.')));
    }
  };
  // The card no longer edits lines itself — EstimateEditorModal does, with its own autosave —
  // so the per-line draft state, the shared override_reason and the inline "+ Add material" /
  // "+ Add service" forms that sat beside them are gone with the strip they belonged to. Adding
  // a line now happens in the modal, against the catalogue rather than as a typed custom row.
  const [editMode, setEditMode] = useState(false);
  const [marginPercent, setMarginPercent] = useState('');
  const [discountPercent, setDiscountPercent] = useState('');
  const [sectionsState, setSectionsState] = useState<{
    materials_expanded: boolean; services_expanded: boolean;
    totals?: ProposalSurfaceContext['estimate_totals'];
    materials?: ProposalSurfaceContext['estimate_materials'];
    services?: ProposalSurfaceContext['estimate_services'];
  }>({ materials_expanded: false, services_expanded: false });
  const [previewOpenState, setPreviewOpenState] = useState(false);
  useEffect(() => {
    onSurfaceContext?.({
      estimate_open: editMode,
      option_id: activeEstimate?.option_id,
      option_label: activeEstimate?.estimate_description || activeEstimate?.estimate_name,
      preview_open: previewOpenState,
      ...sectionsState,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode, activeEstimate?.option_id, sectionsState, previewOpenState]);

  // SAM's proposal_prepared_open tool, view='view_estimate' — "open the estimate" means the
  // actual editor modal (Materials/Work & Other Services), not just revealing the tab strip that
  // sits above it. Without this, a bare "open the estimate" left the modal closed until the user
  // also asked to switch an option (which does open it) — found via live SAM testing.
  useEffect(() => { if (estimateOpenSignal) setEditMode(true); }, [estimateOpenSignal]);

  // SAM's proposal_surface_interact tool, surface='view_estimate'. select_option/close act on
  // this card's own state directly; everything else (expand/collapse a section, open an item,
  // scroll) is forwarded into EstimateEditorModal, opening it first if it wasn't already —
  // "expand materials" should work even as the very first thing SAM does.
  const [estimateEditorSignal, setEstimateEditorSignal] = useState<ProposalSurfaceInteractPayload & { tick: number } | null>(null);
  useEffect(() => {
    if (!estimateInteractSignal) return;
    const { action, option_id } = estimateInteractSignal;
    if (action === 'select_option') {
      if (option_id) setSelectedEstimateCode(option_id);
      setEstimateOpen(true);
      setEditMode(true);
    } else if (action === 'close') {
      setEditMode(false);
    } else {
      setEditMode(true);
      setEstimateEditorSignal(estimateInteractSignal);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estimateInteractSignal?.tick]);

  const startEdit = () => {
    setMarginPercent(estimateDetail?.commercial_summary ? String(estimateDetail.commercial_summary.margin.percent ?? 0) : '');
    setDiscountPercent(estimateDetail?.commercial_summary ? String(estimateDetail.commercial_summary.discount.percent ?? 0) : '');
    setEditMode(true);
  };


  if (estimatesLoading) {
    return <div className="flex items-center gap-2 py-1"><Spinner size="sm" /><span className="text-xs text-ui-text3">{t('workflow.estimateLoading', 'Loading estimate…')}</span></div>;
  }
  if (!latestEstimateCode) {
    return (
      <p className="rounded-lg border border-dashed border-ui-border bg-ui-subtle px-3 py-3 text-xs text-ui-text3">
        {t('workflow.estimateNone', 'No estimate yet for this engagement — ask SAM to generate one.')}
      </p>
    );
  }
  if (!estimateDetail) return null;

  const openTasks = (tasks ?? []).filter((tk) => !TASK_DONE.has(tk.status) && tk.status !== 'cancelled');

  // Set-level, not option-level. This used to read `{option_id} · v{n}` — the identity of ONE
  // option — inside a card that owns the whole set and the proposal drafted from it. Which option
  // that was depended on list order, so the header named an arbitrary member of the set as though
  // it were the subject.
  //
  // No dollar amount on purpose: the expanded body already shows the full commercial breakdown,
  // and repeating just the total on the fold toggle read as a duplicate rather than a preview.
  const optionCount = estimates?.length ?? 0;
  // Options the proposal actually carries. Read from the PROPOSAL rather than counting
  // `selected_for_proposal` across options: the proposal's own `estimate_options` is what it was
  // built from, and the two can differ once an option is added to the set after the draft exists.
  const optionsInDraft = proposal?.estimate_options?.length ?? 0;
  const summaryLine = [
    // From the option LISTING, not from the opened option's identity block: `EstimateOption`
    // describes one option and carries no set id. Every row in a listing belongs to the same
    // set for an engagement, so the first row's is the set's.
    estimates?.[0]?.estimate_set_id,
    optionCount > 0 ? t('workflow.estimateSetOptionCount', '{{count}} option', { count: optionCount }) : null,
    proposal ? t('workflow.estimateSetInProposalDraft', '{{count}} in proposal draft', { count: optionsInDraft }) : null,
    // Kept beyond the three parts the design calls for: it is real information, and it was added
    // deliberately (an estimate card saying "1 task open" read as the estimate's own task — every
    // task on the platform is engagement-scoped, so naming the scope was the fix). Drop this line
    // if the header should match the design exactly.
    openTasks.length > 0
      ? t('tasks.openOnEngagement', '{{count}} engagement task open', { count: openTasks.length })
      : null,
  ].filter(Boolean).join(' · ');

  // At proposal_prepared stage the card is always shown expanded — no toggle needed since the
  // ProposalPreparedCard is already the compact summary and the estimate is the main content.
  const isProposalPrepared = proposal?.status === 'DRAFT' && proposal?.is_editable === false;

  return (
    <div ref={rootRef}>
      {/* Collapsed state: standalone pill with its own border */}
      {!isProposalPrepared && !expanded && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="group flex w-full items-center justify-between gap-2 bg-ui-card px-3 py-2.5 text-left transition-colors hover:bg-ui-hover"
        >
          <div className="min-w-0">
            <p className="text-sm font-semibold text-ui-text">{t('workflow.estimateSetProposalDraftTitle', 'Estimate set & proposal')}</p>
            <p className="mt-0.5 truncate text-xs text-ui-text3">{summaryLine}</p>
          </div>
          <span className="flex flex-shrink-0 items-center gap-1 text-xs font-medium text-accent-text group-hover:text-ui-text transition-colors">
            {t('workflow.view', 'View')}
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </span>
        </button>
      )}

      {/* Expanded state: single border wraps toggle + content, no gap */}
      {(expanded || isProposalPrepared) && (
      <div className="overflow-hidden">
        {!isProposalPrepared && (
          <button
            type="button"
            onClick={() => setExpanded(false)}
            className="group flex w-full items-center justify-between gap-2 border-b border-ui-border bg-ui-card px-3 py-2.5 text-left transition-colors hover:bg-ui-hover"
          >
            <div className="min-w-0">
              <p className="text-sm font-semibold text-ui-text">{t('workflow.estimateSetProposalDraftTitle', 'Estimate set & proposal')}</p>
              <p className="mt-0.5 truncate text-xs text-ui-text3">{summaryLine}</p>
            </div>
            <span className="flex flex-shrink-0 items-center gap-1 text-xs font-medium text-accent-text group-hover:text-ui-text transition-colors">
              {t('workflow.hide', 'Hide')}
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className="rotate-180">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </span>
          </button>
        )}
        {!isProposalPrepared && (assessment?.measurement_report || photoCount > 0 || voiceNoteCount > 0 || documentCount > 0 || onOpenAssessment) && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-ui-border bg-ui-subtle px-3 py-1.5 text-xs text-ui-text3">
            {assessment?.measurement_report?.filename && (
              <span className="truncate">{t('workflow.estimateSourceFile', 'Source file')}: {assessment.measurement_report.filename}</span>
            )}
            {photoCount > 0 && <span>{photoCount} photo{photoCount === 1 ? '' : 's'}</span>}
            {voiceNoteCount > 0 && <span>{voiceNoteCount} voice note{voiceNoteCount === 1 ? '' : 's'}</span>}
            {documentCount > 0 && <span>{documentCount} document{documentCount === 1 ? '' : 's'}</span>}
            {onOpenAssessment && !isProposalPrepared && (
              <button
                type="button"
                onClick={onOpenAssessment}
                className="font-semibold text-accent-text hover:underline"
              >
                {t('workflow.openAssessmentCard', 'Open assessment card')}
              </button>
            )}
          </div>
        )}
        {/* The proposal draft renders INSIDE this fold, above the estimate it is priced from —
            the approved mockup's "Estimate set & proposal draft" is a single foldable wrapper
            holding the capture chips, the proposal draft and the estimate behind it, not three
            sibling blocks. ProposalDisplayCard still owns its own row, template picker and
            document modal; it is only positioned here.

            It is the SAME module (`proposal_summary_panel`) the workflow binds in its own right,
            so WorkflowCard suppresses the standalone copy whenever this card is also active —
            exactly how the meeting card is suppressed when AssessmentCaptureCard hosts it. At
            proposal_approved/sent/accepted/rejected this card is not bound at all and the
            proposal renders standalone, which is why it stays a real module rather than becoming
            a private child of this one. */}
        <div className="border-b border-ui-border">
          <ProposalDisplayCard
            engagementId={engagementId}
            propertyId={propertyId}
            onViewEstimate={revealEstimate}
            onOpenAssessment={onOpenAssessment}
            previewOpenSignal={previewOpenFromSignal}
            interactSignal={previewInteractSignal}
            onSurfaceContext={({ preview_open }) => setPreviewOpenState(preview_open)}
          />
        </div>
        {/* At proposal_prepared stage the estimate reveals itself via "View estimate" in the
            ProposalPreparedCard — no always-visible toggle needed. */}
        {!isProposalPrepared && (
        <button
          type="button"
          onClick={() => setEstimateOpen((v) => !v)}
          className="flex w-full items-center gap-2.5 border-b border-ui-border bg-ui-subtle px-3 py-2 text-left hover:bg-ui-hover"
        >
          <span className="min-w-0 flex-1">
            <span className="block text-xs font-bold text-ui-text">
              {t('workflow.estimateBehindProposal', 'Estimate behind this proposal')}
            </span>
            <span className="mt-0.5 block truncate text-xs text-ui-text3">
              {t('workflow.estimateOptionCount', '{{count}} option', { count: (estimates ?? []).length })}
              {activeEstimate && ` · ${activeEstimate.estimate_description || activeEstimate.estimate_name}`}
              {estimateDetail.commercial_summary
                && ` · ${fmtMoney(estimateDetail.commercial_summary.grand_total, estimateDetail.commercial_summary.currency)}`}
            </span>
          </span>
          <span className="flex-shrink-0 text-xs text-ui-text3">{estimateOpen ? '▲' : '▼'}</span>
        </button>
        )}

        {estimateOpen && (<>
        {/* Every option in this engagement's estimate_set is a sibling pricing option (Good/
            Better/Best, alternates) — shown as tabs so a Sales Agent can build/compare more than
            one without losing whichever one they started with. Always shown (even with just one
            option) so "+ Add option" has a permanent home. Card-per-option layout (rather than a
            row of pills) so each tab can carry its own price/version without truncating. */}
        {/* ONE continuous card for the whole estimate surface — options strip, the selected
            option's totals and its actions all live inside a single border/radius now instead
            of two stacked boxes with their own outlines, which is what made this read as two
            unrelated components stitched together. Sub-sections divide with an inner border-b,
            same as before; only the outer double-border is gone. */}
        <div ref={estimateSectionRef} className="rounded-[10px] border border-ui-border">
          {/* Real tab-control styling now, not a row of price chips above an unrelated card:
              the active tab drops its bottom border and shares the panel's own background, so
              it visually fuses into the content beneath it (the `-mb-px` pulls it across the
              seam); every inactive tab keeps a muted fill and stays behind that seam. The
              "Option {{n}}" label is gone — with the total folded into the tab itself, and the
              tab already sitting directly above its own panel, numbering it added nothing the
              position doesn't already say. no-scrollbar: this row scrolls horizontally but the
              native scrollbar track was the other tell that this was a distinct, separately-
              scrolling widget rather than part of the card around it. */}
          {/* `relative z-0` CONTAINS the active tab's z-10. Every ancestor between here and the
              page root is statically positioned, so without a stacking context of its own that
              z-10 competed globally — and tied with the sticky engagement header, which is also
              z-10. DOM order broke the tie in the tab's favour, so scrolling this card slid the
              purple option tab out over the header. The z-10 below is only ever meant to lift the
              active tab above its own panel's top border. */}
          <div className="no-scrollbar relative z-0 flex items-center gap-1.5 overflow-x-auto">
            {(() => {
              const editState = estimates?.[0]?.edit_state;
              if (editState === 'FROZEN') return <span className="flex-shrink-0 rounded-md bg-ui-subtle px-2 py-0.5 text-xs font-bold text-ui-text2">{t('workflow.estimateOptionsFrozen', 'Frozen').toUpperCase()}</span>;
              if (editState === 'REWORK') return <span className="flex-shrink-0 rounded-md bg-warning/10 px-2 py-0.5 text-xs font-bold text-warning-text">{t('workflow.estimateOptionsRework', 'Needs rework').toUpperCase()}</span>;
              return null;
            })()}
            {(estimates ?? []).map((est) => {
              const active = est.option_id === latestEstimateCode;
              return (
                <button
                  key={est.option_id}
                  type="button"
                  onClick={() => setSelectedEstimateCode(est.option_id)}
                  className={`relative -mb-px flex-shrink-0 border py-2 pb-[13px] pl-3 pr-6 text-left transition-colors ${
                    active
                      ? 'z-10 border-accent border-b-accent bg-accent-fill text-on-accent'
                      // bg-ui-hover sits 2/255 off bg-ui-subtle — indistinguishable as a hover
                      // cue, so inactive tabs get the brand tint instead, same as every other
                      // hoverable control on this card (buttons below use hover:bg-ui-hover for
                      // a neutral bg, but THIS bg already reads as neutral at rest).
                      : 'border-transparent border-b-ui-border bg-ui-subtle text-ui-text2 hover:border-ui-border hover:bg-ui-hover hover:text-ui-text'
                  }`}
                >
                  <div className="whitespace-nowrap text-xs font-semibold">
                    {est.estimate_description || est.estimate_name}
                  </div>
                  <div className={`mt-0.5 whitespace-nowrap text-xs ${active ? 'text-white/80' : 'opacity-75'}`}>
                    {fmtMoney(est.grand_total, est.currency)}
                  </div>
                  {/* Only offered when there is more than one option — the backend refuses to
                      delete the last one, so showing a control that always fails would be a lie.
                      A <span>, not a <button>: this sits inside the tab button, and nesting
                      buttons is invalid HTML. */}
                  {!isProposalPrepared && (estimates ?? []).length > 1 && (
                    <span
                      role="button"
                      tabIndex={0}
                      aria-label={t('workflow.estimateDeleteOption', 'Delete this option')}
                      title={t('workflow.estimateDeleteOption', 'Delete this option')}
                      onClick={(e) => {
                        e.stopPropagation();  // don't also select the tab
                        setDeleteOptionError('');
                        setOptionToDelete({
                          id: est.option_id,
                          label: est.estimate_description || est.estimate_name,
                        });
                      }}
                      onKeyDown={(e) => {
                        if (e.key !== 'Enter' && e.key !== ' ') return;
                        e.preventDefault();
                        e.stopPropagation();
                        setDeleteOptionError('');
                        setOptionToDelete({
                          id: est.option_id,
                          label: est.estimate_description || est.estimate_name,
                        });
                      }}
                      className={`absolute right-1 top-1 flex h-4 w-4 cursor-pointer items-center justify-center rounded text-xs leading-none ${
                        active
                          ? 'text-white/70 hover:bg-white/20 hover:text-white'
                          : 'text-ui-text3 hover:bg-danger/10 hover:text-danger-text'
                      }`}
                    >
                      ×
                    </span>
                  )}
                </button>
              );
            })}
            {!isProposalPrepared && (
              <button
                type="button"
                onClick={() => setShowAddOption((v) => !v)}
                className="-mb-px flex-shrink-0 whitespace-nowrap border border-transparent border-b-ui-border px-3 pb-[13px] pt-2 text-xs font-semibold text-accent-text hover:border-ui-border hover:bg-ui-hover"
              >
                + {t('workflow.estimateAddOption', 'Add option')}
              </button>
            )}
          </div>
          {/* The tab panel: bordered on top so the seam the active tab punches through (via
              `border-b-ui-card` + `-mb-px` above) reads as one continuous edge with whichever
              tab isn't currently poking through it. */}
          <div className="border-t border-ui-border">
          {/* Name and basis, inline — the same strip this has always been, not a modal. The WBS
              template picker that used to lead it is gone: a Sales Agent adding an option is
              naming a product choice the client will recognise ("Premium Shingle System"), not
              choosing a work-breakdown package. The package resolves from the tenant's own
              estimation_wbs_defaults by engagement type — the same resolution the workflow's
              automation hook has always used for the first option, which the REST caller
              previously had no way to reach (see EstimateService.resolve_wbs_code). */}
          {showAddOption && (
            <div className="mx-3.5 my-3 space-y-2 rounded-xl border border-accent-soft bg-accent-soft/25 px-3 py-2.5">
              <div>
                <p className="text-xs font-semibold text-ui-text">{t('workflow.estimateNewOption', 'New option')}</p>
                <p className="mt-0.5 text-xs text-ui-text2">
                  {t('workflow.estimateNewOptionHint', 'Give it a name the client will recognise, e.g. "GAF Timberline HDZ Roofing System".')}
                </p>
              </div>
              <input
                value={newOptionLabel}
                onChange={(e) => setNewOptionLabel(e.target.value)}
                placeholder={t('workflow.estimateAddOptionLabelPlaceholder', 'Option name')}
                className="w-full rounded-lg border border-ui-border bg-ui-card px-2.5 py-1.5 text-xs text-ui-text placeholder:text-ui-text3"
              />

              {/* How the new option prices its materials, set here rather than corrected after
                  the fact — the whole reason a set holds several options is that each answers
                  the price differently. Candidates come from the option already open: a new
                  option is generated from the same WBS package against the same roof, so it
                  resolves the same material classes, and it has no version of its own to ask
                  until it exists. */}
              <MaterialBasisPicker
                versionCode={versionCode ?? ''}
                collapsible={false}
                basis={newOptionBasis} onBasisChange={setNewOptionBasis}
                supplierCode={newOptionSupplier} onSupplierChange={setNewOptionSupplier}
                manufacturerCode={newOptionManufacturer} onManufacturerChange={setNewOptionManufacturer}
              />

              {addOptionError && <p className="text-xs font-medium text-danger-text">{addOptionError}</p>}
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => void submitAddOption()}
                  disabled={!newOptionLabel.trim() || addingOption}
                  className="rounded-lg btn-sheen px-2.5 py-1 text-xs font-semibold text-on-primary disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {addingOption ? t('workflow.estimateAddingOption', 'Creating…') : t('workflow.estimateAddOptionSubmit', 'Create option')}
                </button>
                <button
                  type="button"
                  onClick={closeAddOption}
                  className="rounded-lg border border-ui-border bg-ui-card px-2.5 py-1 text-xs font-medium text-ui-text2 hover:bg-ui-hover"
                >
                  {t('workflow.cancel', 'Cancel')}
                </button>
              </div>
            </div>
          )}
          {estimateDetail.commercial_summary && (
            <div className="flex flex-col gap-[6px] px-3.5 py-2 text-sm">
              <div className="flex justify-between"><span className="text-ui-text2">{t('workflow.estimateMaterials', 'Materials')}</span><b className="font-semibold text-ui-text">{fmtMoney(estimateDetail.commercial_summary.material_cost_total, estimateDetail.commercial_summary.currency)}</b></div>
              <div className="flex justify-between"><span className="text-ui-text2">{t('workflow.estimateWorkOther', 'Work & other costs')}</span><b className="font-semibold text-ui-text">{fmtMoney(estimateDetail.commercial_summary.boq_cost_total, estimateDetail.commercial_summary.currency)}</b></div>
              <div className="flex justify-between border-t border-ui-border pt-[7px]"><span className="text-ui-text2">{t('workflow.estimateJobCost', 'Material and Service cost')}</span><b className="font-semibold text-ui-text">{fmtMoney(estimateDetail.commercial_summary.direct_cost_total, estimateDetail.commercial_summary.currency)}</b></div>
              {(estimateDetail.commercial_summary.contingency.amount ?? 0) > 0 && (
                <div className="flex justify-between"><span className="text-ui-text2">{commercialLabel(t, estimateDetail.commercial_summary.contingency, 'Contingency')}</span><b className="font-semibold text-ui-text">{fmtMoney(estimateDetail.commercial_summary.contingency.amount, estimateDetail.commercial_summary.currency)}</b></div>
              )}
              {(estimateDetail.commercial_summary.overhead.amount ?? 0) > 0 && (
                <div className="flex justify-between"><span className="text-ui-text2">{commercialLabel(t, estimateDetail.commercial_summary.overhead, 'Overhead')}</span><b className="font-semibold text-ui-text">{fmtMoney(estimateDetail.commercial_summary.overhead.amount, estimateDetail.commercial_summary.currency)}</b></div>
              )}
              {!editMode ? (
                <>
                  <div className="flex justify-between"><span className="text-ui-text2">{commercialLabel(t, estimateDetail.commercial_summary.margin, 'Margin')}</span><b className="font-semibold text-ui-text">{fmtMoney(estimateDetail.commercial_summary.margin.amount, estimateDetail.commercial_summary.currency)}</b></div>
                  {(estimateDetail.commercial_summary.discount.amount ?? 0) > 0 && (
                    <div className="flex justify-between"><span className="text-ui-text2">{t('workflow.estimateDiscount', 'Discount')}</span><b className="font-semibold text-danger-text">−{fmtMoney(estimateDetail.commercial_summary.discount.amount, estimateDetail.commercial_summary.currency)}</b></div>
                  )}
                  {/* The figure margin/discount actually produce — without it the summary stopped at
                      the cost line and an edited discount looked like it hadn't been saved. */}
                  <div className="flex justify-between border-t border-ui-border pt-[7px]"><span className="font-semibold text-ui-text">{t('workflow.estimateTotal', 'Total estimate')}</span><b className="font-bold text-ui-text">{fmtMoney(estimateDetail.commercial_summary.grand_total, estimateDetail.commercial_summary.currency)}</b></div>
                </>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-ui-text2">{t('workflow.estimateMarginPct', 'Margin %')}</span>
                    <input
                      type="number" value={marginPercent} onChange={(e) => setMarginPercent(e.target.value)}
                      className="w-16 rounded-md border border-ui-border bg-ui-input px-1.5 py-1 text-right text-xs text-ui-text focus:border-ui-ring focus:outline-none"
                    />
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-ui-text2">{t('workflow.estimateDiscountPct', 'Discount %')}</span>
                    <input
                      type="number" value={discountPercent} onChange={(e) => setDiscountPercent(e.target.value)}
                      className="w-16 rounded-md border border-ui-border bg-ui-input px-1.5 py-1 text-right text-xs text-ui-text focus:border-ui-ring focus:outline-none"
                    />
                  </div>
                </div>
              )}
              {/* No tax row: the estimate is pre-tax by definition — `estimate_total` is "the
                  final estimate selling amount before proposal-level tax and presentation".
                  Tax is applied on the proposal's Pricing & Options section, from the tenant's
                  configured rate, and is shown there. */}
            </div>
          )}

          {/* At Proposal Prepared the estimate is opened from the proposal row's "View estimate"
              (one button, not two): no button row here. */}
          {!editMode && !isProposalPrepared && (
            <div className="flex flex-wrap gap-2 px-3.5 pb-2">
              {estimateDetail.version.status !== 'LOCKED' && estimateDetail.version.status !== 'SUPERSEDED' ? (
                <button
                  type="button" onClick={startEdit}
                  className="rounded-[7px] border border-accent px-[11px] py-1.5 text-xs font-semibold text-accent-text hover:bg-ui-hover"
                >
                  {t('workflow.viewEditEstimate', 'View / Edit estimate')}
                </button>
              ) : (
                // §4.2 copy-on-write: only LOCKED (frozen by an approved proposal) and SUPERSEDED
                // (an old, forked-over version) are genuinely historical — DRAFT and COMPLETED are
                // both editable in place now (see `_assert_version_editable`'s comment), so forking
                // is only needed to revise a version an approval has already frozen.
                <button
                  type="button" onClick={() => void onForkVersion()} disabled={forking}
                  className="rounded-[7px] border border-accent px-[11px] py-1.5 text-xs font-semibold text-accent-text hover:bg-ui-hover disabled:opacity-60"
                >
                  {forking ? t('workflow.estimateForking', 'Creating new version…') : t('workflow.estimateCreateVersionToEdit', 'Create new version to edit')}
                </button>
              )}
              {!isProposalPrepared && (
              <button
                type="button" onClick={() => setVersionsOpen((v) => !v)}
                className="rounded-[7px] border border-ui-border px-[11px] py-1.5 text-xs font-semibold text-ui-text2 hover:bg-ui-hover"
              >
                {t('workflow.estimateVersionHistoryCount', 'Version history ({{count}})', { count: versions?.length ?? estimateDetail.version.estimate_option_version })}
              </button>
              )}
              {/* No "Select for proposal" button here. Which option the proposal quotes is chosen
                  ON the proposal — "Make recommended", in its Pricing & Options section — and
                  that already calls the same EstimateService.select_for_proposal underneath. Two
                  controls for one decision, in two different cards, was the confusing part. The
                  first option completed becomes the basis automatically (EstimateService.
                  mark_complete), so there is always something to open the proposal with. */}
            </div>
          )}
          {forkError && <p className="px-3.5 pb-2 text-xs font-medium text-danger-text">{forkError}</p>}

          {versionsOpen && !editMode && (
            <div className="border-t border-ui-border">
              <div className="flex items-center justify-between px-3.5 pt-2">
                <p className="text-xs font-semibold text-ui-text2">{t('workflow.estimateVersionsTitle', 'Estimate versions')}</p>
                <button
                  type="button" onClick={() => setVersionsOpen(false)}
                  aria-label={t('workflow.close', 'Close')}
                  className="text-sm text-ui-text3 hover:text-ui-text2"
                >
                  ×
                </button>
              </div>
              <div className="divide-y divide-ui-border">
              {(versions ?? []).map((v) => (
                <div key={v.estimate_id} className="flex items-center justify-between gap-2 px-3.5 py-1.5 text-xs">
                  <span className="text-ui-text2">
                    v{v.estimate_option_version}
                    {v.estimate_option_version === estimateDetail.version.estimate_option_version && (
                      <span className="ml-1 rounded-full bg-accent-soft px-1.5 py-0.5 text-xs font-medium text-accent-text">{t('workflow.estimateCurrent', 'current')}</span>
                    )}
                    <span className="ml-1 text-ui-text3">· {v.status.toLowerCase()}{v.created_at ? ` · ${fmtDate(v.created_at)}` : ''}</span>
                  </span>
                  <span className="flex-shrink-0 font-semibold text-ui-text">
                    {v.grand_total != null ? fmtMoney(v.grand_total, v.currency ?? 'USD') : '—'}
                  </span>
                </div>
              ))}
              {versions && versions.length === 0 && (
                <p className="px-3.5 py-2 text-xs text-ui-text3">{t('workflow.estimateNoVersions', 'No version history yet.')}</p>
              )}
              </div>
            </div>
          )}

          {/* Validation — EST-FR-05-011: what's blocking (or just warning on) this version
              completing, readable at any time rather than only on a failed Complete attempt.
              Lives inside the option card (it validates THIS option), so it folds with it. */}
          {validation && (validation.blocking.length > 0 || validation.warnings.length > 0) && (
            <div className="border-t border-ui-border px-3.5 py-[11px]">
              <button
                type="button"
                onClick={() => setShowValidation((v) => !v)}
                className="flex w-full items-center justify-between gap-2 text-left"
              >
                <span className="text-micro font-bold uppercase tracking-[0.08em] text-ui-text3">
                  {t('workflow.estimateValidation', 'Validation')}
                  {validation.blocking.length > 0 && <span className="ml-1.5 text-danger-text">({validation.blocking.length})</span>}
                </span>
                <svg
                  className={`h-3 w-3 flex-shrink-0 text-ui-text3 transition-transform ${showValidation ? 'rotate-180' : ''}`}
                  viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
              {showValidation && (
                <div className="mt-[9px] space-y-1.5">
                  {validation.blocking.map((i, idx) => (
                    <p key={`${i.code}-${i.line_code ?? 'none'}-${idx}`} className="rounded-[7px] bg-danger/10 px-[11px] py-2 text-xs leading-relaxed text-danger-text">{i.message}</p>
                  ))}
                  {validation.warnings.map((i, idx) => (
                    <p key={`${i.code}-${i.line_code ?? 'none'}-${idx}`} className="rounded-[7px] bg-warning/10 px-[11px] py-2 text-xs leading-relaxed text-warning-text">{i.message}</p>
                  ))}
                </div>
              )}
            </div>
          )}
          </div>
        </div>

        </>)}


        {/* The editor is a MODAL now, not a strip below the fold. Pricing a roof is the job, not
            a footnote to the workflow card — see EstimateEditorModal for the autosave contract.
            The inline block below is retained only as the reason/recalculate affordance for the
            older flow and is superseded on screen by the overlay. */}
      {optionToDelete && (
        <Modal
          title={t('workflow.estimateDeleteOptionTitle', 'Delete estimate option')}
          onClose={() => setOptionToDelete(null)}
          onSave={() => void confirmDeleteOption()}
          saving={deletingOption}
          saveLabel={t('workflow.estimateDeleteOptionConfirm', 'Delete option')}
          maxWidthClassName="max-w-md"
        >
          <p className="text-xs leading-relaxed text-ui-text2">
            {t(
              'workflow.estimateDeleteOptionBody',
              'Delete "{{name}}"? Its pricing is removed from this engagement and from the proposal draft. Proposals already sent keep the copy they were built with.',
              { name: optionToDelete.label },
            )}
          </p>
          {deleteOptionError && (
            <p className="mt-2 text-xs font-medium text-danger-text">{deleteOptionError}</p>
          )}
        </Modal>
      )}

        {editMode && estimateDetail && versionCode && detailMatchesOption(estimateDetail, latestEstimateCode) && (
          <EstimateEditorModal
            detail={estimateDetail}
            versionCode={versionCode}
            onClose={() => setEditMode(false)}
            readOnly={isProposalPrepared}
            interactSignal={estimateEditorSignal}
            onSectionsChange={setSectionsState}
          />
        )}


        {/* No Tasks list here: WorkflowCard's own Tasks section further down the card renders the
            same engagement-scoped tasks, so repeating them inside the estimate card showed every
            task twice on screen. The count still appears on this card's own summary line above. */}

        {!isProposalPrepared && (
        <div className="border-t border-ui-border px-3 py-1.5">
          <p className="text-xs text-ui-text3">{t('workflow.estimateSamHint', 'Ask SAM to adjust pricing, swap materials, or recalculate this estimate.')}</p>
        </div>
        )}
      </div>
      )}

      {/* The proposal card is NOT rendered here any more.
          It used to hang off the end of this component so it would sit directly under the
          estimate, but everything above returns early — while `useGetEstimateQuery` is in flight
          (`!estimateDetail` -> return null), when the engagement has no estimate yet, and whenever
          that query errors. The proposal has its own query and its own guard, so tying its
          visibility to the estimate's load state meant it vanished on every revisit until the
          estimate resolved, and stayed gone for good if it failed.
          It is now bound as `proposal_summary_panel` at this status like every other module
          (workflow_definition_seed.py MODULE_DISPLAYS), which renders it as a sibling of this
          card, in the same place on screen, independent of anything here. */}
    </div>
  );
};

// Display for the 'approved_estimate_snapshot' business module (display-only). Renders once the
// engagement's current-reference estimate VERSION has been locked (Estimate.status === 'LOCKED')
// — today that only happens via EstimateService.lock_version(), which nothing calls
// yet (no proposal-approval flow exists — PRP module, not built). Renders null until something
// does; this is real scaffolding for that flow, not fabricated data — no approved_by/PDF/download
// fields exist on the backend, so this card only shows what's actually there (estimate code,
// locked version, total) rather than inventing the rest of the mockup's fields.
const ApprovedEstimateSnapshotCard: React.FC<{ engagementId: string }> = ({ engagementId }) => {
  const { t } = useTranslation();
  const { data: estimates } = useListEstimatesForEngagementQuery(engagementId);
  const latestEstimateCode = estimates?.[0]?.option_id;
  const { data: estimateDetail } = useGetEstimateQuery(
    { estimateCode: latestEstimateCode as string, engagementId },
    { skip: !latestEstimateCode },
  );

  if (!estimateDetail || estimateDetail.version.status !== 'LOCKED') return null;
  return (
    <div className="rounded-xl border border-ui-border bg-success/5 overflow-hidden">
      <div className="flex items-start justify-between gap-3 px-3 py-2.5">
        <div className="min-w-0">
          <p className="truncate text-xs font-semibold uppercase tracking-wide text-success-text">{t('workflow.approvedSnapshotTitle', 'Approved snapshot')}</p>
          <p className="mt-0.5 truncate text-xs text-ui-text3">
            {estimateDetail.estimate.option_id} · v{estimateDetail.version.estimate_option_version} · {t('workflow.approvedSnapshotLocked', 'locked')}
          </p>
          {estimateDetail.version.locked_reason && (
            <p className="mt-0.5 truncate text-xs text-ui-text3">{humanize(estimateDetail.version.locked_reason)}</p>
          )}
        </div>
        {estimateDetail.commercial_summary && (
          <div className="flex-shrink-0 text-right">
            <p className="text-base font-bold text-ui-text">{fmtMoney(estimateDetail.commercial_summary.grand_total, estimateDetail.commercial_summary.currency)}</p>
            <p className="text-xs text-ui-text3">{t('workflow.estimateTotal', 'Total estimate')}</p>
          </div>
        )}
      </div>
      <div className="border-t border-ui-border px-3 py-2">
        <p className="text-xs text-ui-text3">{t('workflow.approvedSnapshotHint', 'This estimate is locked and cannot be edited — ask SAM to fork a new version if changes are needed.')}</p>
      </div>
    </div>
  );
};

// WFL business-module linking, POST side (backend: enums.GateConditionType — see
// ModuleDisplayDefinition/module_displays). A status_entry module_displays binding names a
// module_code; state.active_display_modules (from WorkflowService._display_modules_for_status)
// lists every module_code bound to the engagement's CURRENT status. Every code present here that
// also has an entry in this map renders — a status can show more than one module at once (e.g.
// the meeting card AND the Assessment Capture tool at Assessment Scheduled). Independent of any
// capture form the SAME module_code may also back on a transition (BUSINESS_CAPTURE_COMPONENTS
// above) — one code, two possible roles depending on which binding references it. Every entry
// takes just {engagementId}.
const BUSINESS_MODULE_DISPLAY_COMPONENTS: Record<string, React.FC<{
  engagementId: string; propertyId?: string; tasks?: TaskFull[];
  assessmentOpenSignal?: number; onOpenAssessment?: () => void; readOnly?: boolean;
}>> = {
  meeting_schedule_for_assessment: AssessmentMeetingDisplayCard,
  assessment_capture_tool: AssessmentCaptureCard,
  estimate_summary_panel: EstimateSummaryDisplayCard,
  approved_estimate_snapshot: ApprovedEstimateSnapshotCard,
  proposal_summary_panel: ProposalDisplayCard,
};

const humanize = (s: string) => s
  .replace(/_id$/, '') // "qa_responsible_user_id" -> "qa responsible user", not "...User Id"
  .replace(/_/g, ' ')
  .replace(/\b\w/g, (c) => c.toUpperCase());

// api-service app/common/messages.py — M.WORKFLOW_TRANSITION_BLOCKED. Raised for closed-early,
// on-hold, and blocking-governance-gate rejections alike; in every case `detail` carries the
// specific reason (a gate's own failure_message, in the governance case) rather than the generic
// per-code message, so it's worth surfacing distinctly instead of folding into the generic error box.
const TRANSITION_BLOCKED_CODE = 5215;

/** Coarse relative-time string for the "last updated" line — no need for a full i18n date lib here. */
const relativeTime = (iso: string): string => {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
};

interface Warning {
  transitionCode: string;
  gateCode: string;
  message: string;
  reason?: string;
}

export interface WorkflowCardProps {
  /** The engagement whose workflow state/transitions this card shows. Omitted = approvals-only
   *  view (SAM's list_pending_approvals has no engagement scope). */
  engagementId?: string;
  /** Bumped whenever SAM re-opens/re-points this surface at the (possibly same) engagement —
   *  forces a refetch even when nothing invalidated the RTK Query cache (e.g. changed elsewhere). */
  rev?: number;
  /** Reports a short header title (engagement title, or "Pending approvals") up to WorkflowPanel. */
  onMeta?: (meta: { title: string }) => void;
  className?: string;
  /** Engagement-scoped tasks to render below the hold/close-early section. */
  tasks?: TaskFull[];
  onSelectTask?: (id: string) => void;
  /** Approvals-only view (no engagementId): opens the approval's engagement instead of deciding
   *  it inline — see approve/reject removal below. */
  onOpenEngagement?: (engagementId: string) => void;
  /** When true, expand the Quick Quote section (SAM called open_engagement_quick_quote for this
   *  engagement). Resets to false on its own — only the rising edge triggers an expansion. */
  expandQuickQuote?: boolean;
  /** The engagement_id SAM requested to open the satellite map for — used to auto-open the map
   *  when this card's engagementId matches. */
  openQuickQuoteMapEngagementId?: string | null;
  /** Incremented on every REFRESH_QUICK_QUOTE — triggers a scroll to the section even when
   *  expandQuickQuote hasn't changed (e.g. SAM saved without re-opening). */
  quickQuoteScrollTick?: number;
  /** SAM's list_available_transitions found this engagement currently blocked by a
   *  business_capture gate (e.g. schedule_assessment) — auto-open that transition's inline
   *  capture panel, the same one `clickTransition` opens on a button click, and scroll to it.
   *  Without this the chat reply asking "when should I schedule the assessment?" left no form
   *  on screen to answer into. */
  expandTransitionCode?: string | null;
  /** Bumped on EXPAND_ENGAGEMENT_ASSESSMENT_CAPTURE (request_assessment_media_capture tool) — the
   *  user asked SAM to add a photo/voice note/report/note it can't accept itself. Expands + flashes
   *  the Assessment meeting & capture card, no blocked transition involved. */
  expandAssessmentCaptureTick?: number;
  /** SAM called proposal_prepared_open — open the named panel on the Proposal Prepared card.
   *  tick increments so repeated calls to the same view still fire the effect. */
  openProposalPreparedView?: { view: 'preview_proposal' | 'view_estimate' | 'open_assessment'; tick: number } | null;
  /** SAM called proposal_surface_interact — an interaction inside an already-open Proposal
   *  Prepared surface (option switch, section expand/collapse, item open, close, scroll). */
  proposalSurfaceInteract?: ProposalSurfaceInteractPayload & { tick: number } | null;
  /** The parent (EngagementDetailPanel) renders the stage rail + status badge in a sticky band
   *  above this card — hide the duplicate banner inside the card to avoid showing it twice. */
  hideStageBanner?: boolean;
  /** The parent already greys the whole engagement (rail, status row, sections) when it is closed. */
  greyedByParent?: boolean;
}

const TASK_DONE = new Set(['completed', 'done']);
const TASKS_COLLAPSED_LIMIT = 4;

function fmtDate(iso: string) {
  try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return iso; }
}

function fmtMoney(amount: number | null | undefined, currency = 'USD') {
  if (amount == null) return '—';
  try { return new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 0 }).format(amount); }
  catch { return `$${amount.toFixed(0)}`; }
}

// Same abbreviated form QuickQuoteCostEstimate uses for its own low-high range — kept in sync so
// the collapsed Quick Quote bar's number reads the same as the one shown once expanded.
function fmtMoneyK(amount: number): string {
  return amount >= 1000 ? `$${(amount / 1000).toLocaleString(undefined, { maximumFractionDigits: 1 })}k` : `$${Math.round(amount)}`;
}


// A commercial step can be set as a percentage OR a flat currency amount. A FIXED_AMOUNT step
// stores `percent: 0`, so rendering the percentage unconditionally showed "Margin 0%" next to a
// real dollar figure — the label contradicted the number beside it. Percentage-suffixed labels
// are only used when the step actually is a percentage.
export function commercialLabel(
  t: TFunction,
  component: { method?: string; percent?: number } | null | undefined,
  key: 'Contingency' | 'Overhead' | 'Margin',
): string {
  const fallbacks = {
    Contingency: ['workflow.estimateContingency', 'Contingency {{pct}}%', 'workflow.estimateContingencyFlat', 'Contingency'],
    Overhead: ['workflow.estimateOverhead', 'Overhead {{pct}}%', 'workflow.estimateOverheadFlat', 'Overhead'],
    Margin: ['workflow.estimateMargin', 'Margin {{pct}}%', 'workflow.estimateMarginFlat', 'Margin'],
  }[key];
  if (component?.method === 'FIXED_AMOUNT') {
    return t(fallbacks[2], fallbacks[3]);
  }
  return t(fallbacks[0], fallbacks[1], { pct: component?.percent ?? 0 });
}

function InvoiceJobCardDownloadButton({ engagementId }: { engagementId: string }) {
  const { t } = useTranslation();
  const { download, downloading: isFetching } = useDownloadJobCardPdf(engagementId);
  return (
    <button
      type="button"
      onClick={() => void download()}
      disabled={isFetching}
      className="flex items-center gap-1.5 rounded-[7px] border border-brand-dark/30 bg-white px-2.5 py-1.5 text-[11.5px] font-semibold text-brand-dark hover:opacity-90 disabled:opacity-50 dark:bg-ui-card dark:border-brand-dark/40"
    >
      {isFetching ? '…' : `↓ ${t('jobs.downloadFullJobCard', 'Download full job card')}`}
    </button>
  );
}

const WorkflowCard: React.FC<WorkflowCardProps> = ({ engagementId, rev, onMeta, className, tasks, onOpenEngagement, expandQuickQuote, openQuickQuoteMapEngagementId, quickQuoteScrollTick, expandTransitionCode, expandAssessmentCaptureTick, openProposalPreparedView, proposalSurfaceInteract, hideStageBanner, greyedByParent }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { data: engagement } = useGetEngagementByIdQuery(engagementId as string, { skip: !engagementId });
  // The Property workspace card (WorkspacePropertyDetail, in the sibling workspaceApi slice) shows
  // this engagement's status/labels denormalized onto the property — none of that RTK Query state
  // shares tags with workflowEngineApi, so every mutation below that changes engagement/workflow
  // state must also invalidate it here, or the property card keeps showing stale data after the
  // action succeeds even though this card's own queries refetch correctly.
  //
  // Same reasoning extends to the Proposal and Estimate cards embedded below (proposal_summary_
  // panel / estimate_summary_panel): a workflow transition (approve/return/resubmit/send/accept/
  // decline) changes what ProposalDisplayCard/EstimateSummaryDisplayCard show (status, returned
  // reason, locked estimate) but goes through workflowEngineApi's own mutations, which have no
  // knowledge of proposalsApi's/estimatesApi's tags — found via a full-lifecycle regression pass
  // (2026-09-13) where "Return for Revision" left the Proposal card showing the old "Draft" badge
  // until a hard page reload, even though a fresh fetch already had the correct REVISION_REQUESTED
  // status. Invalidated here too so every existing call site below is covered for free.
  const refreshPropertyWorkspace = () => {
    const propertyId = engagement?.property_id;
    if (propertyId) dispatch(workspaceApi.util.invalidateTags([{ type: 'Property', id: propertyId }]));
    if (engagementId) dispatch(proposalsApi.util.invalidateTags([{ type: 'Proposal', id: engagementId }]));
    dispatch(estimatesApi.util.invalidateTags(['EstimateList']));
  };
  const {
    data: state, isLoading: stateLoading, isError: stateIsError, refetch: refetchState,
  } = useGetEngagementWorkflowStateQuery(engagementId as string, { skip: !engagementId });
  const {
    data: transitions, isLoading: transitionsLoading, refetch: refetchTransitions,
  } = useListAvailableTransitionsQuery(engagementId as string, { skip: !engagementId });
  const {
    data: statusNarration, isFetching: statusNarrationLoading, refetch: refetchStatusNarration,
  } = useGetWorkflowStatusNarrationQuery(
    { engagementId: engagementId as string, statusCode: state?.current_status_code },
    { skip: !engagementId || !state },
  );
  const { data: approvalsResp } = useListEngagementApprovalsQuery(engagementId as string, { skip: !engagementId });
  const { data: globalApprovalsResp, isLoading: globalApprovalsLoading } = useListPendingApprovalsQuery(undefined, { skip: !!engagementId });
  // Tenant's stage order (for the progress rail) — same structure regardless of which engagement
  // is open, so it's fetched once and cached by RTK Query rather than per-engagement.
  const { data: customization } = useGetWorkflowCustomizationQuery(undefined, { skip: !engagementId });
  const orderedStages = [...(customization?.stages ?? [])].sort((a, b) => a.display_order - b.display_order);

  // Bumped by "Open assessment card" (EstimateSummaryDisplayCard) to force AssessmentCaptureCard
  // open and scrolled into view — both are independent business-module cards rendered from the
  // same active_display_modules list below, so this is the one piece of state they share.
  const [assessmentOpenSignal, setAssessmentOpenSignal] = useState(0);
  // Last expandAssessmentCaptureTick already acted on (see the expand effect below).
  const consumedAssessmentTickRef = useRef<number | undefined>(undefined);
  const [assessmentModalOpen, setAssessmentModalOpen] = useState(false);
  const [schedulingJob, setSchedulingJob] = useState(false); // JOB-FEAT-03 toggle
  const [reschedulingJob, setReschedulingJob] = useState(false);
  const [handoffExpanded, setHandoffExpanded] = useState(false);
  const jobCardRef = useRef<HTMLDivElement | null>(null);
  const { download: downloadJobCardPdf } = useDownloadJobCardPdf(engagementId);
  // SAM's get_job_card (EXPAND_ENGAGEMENT_JOB_CARD): unfold the Job Card and bring it into view.
  useEffect(() => {
    if (!engagementId) return undefined;
    return subscribeEngagementSignal('expand_job_card', engagementId, () => {
      setHandoffExpanded(true);
      // From Job Scheduled on, the Job Card lives inside the status's own collapsible — open it
      // (only the current status's one is rendered, so opening all is harmless).
      setSchedulePanelVisible(true);
      setExecutionPanelVisible(true);
      setExecutedSummaryPanelVisible(true);
      setQaSummaryPanelVisible(true);
      setJobCompletedPanelVisible(true);
      setTimeout(() => jobCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 250);
    });
  }, [engagementId]);
  // SAM's get_job_card(section=...) (OPEN_ENGAGEMENT_JOB_CARD_SECTION): open the same popup the Job
  // Card's quick-link opens. The backend only sends this when the stage shows the Job Card.
  useEffect(() => {
    if (!engagementId) return undefined;
    return subscribeEngagementSignal('open_job_card_section', engagementId, (section) => {
      if (section === 'proposal') { setJobCardProposalPreviewOpen(false); setJobCardProposalModalOpen(true); }
      else if (section === 'assessment') { setAssessmentOpenSignal((v) => v + 1); setAssessmentModalOpen(true); }
      else if (section === 'materials') setMaterialListModalOpen(true);
      else if (section === 'notes') setNotesModalOpen(true);
    });
  }, [engagementId]);
  // SAM's get_job_card(close=true) (CLOSE_ENGAGEMENT_JOB_CARD_SECTION): close any Job Card popup.
  useEffect(() => {
    if (!engagementId) return undefined;
    return subscribeEngagementSignal('close_job_card_section', engagementId, () => {
      setJobCardProposalModalOpen(false);
      setJobCardProposalPreviewOpen(false);
      setAssessmentModalOpen(false);
      setMaterialListModalOpen(false);
      setNotesModalOpen(false);
    });
  }, [engagementId]);
  // SAM's get_job_card(download=true) (DOWNLOAD_ENGAGEMENT_JOB_CARD): the same PDF download as the button.
  useEffect(() => {
    if (!engagementId) return undefined;
    return subscribeEngagementSignal('download_job_card', engagementId, () => { void downloadJobCardPdf(); });
  }, [engagementId, downloadJobCardPdf]);
  useEffect(() => { setHandoffExpanded(false); }, [engagementId]);
  // Auto-open hold details panel when engagement is on hold so it greets the user expanded.
  useEffect(() => {
    if (state?.current_status_code === 'job_execution_on_hold') setHoldDetailsPanelOpen(true);
  }, [state?.current_status_code]);
  const { data: jobCardSummary } = useGetJobCardQuery(engagementId as string, { skip: !engagementId || !engagement?.project_manager_id });
  const { data: qaSummaryData } = useGetQaSummaryQuery(engagementId as string, { skip: !engagementId || !state || !EXECUTION_CARD_STATUSES.has(state.current_status_code) });
  // v5 spec row 27: Fail QA sends the job straight back to Job Execution (there is no QA Rejected
  // status), so "being reworked after a failed inspection" is job_execution + the latest QA result.
  const qaFailedRework = state?.current_status_code === 'job_execution' && qaSummaryData?.qa_result === 'fail';
  const billingAttached = !!state?.active_display_modules?.includes(BILLING_MODULE);
  const handoffAttached = !!state?.active_display_modules?.includes(HANDOFF_MODULE);
  const jobCardAttached = !!state?.active_display_modules?.includes(JOB_CARD_MODULE);
  const siteBoardAttached = !!state?.active_display_modules?.includes(SITE_BOARD_MODULE);
  const { data: invoices = [] } = useListInvoicesQuery(engagementId as string, { skip: !engagementId || !billingAttached });
  const RESCHEDULE_STATUSES = new Set(['job_scheduled', 'job_prepared', 'job_execution', 'job_execution_on_hold']);
  const { data: existingSchedule } = useGetScheduleQuery(engagementId as string, { skip: !engagementId || !state || !RESCHEDULE_STATUSES.has(state.current_status_code) });
  // JOB-FEAT-02 — Job Card's "Proposal details" quick-link. `ppPreviewSignal` alone does nothing
  // here: it only opens the internal preview of an ALREADY-MOUNTED ProposalDisplayCard, and that
  // component is only mounted via the active_display_modules map at proposal-stage statuses —
  // never at Job statuses (project_initiated onward). This modal mounts it unconditionally,
  // the same way the Assessment modal below already does for Assessment details.
  const [jobCardProposalModalOpen, setJobCardProposalModalOpen] = useState(false);
  const [qaFailFormOpen, setQaFailFormOpen] = useState(false); // JOB-FEAT-05 toggle (kept for backwards compat)
  const [qaInspectModalOpen, setQaInspectModalOpen] = useState(false);
  const [qaInspectInitialResult, setQaInspectInitialResult] = useState<'pass' | 'fail'>('pass');
  const [invoiceCreateOpen, setInvoiceCreateOpen] = useState(false);
  const { canPrepare: canPrepareInvoice, canSeeAmounts: canSeeInvoiceAmounts, loaded: invoicePermsLoaded } = useCanPrepareInvoice(engagementId as string);
  const amountsRestricted = invoicePermsLoaded && !canSeeInvoiceAmounts;
  const [holdDetailsPanelOpen, setHoldDetailsPanelOpen] = useState(false);
  const [schedulePanelVisible, setSchedulePanelVisible] = useState(false);
  const [executionPanelVisible, setExecutionPanelVisible] = useState(false);
  const [executedSummaryPanelVisible, setExecutedSummaryPanelVisible] = useState(false);
  const [qaSummaryPanelVisible, setQaSummaryPanelVisible] = useState(false);
  const [jobCompletedPanelVisible, setJobCompletedPanelVisible] = useState(false);
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [materialListModalOpen, setMaterialListModalOpen] = useState(false);
  // JOB-FEAT-02 — Job Card's "✎ Notes" quick-link. Confirmed live to previously no-op: unlike
  // onOpenProposal/onOpenAssessment above, no handler was ever passed for it at all. The real
  // Notes section (PropertyNotesSection) already exists for this engagement, just further down
  // the SAME scrollable pane — UXView.tsx (chat/UXView.tsx), not this component. Rather than
  // duplicate its data-fetch/add-note UI in a second modal, or thread a ref down through a prop
  // WorkflowCard doesn't otherwise need, this jumps to it directly via the DOM id UXView.tsx gives
  // it ("engagement-notes-section") — a plain anchor-link technique, safe here because both
  // pieces are always mounted in one continuous scroll container, never separate tabs/panels.
  // Notes are now shown in their own modal (same pattern as Proposal/Assessment modals above)
  // rather than scrolling to a section below — the section below has been removed.
  const openNotesModal = () => setNotesModalOpen(true);
  // ProposalDisplayCard forces its OWN internal full preview open (via previewOpenSignal), on top
  // of a summary panel this wrapper renders underneath — closing the preview's own X previously
  // only closed that inner layer, leaving the summary panel (and this wrapper's own header/X)
  // still showing, so the user needed a second click. Tracking preview_open via the existing
  // onSurfaceContext callback lets this wrapper close itself the instant the inner preview
  // closes, and hide its own duplicate header/X while the inner preview is showing, so from the
  // user's side one click opens the preview and one click on ITS X closes everything.
  const [jobCardProposalPreviewOpen, setJobCardProposalPreviewOpen] = useState(false);

  // Reported by EstimateSummaryDisplayCard (which itself merges in the nested ProposalDisplayCard's
  // preview state) on every change to the Proposal Prepared surfaces — a SAM action OR a manual
  // click, either way. Combined with this component's own assessmentModalOpen and reported as
  // active_proposal_surface, so "what am I looking at" stays accurate even when the user drives
  // the UI by hand instead of through SAM.
  const [estimateSurfaceState, setEstimateSurfaceState] = useState<{
    estimate_open: boolean; option_id?: string; option_label?: string;
    materials_expanded: boolean; services_expanded: boolean; preview_open: boolean;
    totals?: ProposalSurfaceContext['estimate_totals'];
    materials?: ProposalSurfaceContext['estimate_materials'];
    services?: ProposalSurfaceContext['estimate_services'];
  } | null>(null);
  const proposalSurfaceState = useMemo(() => {
    if (!engagementId) return null;
    return {
      engagement_id: engagementId,
      preview_open: estimateSurfaceState?.preview_open ?? false,
      estimate_open: estimateSurfaceState?.estimate_open ?? false,
      estimate_option_id: estimateSurfaceState?.option_id,
      estimate_option_label: estimateSurfaceState?.option_label,
      materials_expanded: estimateSurfaceState?.materials_expanded ?? false,
      services_expanded: estimateSurfaceState?.services_expanded ?? false,
      assessment_open: assessmentModalOpen,
      estimate_totals: estimateSurfaceState?.totals,
      estimate_materials: estimateSurfaceState?.materials,
      estimate_services: estimateSurfaceState?.services,
    };
  }, [engagementId, estimateSurfaceState, assessmentModalOpen]);
  useSamSurfaceContext('proposal_surface', proposalSurfaceState);

  // Signals passed down to EstimateSummaryDisplayCard when SAM calls proposal_prepared_open.
  const [ppEstimateSignal, setPpEstimateSignal] = useState(0);
  const [ppPreviewSignal, setPpPreviewSignal] = useState(0);
  useEffect(() => {
    if (!openProposalPreparedView) return;
    if (openProposalPreparedView.view === 'preview_proposal') setPpPreviewSignal((v) => v + 1);
    if (openProposalPreparedView.view === 'view_estimate') setPpEstimateSignal((v) => v + 1);
    if (openProposalPreparedView.view === 'open_assessment') {
      setAssessmentOpenSignal((v) => v + 1);
      setAssessmentModalOpen(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openProposalPreparedView?.tick]);

  // SAM's proposal_surface_interact tool — an interaction inside an already-open surface. Routed
  // by `surface`: view_estimate forwards into EstimateSummaryDisplayCard (option switch, section
  // expand/collapse, item open, scroll, close all happen inside its own EstimateEditorModal);
  // preview_proposal forwards into ProposalDisplayCard's preview modal; open_assessment acts
  // directly on the assessment MODAL WRAPPER this component owns (assessmentScrollRef below) since
  // that div — not anything inside AssessmentCaptureCard — is the actual scroll container.
  const [estimateInteractSignal, setEstimateInteractSignal] = useState<ProposalSurfaceInteractPayload & { tick: number } | null>(null);
  const [previewInteractSignal, setPreviewInteractSignal] = useState<ProposalSurfaceInteractPayload & { tick: number } | null>(null);
  const assessmentScrollRef = useRef<HTMLDivElement | null>(null);
  // A scroll/scroll_to_section action that arrived while the assessment modal was still closed —
  // e.g. "scroll down in the assessment" with nothing open yet. Opening it first and applying the
  // scroll once mounted reads as one action to the user, same as "open the estimate and scroll
  // down" already does for that surface.
  const pendingAssessmentScrollRef = useRef<ProposalSurfaceInteractPayload | null>(null);
  const applyAssessmentScroll = (p: ProposalSurfaceInteractPayload) => {
    const el = assessmentScrollRef.current;
    if (!el) return;
    const { action, section } = p;
    if (action === 'scroll_top') el.scrollTo({ top: 0, behavior: 'smooth' });
    else if (action === 'scroll_bottom') el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    else if (action === 'scroll_down') el.scrollBy({ top: el.clientHeight * 0.8, behavior: 'smooth' });
    else if (action === 'scroll_up') el.scrollBy({ top: -el.clientHeight * 0.8, behavior: 'smooth' });
    else if (action === 'scroll_to_section' && section) {
      const needle = section.trim().toLowerCase();
      const id = needle.includes('measurement') || needle.includes('roof')
        ? 'assessment-section-measurements'
        : needle.includes('source') || needle.includes('reasoning')
          ? 'assessment-section-sources-reasoning'
          : null;
      const target = id ? el.querySelector(`#${id}`) : null;
      target?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
  };
  useEffect(() => {
    if (!proposalSurfaceInteract) return;
    const { surface, action } = proposalSurfaceInteract;
    if (surface === 'view_estimate') {
      setEstimateInteractSignal(proposalSurfaceInteract);
    } else if (surface === 'preview_proposal') {
      setPreviewInteractSignal(proposalSurfaceInteract);
    } else if (surface === 'open_assessment') {
      if (action === 'close') {
        setAssessmentModalOpen(false);
      } else if (!assessmentModalOpen) {
        pendingAssessmentScrollRef.current = proposalSurfaceInteract;
        setAssessmentOpenSignal((v) => v + 1);
        setAssessmentModalOpen(true);
      } else {
        applyAssessmentScroll(proposalSurfaceInteract);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposalSurfaceInteract?.tick]);
  // The modal above mounts its scroll container on the render right after assessmentModalOpen
  // flips true — one frame later than this effect, so the pending scroll waits for that frame.
  useEffect(() => {
    if (!assessmentModalOpen || !pendingAssessmentScrollRef.current) return;
    const p = pendingAssessmentScrollRef.current;
    pendingAssessmentScrollRef.current = null;
    requestAnimationFrame(() => applyAssessmentScroll(p));
  }, [assessmentModalOpen]);
  // Only fetched once the modal is actually open — the header and its verdict banner are the
  // only things that need them, and neither exists until then.
  const { data: property } = useGetPropertyDetailQuery(engagement?.property_id as string, {
    skip: !assessmentModalOpen || !engagement?.property_id,
  });
  const { data: modalAssessment } = useGetCurrentEngagementAssessmentQuery(engagementId as string, {
    skip: !assessmentModalOpen || !engagementId,
  });
  // Notes modal — lazy fetch when open, scoped to this engagement.
  const { data: allNotesForModal = [] } = useGetPropertyNotesQuery(engagement?.property_id as string, {
    skip: !notesModalOpen || !engagement?.property_id,
    refetchOnMountOrArgChange: true,  // notes SAM just added must show when the popup opens
  });
  const notesForModal = allNotesForModal.filter((n: { engagement_id?: string | null }) => n.engagement_id === engagementId);
  const assessmentMeta = useMemo(() => {
    if (!modalAssessment) return null;
    const v = modalAssessment.validation_summary;
    const byCode = new Map(modalAssessment.measurement_values.map((m: { field_code: string; value: unknown }) => [m.field_code, m.value]));
    const sq = byCode.get('roof_area_sq');
    const fields = modalAssessment.measurement_values.length;
    const captured = modalAssessment.created_on
      ? `captured ${new Date(modalAssessment.created_on).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`
      : '';
    return {
      blocking: v.blocking_issue_count,
      verdict: v.blocking_issue_count > 0
        ? t('workflow.assessmentNotAccepted', 'Measurement incomplete')
        : t('workflow.assessmentAccepted', 'Measurement accepted'),
      // Only states what the record actually holds. The reference mockup also showed a report
      // version; a hand-entered assessment has no report at all, and inventing one would be
      // claiming a provenance this measurement does not have.
      facts: [
        typeof sq === 'number' ? `${sq} SQ roof area` : null,
        modalAssessment.measurement_report?.filename ?? null,
        `${fields - v.blocking_issue_count} of ${fields} fields validated`,
      ].filter(Boolean).join(' · '),
      captured,
    };
  }, [modalAssessment, t]);

  // SAM re-opened/refreshed this engagement — always look again, even if nothing invalidated the
  // cache (e.g. a change made from a different session/voice turn).
  useEffect(() => {
    if (!engagementId || rev === undefined) return;
    void refetchState();
    void refetchTransitions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rev]);

  // Report a short header title up to WorkflowPanel.
  useEffect(() => {
    const title = engagementId
      ? (engagement?.title || t('workflow.title', 'Workflow'))
      : t('workflow.pendingApprovals', 'Pending Approvals');
    onMeta?.({ title });
  }, [engagement?.title, engagementId, onMeta, t]);

  // Push the on-screen workflow state to SAM as active_engagement_workflow. `optimisticState` lets
  // runTransition report the mutation's OWN response immediately, without waiting on the query
  // refetch it also triggers — cleared as soon as that refetch lands (state.updated_on moves past
  // it), so the query result always wins once it's actually back.
  const [optimisticWorkflowState, setOptimisticWorkflowState] = useState<typeof state | null>(null);
  useEffect(() => { if (state) setOptimisticWorkflowState(null); }, [state?.updated_on]);
  const effectiveWorkflowState = state ?? optimisticWorkflowState;
  const workflowSurfaceState = useMemo(() => {
    if (!effectiveWorkflowState) return null;
    const labels = (effectiveWorkflowState.display_labels as unknown as Rec) || {};
    return {
      engagement_id: sv(effectiveWorkflowState.engagement_id),
      // property_id/title come from this component's own engagement fetch, not the workflow-state
      // query (EngagementWorkflowStateOut carries neither) — property_address would need a further
      // property fetch this card doesn't otherwise make, so it's left out here; ui_context.py only
      // uses it for a display line, never for a tool argument, unlike property_id.
      ...(engagement?.property_id ? { property_id: sv(engagement.property_id) } : {}),
      ...(engagement?.title ? { title: sv(engagement.title) } : {}),
      stage: sv(labels.stage),
      process: sv(labels.process),
      status: sv(labels.status),
      operational_type: sv(effectiveWorkflowState.operational_type),
    };
  }, [effectiveWorkflowState, engagement?.property_id, engagement?.title]);
  useSamSurfaceContext('engagement_workflow', workflowSurfaceState);

  // ── transitions ──────────────────────────────────────────────────────────────
  const [executeTransition, { isLoading: executing }] = useExecuteWorkflowTransitionMutation();
  const [activeTransition, setActiveTransition] = useState<string | null>(null);
  // Which transition_code actually kicked off the in-flight request — `executing` alone can't
  // tell one button from another, and without this the clicked button showed no feedback at
  // all (same colors whether idle or mid-request), so a user unsure their click "took" would
  // reasonably tap a DIFFERENT transition while the first was still in flight.
  const [pendingTransitionCode, setPendingTransitionCode] = useState<string | null>(null);
  // Inline "capture first" panel for a business_capture resolution — see
  // BUSINESS_CAPTURE_COMPONENTS' comment. Holds the transition_code so the panel renders under
  // the right button, same pattern as activeTransition/resolvingTransition below.
  const [schedulingTransition, setSchedulingTransition] = useState<string | null>(null);
  const [reasonText, setReasonText] = useState('');
  const [executeError, setExecuteError] = useState<string | null>(null);
  const [blockedMessage, setBlockedMessage] = useState<string | null>(null);
  const [warning, setWarning] = useState<Warning | null>(null);
  const [restrictedTransition, setRestrictedTransition] = useState<{ code: string; reason: string } | null>(null);

  // ── tasks ──────────────────────────────────────────────────────────────────
  const [tasksExpanded, setTasksExpanded] = useState(false);
  // Quick Quote starts collapsed behind a summary bar — the map + AI estimate + cost pill is a
  // lot to render inline on a card that's opened constantly for unrelated tasks (see
  // PropertyRoofEstimateSection's own note about the same cost tradeoff for its satellite map).
  const [quickQuoteExpanded, setQuickQuoteExpanded] = useState(false);
  const quickQuoteSectionRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (expandQuickQuote) {
      setQuickQuoteExpanded(true);
      setTimeout(() => quickQuoteSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    }
  }, [expandQuickQuote]);
  // Scroll to the section on every REFRESH_QUICK_QUOTE tick (save via SAM) even if already expanded.
  useEffect(() => {
    if (quickQuoteScrollTick) {
      setTimeout(() => quickQuoteSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    }
  }, [quickQuoteScrollTick]);
  // One row ref per transition_code so SAM's EXPAND_ENGAGEMENT_TRANSITION_CAPTURE can scroll to
  // the specific blocked transition it means, not just "somewhere in the transitions section".
  const transitionRowRefs = useRef<Record<string, HTMLDivElement | null>>({});
  // Draws a brief red shake/flash on the Assessment meeting & capture card when SAM points at a
  // required_measurement/open_measurement block — that card is a POST-side module display
  // (always shown once bound to this status), not a business_capture form with a button to open,
  // so there's no scheduling-panel equivalent to toggle; a visual call-out is the only way to
  // draw the user's eye to it. Auto-clears itself so it doesn't linger as a stuck red ring.
  const [assessmentAttention, setAssessmentAttention] = useState(false);
  // Dispatch table, not a hardcoded pair of special cases: whichever resolution.action_type the
  // CURRENTLY blocked transition actually has picks its own on-card target here, so a new gate
  // type (_resolution_hint in api-service/app/domain/workflows/service.py already knows about
  // open_media / open_tasks / open_approval / add_contact_participant / update_engagement_data)
  // gets a sensible expand target automatically instead of only ever reacting to the two types
  // this file happens to special-case — see server-side note in list_available_transitions,
  // which publishes EXPAND_ENGAGEMENT_TRANSITION_CAPTURE for ANY blocked resolution, not just
  // business_capture/open_measurement.
  useEffect(() => {
    if (!expandTransitionCode || !transitions) return;
    const tr = transitions.find((t) => t.transition_code === expandTransitionCode);
    if (!tr) return;
    const scrollToRow = () => setTimeout(
      () => transitionRowRefs.current[expandTransitionCode]?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50,
    );
    if (tr.resolution?.action_type === 'open_measurement') {
      // AssessmentCaptureCard — no button of its own to open, so bump its expand signal + flash.
      setAssessmentOpenSignal((v) => v + 1);
      setAssessmentAttention(true);
      const timer = setTimeout(() => setAssessmentAttention(false), 2000);
      return () => clearTimeout(timer);
    }
    if (tr.resolution?.action_type === 'open_estimate') {
      // EstimateSummaryDisplayCard — "estimate_ready_for_proposal" business_validation gate
      // (prepare_proposal): the blocking action is completing/selecting an estimate option,
      // which lives on that card, not on this transition row. Reuses the same signal SAM's
      // get_estimate/swap_estimate_material/etc. already bump.
      setPpEstimateSignal((v) => v + 1);
      return;
    }
    if (tr.resolution?.action_type === 'update_engagement_data') {
      // The inline "Add details" field-entry form under the transition button.
      openResolveForm(tr);
      scrollToRow();
      return;
    }
    if (tr.resolution?.action_type === 'business_capture') {
      // The inline capture form (e.g. AssessmentScheduleForm) under the transition button.
      setSchedulingTransition(expandTransitionCode);
      scrollToRow();
      return;
    }
    // open_media / open_tasks / open_approval / add_contact_participant / anything future: no
    // dedicated on-card form to open, but the resolution hint text under the button already
    // explains what to do — scrolling there is the generic, always-correct fallback.
    scrollToRow();
  }, [expandTransitionCode, transitions]);
  // The user asked SAM to add a photo/voice note/report/note it has no tool to accept — bump the
  // same expand signal + flash used above for open_measurement, but with no blocked transition
  // involved at all (request_assessment_media_capture fires on request alone).
  useEffect(() => {
    // Guard on `state` too, not just the tick — the render block this triggers reads
    // `state!.active_display_modules` (non-null assertion) once assessmentOpenSignal > 0, so
    // bumping the signal before the workflow state has finished loading crashes the card.
    // Re-runs when `state` arrives too: when SAM lands on this engagement the card mounts fresh
    // and the tick arrives BEFORE the workflow state has loaded — keyed on the tick alone, that
    // early return swallowed the signal for good. The ref makes each tick fire exactly once.
    if (!expandAssessmentCaptureTick || !state || consumedAssessmentTickRef.current === expandAssessmentCaptureTick) return;
    consumedAssessmentTickRef.current = expandAssessmentCaptureTick;
    setAssessmentOpenSignal((v) => v + 1);
    setAssessmentAttention(true);
    const timer = setTimeout(() => setAssessmentAttention(false), 2000);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandAssessmentCaptureTick, !!state]);
  // The SAVED quote for this engagement, which outranks the AI recalculation below: it is the
  // number a human chose and stored, and it is what the expanded card shows. The pill used to
  // read the property estimate alone, so an adjusted quote was invisible until you expanded —
  // the same figure appearing two different ways on one screen.
  const { data: savedQuickQuote } = useGetCurrentQuickQuoteQuery(engagementId ?? '', {
    skip: !engagementId,
  });
  // DB-ONLY. A Quick Quote is "triggered" for an engagement when, and only when, someone opened
  // this card and saved one — which is exactly what a `quick_quotes` record is. So the pill reads
  // that record and nothing else, and a page reload re-reads the same record rather than deciding
  // for itself.
  //
  // This used to fall back to the property's live AI roof estimate whenever nothing was saved.
  // That estimate is a PROPERTY fact (Google Solar area x tenant rate) and exists as soon as the
  // address does, so a brand-new engagement where nobody had run anything still showed a firm
  // "$12.2k-14k" — an untriggered Quick Quote presenting itself as a quoted price. The figure is
  // still there to see, one click away, in the panel below; it just no longer claims to be this
  // engagement's quote before anyone has said so.
  const quickQuoteSummary = savedQuickQuote
    ? {
        low: savedQuickQuote.result.estimated_cost_low,
        high: savedQuickQuote.result.estimated_cost_high,
        areaSqft: savedQuickQuote.inputs.area_sqft,
        isSaved: true,
        isAdjusted: savedQuickQuote.inputs.area_source === 'MANUAL'
          || savedQuickQuote.pricing_basis.complexity_source === 'AGENT_OVERRIDE',
      }
    : null;
  const currentUserId = useAppSelector((s) => s.sessionState.userId);
  // Only the task's owner/assignee see the row itself (title, status, due date, actions) —
  // everyone else viewing this engagement gets no per-task detail and no number at all, not even
  // a total count (a raw count/percentage needs explaining and reads as confusing without the
  // rows behind it). Just a lightweight "something's still open" pulse shown up near the status
  // badge, not under its own "Tasks" heading — disappears once every other task is done.
  // Tasks assigned to me directly, OR unassigned queue tasks (my_queues=true already ensures role match).
  const myTasks = (tasks ?? []).filter(t =>
    t.assignee_user_id === currentUserId ||
    (!t.assignee_user_id && !!t.queue_role_id)
  );
  const otherTaskPending = (tasks ?? []).some(t =>
    !myTasks.includes(t) && !TASK_DONE.has(t.status) && t.status !== 'cancelled',
  );


  const runTransition = async (code: string, reason?: string, acknowledgedWarnings?: string[]) => {
    if (!engagementId) return;
    setExecuteError(null);
    setBlockedMessage(null);
    setPendingTransitionCode(code);
    try {
      const result = await executeTransition({ engagementId, transitionCode: code, reason, acknowledgedWarnings }).unwrap();
      if (result.status === 'warning_required') {
        setWarning({ transitionCode: code, gateCode: result.gate_code, message: result.message, reason });
        return;
      }
      setWarning(null);
      setActiveTransition(null);
      setReasonText('');
      setOptimisticWorkflowState(result.state as unknown as typeof state);
      refreshPropertyWorkspace();
      // JOB-FEAT-02/04/05 — Job Card/Project Board/QA Summary live in a separate RTK Query slice
      // (jobsApi) from this mutation (workflowEngineApi), so a transition here never invalidates
      // their cache on its own; without this they'd keep showing pre-transition data (e.g. QA
      // summary still "Passed" right after Fail QA) until an unrelated refetch happened to occur.
      dispatch(jobsApi.util.invalidateTags([
        { type: 'JobCard', id: engagementId },
        { type: 'ProjectBoard', id: engagementId },
        { type: 'QaSummary', id: engagementId },
        { type: 'Schedule', id: engagementId },
      ]));
    } catch (e) {
      if (getMessageCode(e) === TRANSITION_BLOCKED_CODE) {
        setBlockedMessage(getErrorDetail(e) || apiErrorMessage(e, t, t('workflow.transitionFailed', 'Could not complete that transition.')));
      } else {
        setExecuteError(apiErrorMessage(e, t, t('workflow.transitionFailed', 'Could not complete that transition.')));
      }
    } finally {
      setPendingTransitionCode(null);
    }
  };

  const clickTransition = (tr: AvailableTransition) => {
    // JOB-FEAT-03 — `schedule_job` must NEVER fire directly from the generic transition-button
    // path: doing so bypasses the entire calendar/crew-select flow and leaves the engagement at
    // Job Scheduled with no crew and no date. The Job Card's "Schedule / reschedule Job" link and
    // this primary CTA button are the SAME real action and must share the SAME entry point — the
    // scheduling modal itself calls runTransition('schedule_job') once a real schedule is saved
    // (see JobSchedulingForm's onScheduled handler below).
    if (tr.transition_code === 'schedule_job') {
      setSchedulingJob(true);
      return;
    }
    // JOB-FEAT-05 — both inspect_job and fail_qa_rework open the unified QA Inspect modal.
    // inspect_job pre-selects "pass"; fail_qa_rework pre-selects "fail".
    if (tr.transition_code === 'inspect_job') {
      setQaInspectInitialResult('pass');
      setQaInspectModalOpen(true);
      return;
    }
    if (tr.transition_code === 'fail_qa_rework') {
      setQaInspectInitialResult('fail');
      setQaInspectModalOpen(true);
      return;
    }
    // A business_capture resolution takes priority over the executable/blocked check below —
    // the whole point is that the button stays actionable (opens a capture form) while the gate
    // is unsatisfied, rather than reading as "you can't do this". Only fires when BOTH the gate
    // author opted in (resolution present) AND a matching frontend form is registered — a gate
    // with capture_ui_enabled but no registered component here falls through to the normal
    // blocked-reason handling instead of silently doing nothing.
    const captureKey = tr.resolution?.action_type === 'business_capture' ? tr.resolution.action_key : undefined;
    const CaptureComponent = captureKey ? BUSINESS_CAPTURE_COMPONENTS[captureKey] : undefined;
    if (CaptureComponent) {
      // Toggle: clicking the button again while its capture form is already open closes it,
      // instead of just re-opening the same panel with no way to collapse it from the button.
      setSchedulingTransition((prev) => (prev === tr.transition_code ? null : tr.transition_code));
      return;
    }
    if (!tr.executable) {
      // For `update_engagement_data` gates (e.g. PM assignment, QA responsible user), auto-open
      // the resolve form on the first click — no separate "Add details" link click needed.
      if (tr.resolution?.action_type === 'update_engagement_data') {
        setRestrictedTransition({ code: tr.transition_code, reason: tr.reason || t('workflow.actionRestricted', 'You do not have permission to perform this action.') });
        if (resolvingTransition === tr.transition_code) {
          setResolvingTransition(null);
        } else {
          openResolveForm(tr);
        }
        return;
      }
      setRestrictedTransition({ code: tr.transition_code, reason: tr.reason || t('workflow.actionRestricted', 'You do not have permission to perform this action.') });
      return;
    }
    setRestrictedTransition(null);
    setExecuteError(null);
    setBlockedMessage(null);
    if (tr.requires_reason) {
      setActiveTransition(tr.transition_code);
      setReasonText('');
      return;
    }
    void runTransition(tr.transition_code);
  };

  const confirmReasoned = () => {
    if (!activeTransition || !reasonText.trim()) return;
    void runTransition(activeTransition, reasonText.trim());
  };

  const acknowledgeWarning = () => {
    if (!warning) return;
    void runTransition(warning.transitionCode, warning.reason, [warning.gateCode]);
  };

  // ── Resolve a blocked transition's stub-gate data fields ────────────────────
  // Only for resolution.action_type === 'update_engagement_data' — the only gate type with no
  // real module UI yet (see _resolution_hint in api-service). Other action_types point the user
  // at an existing real surface (Pending Approvals below, or the property's Media/Measurements/
  // Tasks tab) instead of a form.
  const [updateEngagementData, { isLoading: savingResolveData }] = useUpdateEngagementDataMutation();
  const [resolvingTransition, setResolvingTransition] = useState<string | null>(null);
  // The capture form of a blocked transition ("Add details" fields, or the business-capture panel) is open.
  useSamSectionContext('transition_capture', { engagement_id: engagementId }, !!resolvingTransition || !!schedulingTransition);
  const [resolveValues, setResolveValues] = useState<Record<string, string>>({});
  const [resolveError, setResolveError] = useState<string | null>(null);

  const openResolveForm = (tr: AvailableTransition) => {
    setResolveError(null);
    setResolveValues(Object.fromEntries((tr.resolution?.fields ?? []).map((f) => [f, ''])));
    setResolvingTransition(tr.transition_code);
  };

  const [assignPm] = useAssignPmMutation();

  const submitResolveData = async () => {
    if (!engagementId || !resolvingTransition) return;
    setResolveError(null);
    const data: Record<string, unknown> = {};
    for (const [field, value] of Object.entries(resolveValues)) {
      data[field] = field.endsWith('_uploaded') ? value === 'true' : value;
    }
    try {
      // JOB-FEAT-01: the `pm_assignment_required` gate's only field is project_manager_id — route
      // that case through the real assign-pm endpoint (records pm_assignment_history + best-effort
      // customer notification) instead of the generic engagement-data write, which would silently
      // skip both.
      if (Object.keys(data).length === 1 && typeof data.project_manager_id === 'string' && data.project_manager_id) {
        await assignPm({ engagementId, pm_user_id: data.project_manager_id }).unwrap();
      } else {
        await updateEngagementData({ id: engagementId, data }).unwrap();
      }
      setResolvingTransition(null);
      setResolveValues({});
      void refetchTransitions();
      refreshPropertyWorkspace();
    } catch (e) {
      setResolveError(apiErrorMessage(e, t, t('workflow.resolveSaveFailed', 'Could not save those details.')));
    }
  };

  // ── approvals ────────────────────────────────────────────────────────────────
  const [decideApproval, { isLoading: deciding }] = useDecideApprovalMutation();
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectComment, setRejectComment] = useState('');
  const [approvalError, setApprovalError] = useState<string | null>(null);
  // After approving, the row itself just says "Approved on ..." — nothing points the user back
  // up at the transition button that's now unblocked (the mirror image of resolveOpenApprovalHint,
  // which points a blocked transition DOWN at this section). Surfaced for a few seconds, and
  // cleared early if the user acts on it by clicking a transition.
  const [approvalSuccessMessage, setApprovalSuccessMessage] = useState<string | null>(null);
  useEffect(() => {
    if (!approvalSuccessMessage) return;
    const id = setTimeout(() => setApprovalSuccessMessage(null), 6000);
    return () => clearTimeout(id);
  }, [approvalSuccessMessage]);

  // Scoped to the CURRENT stage, and either still pending or decided-but-still-on-the-step-it-
  // resolved-to. A decision and the transition it unblocks are two separate actions (often
  // seconds apart) — resolved_status_code is stamped onto the approval by the FIRST transition
  // after it's decided (see WorkflowApprovalRequestDAO.stamp_resolved_status), so "Approved on
  // ..." stays visible through that resulting status (e.g. Proposal Approved) and disappears once
  // the engagement moves past it into any later status (e.g. Proposal Sent) — rather than
  // vanishing the instant it's decided, or lingering through the whole (much broader) stage.
  // Requests created before stage_code existed have none and are excluded here, same tolerance
  // as elsewhere for legacy records.
  // A rejected approval that gets re-requested (e.g. proposal edited and resubmitted) creates a
  // NEW ApprovalRequestOut with the same approval_type, rather than mutating the old one — so
  // without deduping, every past rejection for that type stays matched by resolved_status_code
  // (rejection doesn't move the status forward) and piles up alongside the current one. Only the
  // most-recently-requested request per approval_type is load-bearing; keep just that one.
  const approvals: ApprovalRequestOut[] = visibleApprovals(approvalsResp?.data ?? [], {
    engagementId, stageCode: state?.current_stage_code, statusCode: state?.current_status_code,
  });

  const rejectionNoticeText = rejectionNotice(approvals);
  // Approval types whose latest request was rejected — a transition still gated on that same
  // approval can't run until the work is revised and resubmitted, so it isn't offered.
  const rejectedApprovalTypes = new Set(approvals.filter((a) => a.status === 'rejected').map((a) => a.approval_type));

  // ── On-Hold flag (G2/G3) ─────────────────────────────────────────────────────
  // Only offered when the engine reports on_hold (already flagged) or when the current
  // process has no dedicated on-hold status of its own — the backend rejects the flag for
  // Prospects/Leads (they already have prospect_on_hold/lead_on_hold as real statuses reached
  // via their own transitions), so this button simply surfaces whatever the server allows;
  // a 409 from the server on processes that already have their own mechanism is expected and
  // shown as the error text rather than pre-guessed client-side.
  const [putOnHold, { isLoading: holding }] = usePutEngagementOnHoldMutation();
  const [resumeEngagement, { isLoading: resuming }] = useResumeEngagementMutation();
  const [holdEditorOpen, setHoldEditorOpen] = useState(false);
  const [holdReason, setHoldReason] = useState('');
  const [holdError, setHoldError] = useState<string | null>(null);

  const submitPutOnHold = async () => {
    if (!engagementId || !holdReason.trim()) return;
    setHoldError(null);
    try {
      await putOnHold({ engagementId, reason: holdReason.trim() }).unwrap();
      setHoldEditorOpen(false);
      setHoldReason('');
      refreshPropertyWorkspace();
    } catch (e) {
      setHoldError(apiErrorMessage(e, t, t('workflow.holdFailed', 'Could not put the engagement on hold.')));
    }
  };
  const submitResume = async () => {
    if (!engagementId) return;
    setHoldError(null);
    try {
      await resumeEngagement(engagementId).unwrap();
      refreshPropertyWorkspace();
    } catch (e) {
      setHoldError(apiErrorMessage(e, t, t('workflow.resumeFailed', 'Could not resume the engagement.')));
    }
  };

  // JOB-FEAT-04 — "Notify Customer" while on hold (mockup's ✆ Notify Customer secondary action).
  // Reuses CommunicationService.send_email + the same customer-contact lookup pattern as
  // JobService._notify_customer_of_pm, but user-triggered so a failure surfaces to the PM instead
  // of being silently swallowed.
  const [notifyCustomerHold, { isLoading: notifyingCustomer }] = useNotifyCustomerOfHoldMutation();
  const [notifyCustomerError, setNotifyCustomerError] = useState<string | null>(null);
  const [notifyCustomerSent, setNotifyCustomerSent] = useState(false);
  const [updateHoldDetails] = useUpdateHoldDetailsMutation();
  const [holdUpdateEditorOpen, setHoldUpdateEditorOpen] = useState(false);
  const [holdUpdateNotes, setHoldUpdateNotes] = useState('');
  const [holdUpdateError, setHoldUpdateError] = useState<string | null>(null);
  const submitNotifyCustomerHold = async () => {
    if (!engagementId) return;
    setNotifyCustomerError(null);
    setNotifyCustomerSent(false);
    try {
      await notifyCustomerHold({ engagementId }).unwrap();
      setNotifyCustomerSent(true);
    } catch (e) {
      setNotifyCustomerError(apiErrorMessage(e, t, t('jobs.notifyCustomerFailed', 'Could not notify the customer.')));
    }
  };

  const submitUpdateHoldDetails = async () => {
    if (!engagementId) return;
    setHoldUpdateError(null);
    try {
      await updateHoldDetails({ engagementId, hold_notes: holdUpdateNotes.trim() }).unwrap();
      setHoldUpdateEditorOpen(false);
      refreshPropertyWorkspace();
    } catch (e) {
      setHoldUpdateError(apiErrorMessage(e, t, t('jobs.holdUpdateFailed', 'Could not update hold details.')));
    }
  };

  // ── Close Engagement Early (G1) ───────────────────────────────────────────────
  // Owner-only, permanent, engagement-level outcome (not a status move) — see api-service
  // CloseEngagementEarlyRequest for why. Once set, the server blocks every further
  // transition/hold/resume on this engagement.
  const [closeEarly, { isLoading: closingEarly }] = useCloseEngagementEarlyMutation();
  const [closeEarlyEditorOpen, setCloseEarlyEditorOpen] = useState(false);
  const [closureType, setClosureType] = useState<'closed_lost' | 'future_follow_up'>('closed_lost');
  const [closeReason, setCloseReason] = useState('');
  const [closeEarlyError, setCloseEarlyError] = useState<string | null>(null);

  const submitCloseEarly = async () => {
    if (!engagementId || !closeReason.trim()) return;
    setCloseEarlyError(null);
    try {
      await closeEarly({ id: engagementId, closureType, reason: closeReason.trim() }).unwrap();
      setCloseEarlyEditorOpen(false);
      refreshPropertyWorkspace();
    } catch (e) {
      setCloseEarlyError(apiErrorMessage(e, t, t('workflow.closeEarlyFailed', 'Could not close the engagement early.')));
    }
  };

  const approve = async (approvalId: string) => {
    setApprovalError(null);
    try {
      await decideApproval({ approvalId, decision: 'approved' }).unwrap();
      refreshPropertyWorkspace();
      setApprovalSuccessMessage(t('workflow.approvalRecordedProceed', 'Approved — you can now continue with the actions above.'));
    } catch (e) {
      setApprovalError(apiErrorMessage(e, t, t('workflow.decisionFailed', 'Could not record that decision.')));
    }
  };
  const reject = async (approvalId: string) => {
    setApprovalError(null);
    try {
      await decideApproval({ approvalId, decision: 'rejected', comments: rejectComment.trim() || undefined }).unwrap();
      setRejectingId(null);
      setRejectComment('');
      refreshPropertyWorkspace();
    } catch (e) {
      setApprovalError(apiErrorMessage(e, t, t('workflow.decisionFailed', 'Could not record that decision.')));
    }
  };

  // ── Approvals-only view (no engagementId — list_pending_approvals tool) ────
  if (!engagementId) {
    const globalApprovals = globalApprovalsResp?.data ?? [];
    return (
      <div className={cn('rounded-2xl bg-ui-card overflow-hidden', className)}>
        <div className="px-3 py-3 space-y-2">
          {globalApprovalsLoading ? (
            <div className="flex items-center gap-2 py-2"><Spinner size="sm" /><span className="text-xs text-ui-text3">Loading approvals…</span></div>
          ) : globalApprovals.length === 0 ? (
            <p className="text-sm text-ui-text3 py-4 text-center">No pending approvals.</p>
          ) : (
            globalApprovals.map((a) => (
              <button
                key={a.approval_request_id}
                type="button"
                onClick={() => onOpenEngagement?.(a.engagement_id)}
                disabled={!onOpenEngagement}
                className="flex w-full items-center gap-2 rounded-lg border border-ui-border bg-ui-subtle px-3 py-2 text-left transition hover:border-ui-border hover:bg-ui-hover disabled:cursor-default disabled:hover:border-ui-border disabled:hover:bg-ui-subtle"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium text-ui-text">{humanize(a.approval_type)}</span>
                    <span className="text-micro uppercase tracking-wide text-ui-text3">{a.status}</span>
                  </div>
                  {(a.engagement_title || a.property_address) && (
                    <p className="mt-0.5 truncate text-xs text-ui-text2">
                      {[a.engagement_title, a.property_address].filter(Boolean).join(' — ')}
                    </p>
                  )}
                  {a.required_role_label && (
                    <p className="mt-0.5 text-xs text-ui-text3">Requires: {a.required_role_label}</p>
                  )}
                </div>
                {onOpenEngagement && (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="flex-shrink-0 text-ui-text3">
                    <path d="M9 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </button>
            ))
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={cn('rounded-2xl bg-ui-card overflow-hidden', greyedByParent ? '' : closedCardClass(!!engagement?.closed_early), className)}>
      {engagement?.closed_early && (
        <p role="status" className="border-b border-ui-border bg-ui-subtle px-3 py-2 text-xs font-semibold text-ui-text2">{CLOSED_CARD_NOTICE}</p>
      )}
      {/* Property/contact header — anchors the card to the physical address and its primary
          point of contact, matching every other Property-scoped surface's convention. */}


      {/* Stage rail + status badge — hidden when parent renders them in a sticky band above */}
      {!hideStageBanner && engagementId && (
        <div className="space-y-2">
          {stateLoading && !state ? (
            <div className="flex items-center gap-2 py-1"><Spinner size="sm" /><span className="text-xs text-ui-text3">{t('workflow.loading', 'Loading workflow state…')}</span></div>
          ) : stateIsError ? (
            <p className="text-xs text-danger-text">{t('workflow.stateUnavailable', 'Workflow state unavailable.')}</p>
          ) : state ? (
            <div className="space-y-2">
              {orderedStages.length > 0 && (
                <StageRail stages={orderedStages} currentStageCode={state.current_stage_code} />
              )}
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center rounded-full bg-accent/10 border border-accent/30 px-2 py-0.5 text-xs font-semibold text-accent-text dark:border-accent/40">
                  {state.display_labels.status}
                </span>
                {/* Mockup's second small badge next to the main status pill for exception-type
                    statuses ("Job Execution On Hold" + "Exception · on hold", "QA Rejected" +
                    "Exception · QA failed") — no backend field for this exists (no
                    `status_category` on the status definition), so this is a small frontend-only
                    lookup rather than new data plumbing; add a code here if a future status needs
                    it too. */}
                {(EXCEPTION_STATUS_LABELS[state.current_status_code] ?? (qaFailedRework ? 'QA failed' : undefined)) && (
                  <span className="inline-flex items-center rounded-full bg-amber-50 border border-amber-300 px-2 py-0.5 text-[11px] font-semibold text-amber-700 dark:bg-amber-900/20 dark:border-amber-700/40 dark:text-amber-400">
                    {t('workflow.exceptionBadge', 'Exception · {{reason}}', { reason: EXCEPTION_STATUS_LABELS[state.current_status_code] ?? 'QA failed' })}
                  </span>
                )}
                {otherTaskPending && (
                  <span className="inline-flex items-center gap-1.5 text-xs text-ui-text3">
                    <span className="relative flex h-2 w-2 flex-shrink-0">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-warning opacity-75" />
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-warning" />
                    </span>
                    {t('workflow.tasksInProgress', 'Tasks in progress')}
                  </span>
                )}
                {state.updated_on && (
                  <span className="ml-auto text-xs text-ui-text3 whitespace-nowrap">
                    {t('workflow.lastUpdated', 'Last updated {{when}}', { when: relativeTime(state.updated_on) })}
                  </span>
                )}
              </div>
            </div>
          ) : null}
        </div>
      )}

      {/* SAM narration + closed-early/on-hold — always shown below the sticky rail */}
      {engagementId && state && ((statusNarrationLoading || !!statusNarration) || !!engagement?.closed_early || state.on_hold) && (
        <div className="space-y-2">
          {/* SAM status card — real LLM-authored 1-2 sentence status update + a few status
              chips, narrating the same transition/gate/authority data below (not a second
              source of truth). Sits below the stage rail/status badge above (the "workflow
              chronology") rather than repeating what that already shows — its own chips are
              told not to restate the current stage/status name for the same reason.
              Regenerated when the engagement or its status changes (see the query's
              statusCode cache key); "Refresh" re-runs it on demand for changes within the
              same status (e.g. a measurement just added). */}
              {(statusNarrationLoading || statusNarration) && (
                <div className="bg-accent-soft/40 dark:bg-accent/10 px-3 py-2 rounded-b-2xl">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-accent-fill text-xs font-bold text-on-accent shrink-0">S</span>
                    <span className="text-xs font-semibold text-accent-text">SAM</span>
                    <button
                      type="button"
                      onClick={() => void refetchStatusNarration()}
                      disabled={statusNarrationLoading}
                      className="ml-auto text-xs text-ui-text3 hover:text-ui-text disabled:opacity-50"
                      title="Refresh status"
                    >
                      {statusNarrationLoading ? '…' : '↻'}
                    </button>
                  </div>
                  {statusNarrationLoading && !statusNarration ? (
                    <p className="text-sm text-ui-text3">Thinking…</p>
                  ) : (
                    <>
                      <p className="text-sm text-ui-text">{statusNarration!.summary}</p>
                      {statusNarration!.chips.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {statusNarration!.chips.map((chip, i) => (
                            <span
                              key={i}
                              className={cn(
                                'text-xs font-medium px-2 py-0.5 rounded-full',
                                chip.tone === 'positive' && 'bg-success/15 text-success-text',
                                chip.tone === 'warning' && 'bg-warning/15 text-warning-text',
                                chip.tone === 'neutral' && 'bg-ui-subtle text-ui-text2',
                              )}
                            >
                              {chip.tone === 'positive' && '✓ '}
                              {chip.label}
                            </span>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* G1 — permanent closed-early banner */}
              {engagement?.closed_early ? (
                <div className="bg-danger/10 px-3 py-2 border-l-2 border-danger">
                  <p className="text-xs font-medium text-danger-text">
                    {t('workflow.closedEarly', 'Closed early')} — {humanize(engagement.closed_early.closure_type)}
                  </p>
                  <p className="mt-0.5 text-xs text-danger-text/90">{engagement.closed_early.reason}</p>
                </div>
              ) : state.on_hold ? (
                /* G2/G3 — on-hold banner with resume */
                <div className="bg-warning/10 px-3 py-2 border-l-2 border-warning">
                  <p className="text-xs font-medium text-warning-text">{t('workflow.onHold', 'On hold')}</p>
                  {state.on_hold_reason && <p className="mt-0.5 text-xs text-warning-text">{state.on_hold_reason}</p>}
                  {state.on_hold_since && (
                    <p className="mt-0.5 text-[10px] text-warning-text">
                      {t('workflow.onHoldSince', 'On hold since {{time}}', { time: new Date(state.on_hold_since).toLocaleString() })}
                    </p>
                  )}
                  {notifyCustomerError && <p className="mt-1 text-[10px] text-danger-text">{notifyCustomerError}</p>}
                  {notifyCustomerSent && <p className="mt-1 text-[10px] text-green-700 dark:text-green-400">{t('jobs.customerNotifiedSent', 'Customer notified.')}</p>}
                  <div className="mt-2 flex justify-end gap-2">
                    {engagementId && (
                      <Button
                        variant="ghost" size="sm" loading={notifyingCustomer}
                        onClick={() => void submitNotifyCustomerHold()}
                      >
                        {t('jobs.notifyCustomer', 'Notify Customer')}
                      </Button>
                    )}
                    <Button size="sm" loading={resuming} onClick={() => void submitResume()}>{t('workflow.resume', 'Resume')}</Button>
                  </div>
                </div>
              ) : null}
        </div>
      )}

      {/* HOLD DETAILS PANEL — collapsible panel for job_execution_on_hold.
          Contains JOB CARD, SITE BOARD, HOLD grid, HOLD NOTES, and FOLLOW-UPS.
          Auto-opens via useEffect above. The "Execution updates" panel is suppressed for
          this status so all execution context lives here instead. */}
      {engagementId && state && engagement && state.current_status_code === 'job_execution_on_hold' && (() => {
        const d = (engagement.data ?? {}) as Record<string, unknown>;
        const holdReason = typeof d.hold_reason === 'string' ? d.hold_reason : null;
        const holdPlacedAt = typeof d.hold_placed_at === 'string' ? d.hold_placed_at : null;
        const holdPlacedBy = typeof d.hold_placed_by === 'string' ? d.hold_placed_by : null;
        const holdExpectedResume = typeof d.hold_expected_resume === 'string' ? d.hold_expected_resume : null;
        const holdNotes = typeof d.hold_notes === 'string' ? d.hold_notes : null;
        const holdFollowUps = Array.isArray(d.hold_follow_ups) ? (d.hold_follow_ups as string[]) : [];
        const holdSubtitle = [
          holdPlacedAt ? `On hold since ${new Date(holdPlacedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}` : null,
          holdReason ? `reason: ${holdReason}` : null,
        ].filter(Boolean).join(' · ');
        return (
          <div className="border-b border-ui-border px-[18px] py-4">
            {/* Toggle button — card style matching Execution updates */}
            <button
              type="button"
              onClick={() => setHoldDetailsPanelOpen(v => !v)}
              className="w-full text-left flex items-center gap-[10px] border border-[#ededed] bg-[#f7f9fd] rounded-[10px] px-[14px] py-3 hover:bg-ui-hover transition-colors dark:border-ui-border dark:bg-ui-subtle"
            >
              <div className="flex-1 min-w-0">
                <p className="text-[14px] font-bold text-ui-text">
                  {t('jobs.holdDetails', 'Hold details')}
                </p>
                {holdSubtitle && (
                  <p className="mt-[3px] text-[11.5px] text-ui-text3 truncate">{holdSubtitle}</p>
                )}
              </div>
              <span className="shrink-0 rounded-[6px] border border-[#D9BBFF] bg-white px-2 py-[3px] text-[11px] font-semibold text-[#3F2D93] dark:bg-ui-card">
                {holdDetailsPanelOpen ? t('common.hide', 'Hide') : t('common.show', 'Show')}
              </span>
              <span className="shrink-0 flex h-[22px] w-[22px] items-center justify-center rounded-full border border-[#ededed] bg-white text-[11px] text-[#575757] dark:bg-ui-card dark:border-ui-border">
                {holdDetailsPanelOpen ? '▴' : '▾'}
              </span>
            </button>

            {holdDetailsPanelOpen && (
              <div className="flex flex-col gap-3 mt-3">
                {/* JOB CARD */}
                {/* Only where the workflow attaches the Job Card (job_card_panel) — same rule SAM reads. */}
                {jobCardAttached && (<>
                {jobCardSummary && (
                  <div className="border border-[#ededed] rounded-[10px] overflow-hidden dark:border-ui-border">
                    <div className="flex items-center gap-2 bg-[#f7f9fd] px-[14px] py-[10px] border-b border-[#ededed] dark:bg-ui-subtle dark:border-ui-border">
                      <p className="flex-1 text-[10.5px] font-bold tracking-[0.09em] uppercase text-[#767676] dark:text-ui-text3">
                        {t('jobs.jobCard', 'Job card')}
                      </p>
                      <InvoiceJobCardDownloadButton engagementId={engagementId!} />
                    </div>
                    <div className="flex flex-wrap gap-2 px-[14px] py-3 border-b border-[#ededed] dark:border-ui-border">
                      <button type="button" onClick={() => setJobCardProposalModalOpen(true)}
                        className="flex items-center gap-[7px] rounded-[9px] border border-[#ededed] bg-white px-[13px] py-[9px] text-[12.5px] font-semibold text-[#575757] hover:bg-ui-subtle dark:bg-ui-card dark:border-ui-border">
                        ▤ {t('jobs.proposalDetails', 'Proposal details')}
                      </button>
                      <button type="button" onClick={() => setAssessmentModalOpen(true)}
                        className="flex items-center gap-[7px] rounded-[9px] border border-[#ededed] bg-white px-[13px] py-[9px] text-[12.5px] font-semibold text-[#575757] hover:bg-ui-subtle dark:bg-ui-card dark:border-ui-border">
                        ▤ {t('jobs.assessmentDetails', 'Assessment details')}
                      </button>
                      <button type="button" onClick={() => setMaterialListModalOpen(true)}
                        className="flex items-center gap-[7px] rounded-[9px] border border-[#D9BBFF] bg-[#F4EEFF] px-[13px] py-[9px] text-[12.5px] font-semibold text-[#3F2D93] hover:opacity-80">
                        ⧉ {t('jobs.materialList', 'Material list')}
                      </button>
                      <button type="button" onClick={openNotesModal}
                        className="flex items-center gap-[7px] rounded-[9px] border border-[#ededed] bg-white px-[13px] py-[9px] text-[12.5px] font-semibold text-[#575757] hover:bg-ui-subtle dark:bg-ui-card dark:border-ui-border">
                        ✎ {t('jobs.notes', 'Notes')}{notesForModal.length > 0 ? ` (${notesForModal.length})` : ''}
                      </button>
                    </div>
                    <div className="grid grid-cols-2">
                      {jobCardSummary.project_manager_name && (
                        <div className="px-[14px] py-[9px] border-b border-r border-[#f2f2f2] dark:border-ui-border">
                          <p className="text-[10.5px] text-[#767676] dark:text-ui-text3">{t('jobs.projectManager', 'Project Manager')}</p>
                          <p className="mt-0.5 text-[12.5px] font-semibold text-ui-text text-pretty">{[jobCardSummary.project_manager_name, jobCardSummary.project_manager_phone].filter(Boolean).join(' · ')}</p>
                        </div>
                      )}
                      {jobCardSummary.pm_assigned_by_name && (
                        <div className="px-[14px] py-[9px] border-b border-[#f2f2f2] dark:border-ui-border">
                          <p className="text-[10.5px] text-[#767676] dark:text-ui-text3">{t('jobs.assignedBy', 'Assigned by')}</p>
                          <p className="mt-0.5 text-[12.5px] font-semibold text-ui-text text-pretty">
                            {jobCardSummary.pm_assigned_by_name}
                            {jobCardSummary.pm_assigned_on ? ` · ${new Date(jobCardSummary.pm_assigned_on).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}` : ''}
                          </p>
                        </div>
                      )}
                      {jobCardSummary.accepted_proposal_number && (
                        <div className="px-[14px] py-[9px] border-b border-r border-[#f2f2f2] dark:border-ui-border">
                          <p className="text-[10.5px] text-[#767676] dark:text-ui-text3">{t('jobs.acceptedProposal', 'Accepted proposal')}</p>
                          <p className="mt-0.5 text-[12.5px] font-semibold text-ui-text text-pretty">
                            {jobCardSummary.accepted_proposal_number}
                            {jobCardSummary.accepted_proposal_date ? ` · accepted ${new Date(jobCardSummary.accepted_proposal_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}` : ''}
                          </p>
                        </div>
                      )}
                      <div className="px-[14px] py-[9px] border-b border-[#f2f2f2] dark:border-ui-border">
                        <p className="text-[10.5px] text-[#767676] dark:text-ui-text3">{t('jobs.contractValue', 'Contract value')}</p>
                        {jobCardSummary.contract_value_restricted
                          ? <p className="mt-0.5 text-[12.5px] text-[#a0a0a0]">Restricted</p>
                          : <p className="mt-0.5 text-[12.5px] font-semibold text-ui-text">{jobCardSummary.contract_value != null ? `$${jobCardSummary.contract_value.toLocaleString()}` : '—'}</p>}
                      </div>
                      {jobCardSummary.schedule?.scheduled_date && (
                        <div className="px-[14px] py-[9px] border-b border-r border-[#f2f2f2] dark:border-ui-border">
                          <p className="text-[10.5px] text-[#767676] dark:text-ui-text3">{t('jobs.scheduledWindow', 'Scheduled window')}</p>
                          <p className="mt-0.5 text-[12.5px] font-semibold text-ui-text text-pretty">
                            {new Date(jobCardSummary.schedule.scheduled_date + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
                            {jobCardSummary.schedule.start_time ? ` · ${jobCardSummary.schedule.start_time}` : ''}
                            {jobCardSummary.schedule.customer_window_note ? `–${jobCardSummary.schedule.customer_window_note}` : ''}
                          </p>
                        </div>
                      )}
                      {jobCardSummary.crew && (
                        <div className="px-[14px] py-[9px] border-b border-[#f2f2f2] dark:border-ui-border">
                          <p className="text-[10.5px] text-[#767676] dark:text-ui-text3">{t('jobs.assignedCrew', 'Assigned crew')}</p>
                          <p className="mt-0.5 text-[12.5px] font-semibold text-ui-text text-pretty">{jobCardSummary.crew.name}</p>
                        </div>
                      )}
                      {jobCardSummary.crew_lead_name && (
                        <div className="px-[14px] py-[9px] border-b border-r border-[#f2f2f2] dark:border-ui-border">
                          <p className="text-[10.5px] text-[#767676] dark:text-ui-text3">{t('jobs.crewLead', 'Crew lead')}</p>
                          <p className="mt-0.5 text-[12.5px] font-semibold text-ui-text text-pretty">{[jobCardSummary.crew_lead_name, jobCardSummary.crew_lead_phone].filter(Boolean).join(' · ')}</p>
                        </div>
                      )}
                      {jobCardSummary.materials_delivery_note && (
                        <div className="px-[14px] py-[9px] border-b border-[#f2f2f2] dark:border-ui-border">
                          <p className="text-[10.5px] text-[#767676] dark:text-ui-text3">{t('jobs.materials', 'Materials')}</p>
                          <p className="mt-0.5 text-[12.5px] font-semibold text-ui-text text-pretty">{jobCardSummary.materials_delivery_note}</p>
                        </div>
                      )}
                      {(() => {
                        const startedAt = typeof d.execution_started_at === 'string' ? d.execution_started_at : null;
                        const startedStr = startedAt ? new Date(startedAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : null;
                        return startedStr ? (
                          <div className="px-[14px] py-[9px] border-b border-r border-[#f2f2f2] dark:border-ui-border">
                            <p className="text-[10.5px] text-[#767676] dark:text-ui-text3">{t('jobs.execution', 'Execution')}</p>
                            <p className="mt-0.5 text-[12.5px] font-semibold text-ui-text text-pretty">Started {startedStr}</p>
                          </div>
                        ) : null;
                      })()}
                    </div>
                  </div>
                )}
                </>)}

                {/* (Site Board renders standalone at these statuses — see SITE_BOARD_IN_STAGE_CARD.) */}

                {/* HOLD section — 2×2 grid */}
                {(holdReason || holdPlacedAt || holdPlacedBy || holdExpectedResume) && (
                  <div className="border border-[#ededed] rounded-[10px] px-[14px] py-3 dark:border-ui-border">
                    <p className="text-[10.5px] font-bold tracking-[0.09em] uppercase text-[#767676] dark:text-ui-text3">
                      {t('jobs.hold', 'Hold')}
                    </p>
                    <div className="grid grid-cols-2 gap-x-[14px] gap-y-[9px] mt-[9px]">
                      {holdReason && (
                        <div>
                          <p className="text-[11px] text-[#767676] dark:text-ui-text3">{t('jobs.reason', 'Reason')}</p>
                          <p className="mt-0.5 text-[12.5px] font-semibold text-ui-text">{holdReason}</p>
                        </div>
                      )}
                      {holdPlacedAt && (
                        <div>
                          <p className="text-[11px] text-[#767676] dark:text-ui-text3">{t('jobs.onHoldSince', 'On hold since')}</p>
                          <p className="mt-0.5 text-[12.5px] font-semibold text-ui-text">{new Date(holdPlacedAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</p>
                        </div>
                      )}
                      {holdPlacedBy && (
                        <div>
                          <p className="text-[11px] text-[#767676] dark:text-ui-text3">{t('jobs.placedBy', 'Placed by')}</p>
                          <p className="mt-0.5 text-[12.5px] font-semibold text-ui-text">{holdPlacedBy}</p>
                        </div>
                      )}
                      {holdExpectedResume && (
                        <div>
                          <p className="text-[11px] text-[#767676] dark:text-ui-text3">{t('jobs.expectedResume', 'Expected resume')}</p>
                          <p className="mt-0.5 text-[12.5px] font-semibold text-ui-text">{holdExpectedResume}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* HOLD NOTES amber card */}
                {holdNotes && (
                  <div className="border border-[#E8C98F] bg-[#FBF0DC] rounded-[10px] px-[14px] py-3 dark:border-amber-700/40 dark:bg-amber-900/15">
                    <p className="text-[10.5px] font-bold tracking-[0.09em] uppercase text-[#8A5A06] dark:text-amber-400">
                      {t('jobs.holdNotes', 'Hold Notes')}
                    </p>
                    <p className="mt-1 text-[12.5px] text-[#8A5A06] leading-relaxed dark:text-amber-300">
                      {holdNotes}
                    </p>
                  </div>
                )}

                {/* FOLLOW-UPS chips */}
                {holdFollowUps.length > 0 && (
                  <div className="border border-[#ededed] rounded-[10px] px-[14px] py-3 dark:border-ui-border">
                    <p className="text-[10.5px] font-bold tracking-[0.09em] uppercase text-[#767676] dark:text-ui-text3">
                      {t('jobs.followUps', 'Follow-Ups')}
                    </p>
                    <div className="flex flex-wrap gap-[6px] mt-[9px]">
                      {holdFollowUps.map((item, i) => (
                        <span key={i} className="bg-[#f2f2f2] text-[#575757] rounded-[6px] px-[9px] py-[3px] text-[11.5px] dark:bg-ui-subtle dark:text-ui-text2">
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {/* A Quick Quote is possible straight off the property's roof imagery any time before a
          real measurement exists — binding controlled by the workflow definition's module_displays
          for the current status, not hardcoded here. PropertyRoofEstimateSection is the whole
          card (EST-FEAT-04): the live estimate, plus — once expanded — adjusting the inputs and
          saving an official versioned quote. Previously two stacked cards (this one plus a
          separate EngagementQuickQuoteCard that stayed empty until saved); merged into one so the
          live number and the saved-quote controls act on the same figure instead of reading as
          two different "Quick Quote" widgets. */}
      {engagementId && state?.active_display_modules?.includes('quick_quote_panel') && engagement?.property_id && (
        <div ref={quickQuoteSectionRef} className="space-y-2">
          {quickQuoteExpanded ? (
            <PropertyRoofEstimateSection
              propertyId={engagement.property_id}
              engagementId={engagementId}
              // Pressing "Quick Quote" with nothing stored IS the request to measure and quote.
              // Opening an already-quoted property just shows what was stored.
              autoGenerate={!savedQuickQuote}
              onHide={() => setQuickQuoteExpanded(false)}
              openSatelliteMap={!!openQuickQuoteMapEngagementId && openQuickQuoteMapEngagementId === engagementId}
              readOnly={!!engagement?.closed_early}
            />
          ) : (
            <div className="flex items-center justify-between gap-3 rounded-xl bg-accent-soft px-3 py-2">
              <div className="min-w-0">
                {quickQuoteSummary ? (
                  <p className="truncate text-xs font-semibold text-accent-text">
                    {fmtMoneyK(quickQuoteSummary.low)}
                    {quickQuoteSummary.high != null && quickQuoteSummary.high !== quickQuoteSummary.low
                      && `–${fmtMoneyK(quickQuoteSummary.high).replace('$', '')}`}
                    {quickQuoteSummary.areaSqft != null && (
                      <span className="font-normal text-accent-text/80">
                        {' '}· {quickQuoteSummary.areaSqft.toLocaleString()} sqft
                      </span>
                    )}
                    {/* Only says something when there IS something to say. "Saved" was noise —
                        the pill sits inside the Quick Quote card, so a stored quote needs no
                        label at all; the figures are the point. What still earns a word is an
                        ADJUSTED quote (someone overrode the measurement or complexity, so this is
                        not what the AI would compute), and an unsaved estimate, which is a live
                        recalculation nobody has committed to yet. */}
                    {(quickQuoteSummary.isAdjusted || !quickQuoteSummary.isSaved) && (
                      <span className="font-normal text-accent-text/80">
                        {' '}— {quickQuoteSummary.isAdjusted
                          ? t('workflow.quickQuoteHintAdjusted', 'adjusted')
                          : t('workflow.quickQuoteHintCaptured', 'Quick Quote available')}
                      </span>
                    )}
                  </p>
                ) : (
                  <p className="truncate text-xs font-semibold text-accent-text">
                    {t('workflow.quickQuoteHint', 'Quick Quote — no measurement needed')}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => setQuickQuoteExpanded(true)}
                className="flex-shrink-0 rounded-lg border border-accent bg-ui-card px-3 py-1.5 text-xs font-semibold text-accent-text hover:bg-ui-hover hover:border-transparent btn-icon-hover"
              >
                {t('workflow.quickQuoteOpen', 'Quick Quote')}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Status displays — every business module bound to the CURRENT status via a
          module_displays entry (WFL, POST side). Data-driven: no hardcoded status check here —
          state.active_display_modules lists whatever the workflow definition binds at this
          status (e.g. Assessment Scheduled -> meeting card + Assessment Capture tool), and only
          codes with a registered component in BUSINESS_MODULE_DISPLAY_COMPONENTS render. */}
      {engagementId && ((state?.active_display_modules?.length ?? 0) > 0 || assessmentOpenSignal > 0) && (
        <div className="space-y-2">
          {(
            // assessment_capture_tool is only bound (auto-shown) at assessment_scheduled. Past
            // that, "Open assessment card" opens it as a MODAL (below) rather than appending it
            // to this list — the card is a full capture tool, and unfolding one inside an
            // already-long workflow card is the same mistake the estimate editor made before it
            // became a modal.
            state!.active_display_modules
          )
            // The meeting renders INSIDE AssessmentCaptureCard, under the heading that names it
            // ("Assessment meeting & capture"), so it is suppressed here whenever that card is
            // also active — otherwise the same meeting appears twice, once above the card and
            // once inside it. When the capture tool is NOT bound the meeting still needs its own
            // module, so this is conditional rather than an outright removal.
            // quick_quote_panel has its own dedicated section above, which wraps the single
            // merged Quick Quote card in a collapsed cost-summary pill. It is still bound like
            // any other module (that section is gated on this very list) — it just renders there
            // instead of here, so excluding it stops the card appearing twice.
            .filter((code) => code !== 'quick_quote_panel')
            .filter((code) => !(
              code === 'meeting_schedule_for_assessment'
              && state!.active_display_modules.includes('assessment_capture_tool')
            ))
            // Same rule, same reason as the meeting card above: the proposal renders INSIDE
            // EstimateSummaryDisplayCard's fold (the approved mockup's one "Estimate set &
            // proposal draft" wrapper), so showing it here too would put the same draft on the
            // page twice — once above the wrapper and once within it. Conditional rather than an
            // outright removal because the estimate panel is bound at only three statuses: at
            // proposal_approved/sent/accepted/rejected there is no wrapper, and the proposal must
            // still render on its own.
            .filter((code) => !(
              code === 'proposal_summary_panel'
              && state!.active_display_modules.includes('estimate_summary_panel')
            ))
            .map((code) => {
              const DisplayComponent = BUSINESS_MODULE_DISPLAY_COMPONENTS[code];
              if (!DisplayComponent) return null;
              const highlight = code === 'assessment_capture_tool' && assessmentAttention;
              return (
                <div key={code} className={highlight ? 'attention-shake attention-ring rounded-2xl' : undefined}>
                  <DisplayComponent
                    engagementId={engagementId} propertyId={engagement?.property_id} tasks={tasks}
                    assessmentOpenSignal={assessmentOpenSignal}
                    onOpenAssessment={() => { setAssessmentOpenSignal((v) => v + 1); setAssessmentModalOpen(true); }}
                    readOnly={code === 'assessment_capture_tool' && !state!.active_display_modules.includes('assessment_capture_tool')}
                    {...(code === 'estimate_summary_panel' ? { estimateOpenSignal: ppEstimateSignal, previewOpenSignal: ppPreviewSignal, estimateInteractSignal, previewInteractSignal, onSurfaceContext: setEstimateSurfaceState } : {})}
                    {...(code === 'proposal_summary_panel' ? { previewOpenSignal: ppPreviewSignal, previewInteractSignal } : {})}
                  />
                </div>
              );
            })}
        </div>
      )}

      {/* Project handoff summary — shown wherever the workflow attaches HANDOFF_MODULE (definition
          module_displays, seeded at Job Initiated), never by a status list. Needs a PM to summarise. */}
      {engagementId && engagement?.project_manager_id && jobCardSummary && handoffAttached && (
        <div className="border-b border-ui-border px-3 py-2.5">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="text-[13px] font-semibold text-ui-text">
                {t('jobs.projectHandoffTitle', 'Project handoff — PM assigned')}
              </p>
              <p className="mt-0.5 text-[11px] text-ui-text3">
                {[jobCardSummary.project_manager_name, 'PM'].filter(Boolean).join(' · ')}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setHandoffExpanded((v) => !v)}
              className="shrink-0 rounded-lg border border-ui-border px-2.5 py-1 text-[11px] font-medium text-ui-text2 hover:bg-ui-hover flex items-center gap-1"
            >
              {handoffExpanded ? t('common.hide', 'Hide') : t('common.view', 'View')}
              <span className="text-[9px]">{handoffExpanded ? '▲' : '▼'}</span>
            </button>
          </div>
        </div>
      )}

      {/* JOB-FEAT-02 — Job Card & Preparation Readiness, wherever the workflow attaches
          JOB_CARD_MODULE. With the handoff summary also attached it opens from that summary's
          View button; on its own it shows directly. (Later job stages embed their own Job Card in
          their collapsibles.) */}
      {engagementId && engagement?.project_manager_id && jobCardAttached
        && !(state && POST_SCHEDULE_STATUSES.has(state.current_status_code))
        && (handoffAttached ? handoffExpanded : true) && (
        <div ref={jobCardRef}>
        <JobCardDisplayCard
          engagementId={engagementId}
          onOpenProposal={() => { setJobCardProposalPreviewOpen(false); setJobCardProposalModalOpen(true); }}
          onOpenAssessment={() => { setAssessmentOpenSignal((v) => v + 1); setAssessmentModalOpen(true); }}
          onOpenNotes={openNotesModal}
        />
        </div>
      )}


      {/* JOB-FEAT-05 — unified QA Inspect modal (inspect_job = pass, fail_qa_rework = fail) */}
      {qaInspectModalOpen && engagementId && (
        <QaInspectModal
          engagementId={engagementId}
          propertyAddress={jobCardSummary?.property?.address_line_1 ?? undefined}
          initialResult={qaInspectInitialResult}
          onPass={(notes) => {
            setQaInspectModalOpen(false);
            void runTransition('inspect_job', notes);
          }}
          onFail={() => {
            // POST /qa/fail already ran fail_qa_rework on the server; running it again here
            // only failed (409) and showed an error right after a successful fail.
            setQaInspectModalOpen(false);
            refreshPropertyWorkspace();
            void refetchState();
            void refetchTransitions();
          }}
          onCancel={() => setQaInspectModalOpen(false)}
        />
      )}

      {schedulingJob && engagementId && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 overflow-y-auto"
          onClick={() => setSchedulingJob(false)}
        >
          <div
            className="my-6 w-full max-w-[560px] rounded-2xl bg-ui-card shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="flex items-center justify-between gap-3 border-b border-ui-border px-4 py-3">
              <div className="flex items-center gap-2">
                <span className="rounded bg-ui-subtle px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest text-ui-text3">
                  {t('jobs.scheduleLabel', 'Schedule')}
                </span>
                <p className="text-[13px] font-semibold text-ui-text">
                  {t('jobs.scheduleJobModalTitle', 'Schedule Job')}
                  {engagement?.data?.address_line_1 ? ` — ${engagement.data.address_line_1 as string}` : ''}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSchedulingJob(false)}
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-ui-text3 hover:bg-ui-hover hover:text-ui-text"
                aria-label={t('common.close', 'Close')}
              >
                ✕
              </button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[80vh]">
              <JobSchedulingForm
                engagementId={engagementId}
                onScheduled={() => {
                  setSchedulingJob(false);
                  // Saving the schedule only writes job_schedules (+ echoes crew_id/scheduled_date
                  // onto engagement.data, which is what the real `crew_assignment_required` gate
                  // reads — see JobService.schedule_job) — it must also fire the real `schedule_job`
                  // workflow transition, the same way every other action button in this app does.
                  //
                  // BUG FIXED 2026-09-18: this used to check the `transitions` array already sitting
                  // in React state, captured in this closure from BEFORE the schedule was saved — at
                  // that point the gate was still unsatisfied, so `executable` was false and the
                  // transition never fired, leaving the user stuck reopening the calendar forever
                  // (confirmed live: crew/date saved for real, status never advanced). Refetching
                  // HERE, after the save, gets the gate's now-current (satisfied) answer instead of
                  // the stale pre-save one. Once already past job_scheduled, `schedule_job` is no
                  // longer in the fresh list at all, so a reschedule still correctly fires nothing.
                  void refetchTransitions().then((result) => {
                    const fresh = result.data ?? [];
                    const scheduleTransition = fresh.find((tr) => tr.transition_code === 'schedule_job');
                    if (scheduleTransition?.executable) {
                      void runTransition('schedule_job');
                    }
                  });
                }}
                onCancel={() => setSchedulingJob(false)}
              />
            </div>
          </div>
        </div>
      )}

      {/* RESCHEDULE MODAL */}
      {reschedulingJob && engagementId && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 pt-[5vh]" onClick={() => setReschedulingJob(false)}>
          <div className="absolute inset-0 bg-black/40" />
          <div
            className="relative z-10 w-full max-w-xl rounded-2xl bg-ui-card shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 border-b border-ui-border px-4 py-3">
              <div className="flex items-center gap-2">
                <span className="rounded bg-ui-subtle px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest text-ui-text3">
                  {t('jobs.rescheduleLabel', 'Reschedule')}
                </span>
                <p className="text-[13px] font-semibold text-ui-text">
                  {t('jobs.rescheduleJobModalTitle', 'Reschedule Job')}
                  {engagement?.data?.address_line_1 ? ` — ${engagement.data.address_line_1 as string}` : ''}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setReschedulingJob(false)}
                className="ml-auto flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-ui-text3 hover:bg-ui-hover hover:text-ui-text"
              >
                ✕
              </button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[80vh]">
              <JobSchedulingForm
                engagementId={engagementId}
                isReschedule
                currentSchedule={jobCardSummary?.schedule?.scheduled_date ? {
                  date: new Date(jobCardSummary.schedule.scheduled_date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }),
                  time: jobCardSummary.schedule.start_time ?? '',
                  durationDays: daysSpanned(jobCardSummary.schedule.start_time, jobCardSummary.schedule.duration_minutes),
                  crewName: jobCardSummary.crew?.name ?? '',
                } : null}
                scheduleHistory={existingSchedule?.history}
                onScheduled={() => setReschedulingJob(false)}
                onCancel={() => setReschedulingJob(false)}
              />
            </div>
          </div>
        </div>
      )}

      {/* JOB-FEAT-05 — QA summary. Hidden for billing/schedule/execution stages (info in their collapsibles). */}
      {engagementId && engagement?.project_manager_id
        && !(state && (POST_SCHEDULE_STATUSES.has(state.current_status_code) || SCHEDULE_CARD_STATUSES.has(state.current_status_code) || EXECUTION_CARD_STATUSES.has(state.current_status_code))) && (
        <QaSummaryPanel engagementId={engagementId} />
      )}

      {/* JOB-FEAT — Execution Summary. Shown from job_execution through close so the PM
          and OA always see crew, date and material count without digging into the Job Card. */}
      {engagementId && state && JOB_EXEC_STATUSES.has(state.current_status_code)
        && !POST_SCHEDULE_STATUSES.has(state.current_status_code)
        && !EXECUTION_CARD_STATUSES.has(state.current_status_code)
        && jobCardSummary && (
        <div className="border-b border-ui-border px-3 py-3">
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-ui-text3">
            {t('jobs.executionSummary', 'Execution Summary')}
          </p>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs text-ui-text">
            {jobCardSummary.crew && (
              <div>
                <p className="text-[10px] text-ui-text3">{t('jobs.crew', 'Crew')}</p>
                <p className="font-medium">{jobCardSummary.crew.name}</p>
              </div>
            )}
            {jobCardSummary.schedule?.scheduled_date && (
              <div>
                <p className="text-[10px] text-ui-text3">{t('jobs.scheduledDate', 'Scheduled date')}</p>
                <p className="font-medium">
                  {new Date(jobCardSummary.schedule.scheduled_date).toLocaleDateString(undefined, {
                    month: 'short', day: 'numeric', year: 'numeric',
                  })}
                  {jobCardSummary.schedule.start_time && ` · ${jobCardSummary.schedule.start_time}`}
                </p>
              </div>
            )}
            {!jobCardSummary.contract_value_restricted && jobCardSummary.contract_value != null && (
              <div>
                <p className="text-[10px] text-ui-text3">{t('jobs.contractValue', 'Contract value')}</p>
                <p className="font-medium">${jobCardSummary.contract_value.toLocaleString()}</p>
              </div>
            )}
            {Object.keys(jobCardSummary.materials_by_supplier).length > 0 && (() => {
              const allLines = Object.values(jobCardSummary.materials_by_supplier).flat();
              const supplierCount = Object.keys(jobCardSummary.materials_by_supplier).length;
              return (
                <div>
                  <p className="text-[10px] text-ui-text3">{t('jobs.materials', 'Materials')}</p>
                  <p className="font-medium">
                    {allLines.length} item{allLines.length !== 1 ? 's' : ''}{' · '}
                    {supplierCount} supplier{supplierCount !== 1 ? 's' : ''}
                  </p>
                </div>
              );
            })()}
            {jobCardSummary.accepted_option_label && (
              <div className="col-span-2">
                <p className="text-[10px] text-ui-text3">{t('jobs.acceptedOption', 'Accepted option')}</p>
                <p className="font-medium">{jobCardSummary.accepted_option_label}</p>
              </div>
            )}
          </div>
        </div>
      )}


      {/* "Open assessment card" opens the capture tool as an overlay, the way View / Edit
          estimate opens the estimate editor — the two are peers, and one of them unfolding
          inline while the other opened full-size was just inconsistent. */}
      {jobCardProposalModalOpen && engagementId && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 overflow-y-auto"
          onClick={() => setJobCardProposalModalOpen(false)}
        >
          <div
            className={jobCardProposalPreviewOpen ? 'contents' : 'my-6 w-full max-w-[760px] rounded-2xl bg-ui-card p-4 shadow-2xl'}
            onClick={(e) => e.stopPropagation()}
          >
            {!jobCardProposalPreviewOpen && (
              <div className="mb-2 flex items-center justify-between">
                <p className="text-sm font-bold text-ui-text">{t('jobs.proposalDetails', 'Proposal details')}</p>
                <button type="button" onClick={() => setJobCardProposalModalOpen(false)} className="text-ui-text3 hover:text-ui-text" aria-label={t('workflow.close', 'Close')}>✕</button>
              </div>
            )}
            <ProposalDisplayCard
              engagementId={engagementId}
              propertyId={engagement?.property_id}
              previewOpenSignal={1}
              onSurfaceContext={(state) => {
                if (state.preview_open) {
                  setJobCardProposalPreviewOpen(true);
                } else if (jobCardProposalPreviewOpen) {
                  // The inner preview just closed (its own X was clicked) — close the whole
                  // thing in the same click, rather than falling back to the summary panel.
                  setJobCardProposalModalOpen(false);
                  setJobCardProposalPreviewOpen(false);
                }
              }}
            />
          </div>
        </div>
      )}

      {assessmentModalOpen && engagementId && (
        <div
          className="modal-scrim fixed inset-0 z-overlay flex items-start justify-center p-4"
          onClick={() => setAssessmentModalOpen(false)}
        >
          <div
            className="my-6 flex max-h-[calc(100dvh-3rem)] w-full max-w-[760px] flex-col overflow-hidden rounded-2xl bg-ui-card shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-shrink-0 items-start gap-2.5 border-b border-ui-border px-5 py-3.5">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold text-ui-text">
                  {t('workflow.assessmentCardTitle', 'Assessment card')}
                </p>
                {/* Says WHICH assessment this is and who stands behind it. The old header named
                    the module; a Sales Agent opening it already knows that, and what they cannot
                    see is the property and who captured it. */}
                <p className="mt-0.5 truncate text-xs text-ui-text3">
                  {[
                    property?.address?.display_line,
                    assessmentMeta?.captured,
                  ].filter(Boolean).join(' · ')}
                </p>
              </div>
              <button
                type="button" onClick={() => setAssessmentModalOpen(false)}
                aria-label={t('workflow.close', 'Close')}
                className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md border border-ui-border text-sm text-ui-text2 hover:bg-ui-hover"
              >
                ×
              </button>
            </div>
            <div ref={assessmentScrollRef} className="min-h-0 flex-1 overflow-y-auto p-4">
              {/* The verdict, up front. It used to open on a wall of "does not apply" warnings —
                  the answer to "is this assessment good enough to price?" was somewhere below
                  fourteen lines of what was NOT captured. */}
              {assessmentMeta && (
                <div className={`mb-3 rounded-[10px] border px-3 py-2.5 text-xs leading-relaxed ${
                  assessmentMeta.blocking > 0
                    ? 'border-danger/40 bg-danger/10 text-danger-text'
                    : 'border-accent-soft bg-accent-soft/30 text-accent-text'
                }`}>
                  <b>{assessmentMeta.blocking > 0 ? '⛔ ' : '✓ '}{assessmentMeta.verdict}</b>
                  {assessmentMeta.facts && <> — {assessmentMeta.facts}</>}
                </div>
              )}
              <AssessmentCaptureCard
                engagementId={engagementId}
                propertyId={engagement?.property_id}
                tasks={tasks}
                assessmentOpenSignal={assessmentOpenSignal}
                initiallyExpanded
                hideHeader
                // Read-only whenever capture is no longer the live step. Opened from the estimate
                // card at assessment_completed the record is evidence, not a form: every section
                // still shows, none of it can be changed. It stays editable only while
                // assessment_capture_tool is genuinely bound to the current status.
                readOnly={!state?.active_display_modules?.includes('assessment_capture_tool') || !!engagement?.closed_early}
              />
              <div className="mt-3.5 flex justify-end">
                <button
                  type="button" onClick={() => setAssessmentModalOpen(false)}
                  className="rounded-lg border border-ui-border bg-ui-card px-3.5 py-2 text-xs font-semibold text-ui-text2 hover:bg-ui-hover"
                >
                  {t('workflow.close', 'Close')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* NOTES MODAL — opened by the "✎ Notes" Job Card quick-link. Same overlay pattern as the
          Proposal/Assessment modals above; fetches lazily on open. */}
      {notesModalOpen && engagementId && engagement?.property_id && (
        <NotesModal
          onClose={() => setNotesModalOpen(false)}
          propertyId={engagement.property_id}
          engagementId={engagementId}
          notes={notesForModal}
          address={[
            jobCardSummary?.property?.address_line_1,
            jobCardSummary?.property?.city,
            jobCardSummary?.property?.state,
          ].filter(Boolean).join(', ') || undefined}
        />
      )}

      {materialListModalOpen && engagementId && (
        <MaterialListModal
          engagementId={engagementId}
          onClose={() => setMaterialListModalOpen(false)}
        />
      )}

      {/* Site Board — standalone wherever the workflow binds it and no stage card embeds it below its
          Job Card (e.g. Job Initiated, on hold, billing settled, post-job, closed). The stage cards only
          render once the engagement and job card are loaded, so until then it stays standalone too. */}
      {engagementId && state && siteBoardAttached
        && (!SITE_BOARD_IN_STAGE_CARD.has(state.current_status_code) || !engagement || !jobCardSummary) && (
        <SiteBoardSection engagementId={engagementId} />
      )}

      {/* SCHEDULE & PREPARATION READINESS — job_scheduled only; job_prepared gets its own panel. */}
      {engagementId && state && engagement && jobCardSummary
        && state.current_status_code === 'job_scheduled' && (() => {
        const d = (engagement.data ?? {}) as Record<string, unknown>;
        const blockingText = typeof d.preparation_blocking_text === 'string' ? d.preparation_blocking_text : null;
        const schedDate = jobCardSummary.schedule?.scheduled_date
          ? new Date(jobCardSummary.schedule.scheduled_date).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })
          : null;
        const timeStr = jobCardSummary.schedule?.start_time ?? null;
        const windowNote = jobCardSummary.schedule?.customer_window_note ?? null;
        const crewName = jobCardSummary.crew?.name ?? null;
        const subtitle = [schedDate, timeStr && windowNote ? `${timeStr}–${windowNote}` : (timeStr ?? windowNote), crewName].filter(Boolean).join(' · ');

        return (
          <div className="border-b border-ui-border px-[18px] py-4">
            <button
              type="button"
              onClick={() => setSchedulePanelVisible(v => !v)}
              className="w-full text-left flex items-center gap-[10px] border border-[#ededed] bg-[#f7f9fd] rounded-[10px] px-[14px] py-3 hover:bg-ui-hover transition-colors dark:border-ui-border dark:bg-ui-subtle"
            >
              <div className="flex-1 min-w-0">
                <p className="text-[14px] font-bold text-ui-text">
                  {t('jobs.schedulePrepTitle', 'Schedule & preparation readiness')}
                </p>
                {subtitle && <p className="mt-[3px] text-[11.5px] text-ui-text3 truncate">{subtitle}</p>}
              </div>
              <span className="shrink-0 rounded-[6px] border border-[#D9BBFF] bg-white px-2 py-[3px] text-[11px] font-semibold text-[#3F2D93] dark:bg-ui-card">
                {schedulePanelVisible ? t('common.hide', 'Hide') : t('common.show', 'Show')}
              </span>
              <span className="shrink-0 flex h-[22px] w-[22px] items-center justify-center rounded-full border border-[#ededed] bg-white text-[11px] text-[#575757] dark:bg-ui-card dark:border-ui-border">
                {schedulePanelVisible ? '▴' : '▾'}
              </span>
            </button>

            {schedulePanelVisible && (
              <div className="flex flex-col gap-3 mt-3">
                {/* JOB CARD section */}
                {/* Only where the workflow attaches the Job Card (job_card_panel) — same rule SAM reads. */}
                {jobCardAttached && (<>
                <div className="border border-[#ededed] rounded-[10px] overflow-hidden dark:border-ui-border">
                  <div className="flex items-center gap-2 bg-[#f7f9fd] px-[14px] py-[10px] border-b border-[#ededed] dark:bg-ui-subtle dark:border-ui-border">
                    <p className="flex-1 text-[10.5px] font-bold tracking-[0.09em] uppercase text-[#767676] dark:text-ui-text3">
                      {t('jobs.jobCard', 'Job card')}
                    </p>
                    <InvoiceJobCardDownloadButton engagementId={engagementId!} />
                  </div>
                  {/* Tab navigation */}
                  <div className="flex flex-wrap gap-2 px-[14px] py-3 border-b border-[#ededed] dark:border-ui-border">
                    <button type="button" onClick={() => setJobCardProposalModalOpen(true)}
                      className="flex items-center gap-[7px] rounded-[9px] border border-[#ededed] bg-white px-[13px] py-[9px] text-[12.5px] font-semibold text-[#575757] hover:bg-ui-subtle dark:bg-ui-card dark:border-ui-border">
                      ▤ {t('jobs.proposalDetails', 'Proposal details')}
                    </button>
                    <button type="button" onClick={() => setAssessmentModalOpen(true)}
                      className="flex items-center gap-[7px] rounded-[9px] border border-[#ededed] bg-white px-[13px] py-[9px] text-[12.5px] font-semibold text-[#575757] hover:bg-ui-subtle dark:bg-ui-card dark:border-ui-border">
                      ▤ {t('jobs.assessmentDetails', 'Assessment details')}
                    </button>
                    <button type="button" onClick={() => setMaterialListModalOpen(true)}
                      className="flex items-center gap-[7px] rounded-[9px] border border-[#D9BBFF] bg-[#F4EEFF] px-[13px] py-[9px] text-[12.5px] font-semibold text-[#3F2D93] hover:opacity-80">
                      ⧉ {t('jobs.materialList', 'Material list')}
                    </button>
                    <button type="button" onClick={openNotesModal}
                      className="flex items-center gap-[7px] rounded-[9px] border border-[#ededed] bg-white px-[13px] py-[9px] text-[12.5px] font-semibold text-[#575757] hover:bg-ui-subtle dark:bg-ui-card dark:border-ui-border">
                      ✎ {t('jobs.notes', 'Notes')}
                    </button>
                  </div>
                  {/* 2-column data grid */}
                  <div className="grid grid-cols-2">
                    {jobCardSummary.project_manager_name && (
                      <div className="px-[14px] py-[9px] border-b border-r border-[#f2f2f2] dark:border-ui-border">
                        <p className="text-[10.5px] text-[#767676] dark:text-ui-text3">{t('jobs.projectManager', 'Project Manager')}</p>
                        <p className="mt-0.5 text-[12.5px] font-semibold text-ui-text text-pretty">{[jobCardSummary.project_manager_name, jobCardSummary.project_manager_phone].filter(Boolean).join(' · ')}</p>
                      </div>
                    )}
                    {jobCardSummary.pm_assigned_by_name && (
                      <div className="px-[14px] py-[9px] border-b border-[#f2f2f2] dark:border-ui-border">
                        <p className="text-[10.5px] text-[#767676] dark:text-ui-text3">{t('jobs.assignedBy', 'Assigned by')}</p>
                        <p className="mt-0.5 text-[12.5px] font-semibold text-ui-text text-pretty">
                          {jobCardSummary.pm_assigned_by_name}
                          {jobCardSummary.pm_assigned_on ? ` · ${new Date(jobCardSummary.pm_assigned_on).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}` : ''}
                        </p>
                      </div>
                    )}
                    {jobCardSummary.accepted_proposal_number && (
                      <div className="px-[14px] py-[9px] border-b border-r border-[#f2f2f2] dark:border-ui-border">
                        <p className="text-[10.5px] text-[#767676] dark:text-ui-text3">{t('jobs.acceptedProposal', 'Accepted proposal')}</p>
                        <p className="mt-0.5 text-[12.5px] font-semibold text-ui-text text-pretty">
                          {jobCardSummary.accepted_proposal_number}
                          {jobCardSummary.accepted_proposal_date ? ` · accepted ${new Date(jobCardSummary.accepted_proposal_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}` : ''}
                        </p>
                      </div>
                    )}
                    <div className="px-[14px] py-[9px] border-b border-[#f2f2f2] dark:border-ui-border">
                      <p className="text-[10.5px] text-[#767676] dark:text-ui-text3">{t('jobs.contractValue', 'Contract value')}</p>
                      {jobCardSummary.contract_value_restricted
                        ? <p className="mt-0.5 text-[12.5px] text-[#a0a0a0]">Restricted</p>
                        : <p className="mt-0.5 text-[12.5px] font-semibold text-ui-text">{jobCardSummary.contract_value != null ? `$${jobCardSummary.contract_value.toLocaleString()}` : '—'}</p>}
                    </div>
                    {jobCardSummary.schedule?.scheduled_date && (
                      <div className="px-[14px] py-[9px] border-b border-r border-[#f2f2f2] dark:border-ui-border">
                        <p className="text-[10.5px] text-[#767676] dark:text-ui-text3">{t('jobs.scheduledWindow', 'Scheduled window')}</p>
                        <p className="mt-0.5 text-[12.5px] font-semibold text-ui-text text-pretty">
                          {new Date(jobCardSummary.schedule.scheduled_date).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
                          {jobCardSummary.schedule.start_time ? ` · ${jobCardSummary.schedule.start_time}` : ''}
                          {jobCardSummary.schedule.customer_window_note ? `–${jobCardSummary.schedule.customer_window_note}` : ''}
                        </p>
                      </div>
                    )}
                    {jobCardSummary.crew && (
                      <div className="px-[14px] py-[9px] border-b border-[#f2f2f2] dark:border-ui-border">
                        <p className="text-[10.5px] text-[#767676] dark:text-ui-text3">{t('jobs.assignedCrew', 'Assigned crew')}</p>
                        <p className="mt-0.5 text-[12.5px] font-semibold text-ui-text text-pretty">{jobCardSummary.crew.name}</p>
                      </div>
                    )}
                    {jobCardSummary.crew_lead_name && (
                      <div className="px-[14px] py-[9px] border-b border-r border-[#f2f2f2] dark:border-ui-border">
                        <p className="text-[10.5px] text-[#767676] dark:text-ui-text3">{t('jobs.crewLead', 'Crew lead')}</p>
                        <p className="mt-0.5 text-[12.5px] font-semibold text-ui-text text-pretty">{[jobCardSummary.crew_lead_name, jobCardSummary.crew_lead_phone].filter(Boolean).join(' · ')}</p>
                      </div>
                    )}
                  </div>
                </div>
                </>)}

                {/* SITE BOARD — below the Job Card, as in every other job stage */}
                {siteBoardAttached && <SiteBoardSection engagementId={engagementId} />}

                {/* BLOCKING ITEMS — populated by SAM via engagement.data.preparation_blocking_text */}
                {blockingText && (
                  <div className="rounded-[10px] border border-[#E8C98F] bg-[#FBF0DC] px-[14px] py-3 dark:border-amber-700/40 dark:bg-amber-900/15">
                    <p className="text-[10.5px] font-bold tracking-[0.08em] uppercase text-[#8A5A06] dark:text-amber-400">
                      {t('jobs.blockingItems', 'Blocking items')}
                    </p>
                    <p className="mt-1 text-[12.5px] text-[#8A5A06] leading-relaxed dark:text-amber-300">{blockingText}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {/* JOB PREPARED — "Readiness confirmed" collapsible: Job Card + embedded Site Board */}
      {engagementId && state && engagement && jobCardSummary
        && state.current_status_code === 'job_prepared' && (() => {
        const d = (engagement.data ?? {}) as Record<string, unknown>;
        const preparedAt = typeof d.prepared_at === 'string' ? d.prepared_at : null;
        const jobCardIssued = !!d.job_card_issued;
        const crewName = jobCardSummary.crew?.name ?? null;
        const subtitle = [
          preparedAt
            ? `${t('jobs.markedPrepared', 'Marked prepared')} ${new Date(preparedAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`
            : null,
          crewName,
          jobCardIssued ? t('jobs.jobCardIssued', 'job card issued') : null,
        ].filter(Boolean).join(' · ');

        return (
          <div className="border-b border-ui-border px-[18px] py-4">
            <button
              type="button"
              onClick={() => setSchedulePanelVisible(v => !v)}
              className="w-full text-left flex items-center gap-[10px] border border-[#ededed] bg-[#f7f9fd] rounded-[10px] px-[14px] py-3 hover:bg-ui-hover transition-colors dark:border-ui-border dark:bg-ui-subtle"
            >
              <div className="flex-1 min-w-0">
                <p className="text-[14px] font-bold text-ui-text">
                  {t('jobs.readinessConfirmed', 'Readiness confirmed')}
                </p>
                {subtitle && <p className="mt-[3px] text-[11.5px] text-ui-text3 truncate">{subtitle}</p>}
              </div>
              <span className="shrink-0 rounded-[6px] border border-[#D9BBFF] bg-white px-2 py-[3px] text-[11px] font-semibold text-[#3F2D93] dark:bg-ui-card">
                {schedulePanelVisible ? t('common.hide', 'Hide') : t('jobs.view', 'View')}
              </span>
              <span className="shrink-0 flex h-[22px] w-[22px] items-center justify-center rounded-full border border-[#ededed] bg-white text-[11px] text-[#575757] dark:bg-ui-card dark:border-ui-border">
                {schedulePanelVisible ? '▴' : '▾'}
              </span>
            </button>

            {schedulePanelVisible && (
              <div className="flex flex-col gap-3 mt-3">
                {/* JOB CARD section */}
                {/* Only where the workflow attaches the Job Card (job_card_panel) — same rule SAM reads. */}
                {jobCardAttached && (<>
                <div className="border border-[#ededed] rounded-[10px] overflow-hidden dark:border-ui-border">
                  <div className="flex items-center gap-2 bg-[#f7f9fd] px-[14px] py-[10px] border-b border-[#ededed] dark:bg-ui-subtle dark:border-ui-border">
                    <p className="flex-1 text-[10.5px] font-bold tracking-[0.09em] uppercase text-[#767676] dark:text-ui-text3">
                      {t('jobs.jobCard', 'Job card')}
                    </p>
                    <InvoiceJobCardDownloadButton engagementId={engagementId!} />
                  </div>
                  <div className="flex flex-wrap gap-2 px-[14px] py-3 border-b border-[#ededed] dark:border-ui-border">
                    <button type="button" onClick={() => setJobCardProposalModalOpen(true)}
                      className="flex items-center gap-[7px] rounded-[9px] border border-[#ededed] bg-white px-[13px] py-[9px] text-[12.5px] font-semibold text-[#575757] hover:bg-ui-subtle dark:bg-ui-card dark:border-ui-border">
                      ▤ {t('jobs.proposalDetails', 'Proposal details')}
                    </button>
                    <button type="button" onClick={() => setAssessmentModalOpen(true)}
                      className="flex items-center gap-[7px] rounded-[9px] border border-[#ededed] bg-white px-[13px] py-[9px] text-[12.5px] font-semibold text-[#575757] hover:bg-ui-subtle dark:bg-ui-card dark:border-ui-border">
                      ▤ {t('jobs.assessmentDetails', 'Assessment details')}
                    </button>
                    <button type="button" onClick={() => setMaterialListModalOpen(true)}
                      className="flex items-center gap-[7px] rounded-[9px] border border-[#D9BBFF] bg-[#F4EEFF] px-[13px] py-[9px] text-[12.5px] font-semibold text-[#3F2D93] hover:opacity-80">
                      ⧉ {t('jobs.materialList', 'Material list')}
                    </button>
                    <button type="button" onClick={openNotesModal}
                      className="flex items-center gap-[7px] rounded-[9px] border border-[#ededed] bg-white px-[13px] py-[9px] text-[12.5px] font-semibold text-[#575757] hover:bg-ui-subtle dark:bg-ui-card dark:border-ui-border">
                      ✎ {t('jobs.notes', 'Notes')}
                    </button>
                  </div>
                  <div className="grid grid-cols-2">
                    {jobCardSummary.project_manager_name && (
                      <div className="px-[14px] py-[9px] border-b border-r border-[#f2f2f2] dark:border-ui-border">
                        <p className="text-[10.5px] text-[#767676] dark:text-ui-text3">{t('jobs.projectManager', 'Project Manager')}</p>
                        <p className="mt-0.5 text-[12.5px] font-semibold text-ui-text text-pretty">{[jobCardSummary.project_manager_name, jobCardSummary.project_manager_phone].filter(Boolean).join(' · ')}</p>
                      </div>
                    )}
                    {jobCardSummary.pm_assigned_by_name && (
                      <div className="px-[14px] py-[9px] border-b border-[#f2f2f2] dark:border-ui-border">
                        <p className="text-[10.5px] text-[#767676] dark:text-ui-text3">{t('jobs.assignedBy', 'Assigned by')}</p>
                        <p className="mt-0.5 text-[12.5px] font-semibold text-ui-text text-pretty">
                          {jobCardSummary.pm_assigned_by_name}
                          {jobCardSummary.pm_assigned_on ? ` · ${new Date(jobCardSummary.pm_assigned_on).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}` : ''}
                        </p>
                      </div>
                    )}
                    {jobCardSummary.accepted_proposal_number && (
                      <div className="px-[14px] py-[9px] border-b border-r border-[#f2f2f2] dark:border-ui-border">
                        <p className="text-[10.5px] text-[#767676] dark:text-ui-text3">{t('jobs.acceptedProposal', 'Accepted proposal')}</p>
                        <p className="mt-0.5 text-[12.5px] font-semibold text-ui-text text-pretty">
                          {jobCardSummary.accepted_proposal_number}
                          {jobCardSummary.accepted_proposal_date ? ` · accepted ${new Date(jobCardSummary.accepted_proposal_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}` : ''}
                        </p>
                      </div>
                    )}
                    <div className="px-[14px] py-[9px] border-b border-[#f2f2f2] dark:border-ui-border">
                      <p className="text-[10.5px] text-[#767676] dark:text-ui-text3">{t('jobs.contractValue', 'Contract value')}</p>
                      {jobCardSummary.contract_value_restricted
                        ? <p className="mt-0.5 text-[12.5px] text-[#a0a0a0]">Restricted</p>
                        : <p className="mt-0.5 text-[12.5px] font-semibold text-ui-text">{jobCardSummary.contract_value != null ? `$${jobCardSummary.contract_value.toLocaleString()}` : '—'}</p>}
                    </div>
                    {jobCardSummary.schedule?.scheduled_date && (
                      <div className="px-[14px] py-[9px] border-b border-r border-[#f2f2f2] dark:border-ui-border">
                        <p className="text-[10.5px] text-[#767676] dark:text-ui-text3">{t('jobs.scheduledWindow', 'Scheduled window')}</p>
                        <p className="mt-0.5 text-[12.5px] font-semibold text-ui-text text-pretty">
                          {new Date(jobCardSummary.schedule.scheduled_date).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
                          {jobCardSummary.schedule.start_time ? ` · ${jobCardSummary.schedule.start_time}` : ''}
                          {jobCardSummary.schedule.customer_window_note ? `–${jobCardSummary.schedule.customer_window_note}` : ''}
                        </p>
                      </div>
                    )}
                    {jobCardSummary.crew && (
                      <div className="px-[14px] py-[9px] border-b border-[#f2f2f2] dark:border-ui-border">
                        <p className="text-[10.5px] text-[#767676] dark:text-ui-text3">{t('jobs.assignedCrew', 'Assigned crew')}</p>
                        <p className="mt-0.5 text-[12.5px] font-semibold text-ui-text text-pretty">{jobCardSummary.crew.name}</p>
                      </div>
                    )}
                    {jobCardSummary.crew_lead_name && (
                      <div className="px-[14px] py-[9px] border-b border-r border-[#f2f2f2] dark:border-ui-border">
                        <p className="text-[10.5px] text-[#767676] dark:text-ui-text3">{t('jobs.crewLead', 'Crew lead')}</p>
                        <p className="mt-0.5 text-[12.5px] font-semibold text-ui-text text-pretty">{[jobCardSummary.crew_lead_name, jobCardSummary.crew_lead_phone].filter(Boolean).join(' · ')}</p>
                      </div>
                    )}
                    {jobCardSummary.materials_delivery_note && (
                      <div className="px-[14px] py-[9px] border-b border-[#f2f2f2] dark:border-ui-border">
                        <p className="text-[10.5px] text-[#767676] dark:text-ui-text3">{t('jobs.materials', 'Materials')}</p>
                        <p className="mt-0.5 text-[12.5px] font-semibold text-ui-text text-pretty">{jobCardSummary.materials_delivery_note}</p>
                      </div>
                    )}
                  </div>
                </div>
                </>)}

                {/* SITE BOARD — embedded inside the collapsible for job_prepared */}
                {siteBoardAttached && <SiteBoardSection engagementId={engagementId} />}
              </div>
            )}
          </div>
        );
      })()}

      {/* JOB EXECUTION — "Execution updates" collapsible: Job Card + embedded Site Board +
          RECENT UPDATES + CAPTURED cards. Applies to job_execution and job_execution_on_hold;
          job_executed uses the standalone site board so the QA flow has a clean view. */}
      {engagementId && state && engagement && jobCardSummary
        && state.current_status_code === 'job_execution' && (() => {
        const d = (engagement.data ?? {}) as Record<string, unknown>;
        const startedAt = typeof d.execution_started_at === 'string' ? d.execution_started_at : null;
        const photoCount = typeof d.execution_photo_count === 'number' ? d.execution_photo_count : null;
        const updateCount = typeof d.execution_update_count === 'number' ? d.execution_update_count : null;
        const recentUpdatesText = typeof d.execution_recent_updates_text === 'string' ? d.execution_recent_updates_text : null;
        const capturedTags = Array.isArray(d.execution_captured_tags) ? (d.execution_captured_tags as string[]) : null;
        const crewLeadName = jobCardSummary.crew_lead_name ?? null;
        const startedStr = startedAt
          ? new Date(startedAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
          : null;
        const subtitle = [
          startedStr ? `Started ${startedStr}` : null,
          crewLeadName,
          photoCount != null ? `${photoCount} photo${photoCount !== 1 ? 's' : ''}` : null,
          updateCount != null ? `${updateCount} update${updateCount !== 1 ? 's' : ''}` : null,
        ].filter(Boolean).join(' · ');

        return (
          <div className="border-b border-ui-border px-[18px] py-4">
            <button
              type="button"
              onClick={() => setExecutionPanelVisible(v => !v)}
              className="w-full text-left flex items-center gap-[10px] border border-[#ededed] bg-[#f7f9fd] rounded-[10px] px-[14px] py-3 hover:bg-ui-hover transition-colors dark:border-ui-border dark:bg-ui-subtle"
            >
              <div className="flex-1 min-w-0">
                <p className="text-[14px] font-bold text-ui-text">
                  {t('jobs.executionUpdates', 'Execution updates')}
                </p>
                {subtitle && <p className="mt-[3px] text-[11.5px] text-ui-text3 truncate">{subtitle}</p>}
              </div>
              <span className="shrink-0 rounded-[6px] border border-[#D9BBFF] bg-white px-2 py-[3px] text-[11px] font-semibold text-[#3F2D93] dark:bg-ui-card">
                {executionPanelVisible ? t('common.hide', 'Hide') : t('common.show', 'Show')}
              </span>
              <span className="shrink-0 flex h-[22px] w-[22px] items-center justify-center rounded-full border border-[#ededed] bg-white text-[11px] text-[#575757] dark:bg-ui-card dark:border-ui-border">
                {executionPanelVisible ? '▴' : '▾'}
              </span>
            </button>

            {executionPanelVisible && (
              <div className="flex flex-col gap-3 mt-3">
                {/* JOB CARD */}
                {/* Only where the workflow attaches the Job Card (job_card_panel) — same rule SAM reads. */}
                {jobCardAttached && (<>
                <div className="border border-[#ededed] rounded-[10px] overflow-hidden dark:border-ui-border">
                  <div className="flex items-center gap-2 bg-[#f7f9fd] px-[14px] py-[10px] border-b border-[#ededed] dark:bg-ui-subtle dark:border-ui-border">
                    <p className="flex-1 text-[10.5px] font-bold tracking-[0.09em] uppercase text-[#767676] dark:text-ui-text3">
                      {t('jobs.jobCard', 'Job card')}
                    </p>
                    <InvoiceJobCardDownloadButton engagementId={engagementId!} />
                  </div>
                  <div className="flex flex-wrap gap-2 px-[14px] py-3 border-b border-[#ededed] dark:border-ui-border">
                    <button type="button" onClick={() => setJobCardProposalModalOpen(true)}
                      className="flex items-center gap-[7px] rounded-[9px] border border-[#ededed] bg-white px-[13px] py-[9px] text-[12.5px] font-semibold text-[#575757] hover:bg-ui-subtle dark:bg-ui-card dark:border-ui-border">
                      ▤ {t('jobs.proposalDetails', 'Proposal details')}
                    </button>
                    <button type="button" onClick={() => setAssessmentModalOpen(true)}
                      className="flex items-center gap-[7px] rounded-[9px] border border-[#ededed] bg-white px-[13px] py-[9px] text-[12.5px] font-semibold text-[#575757] hover:bg-ui-subtle dark:bg-ui-card dark:border-ui-border">
                      ▤ {t('jobs.assessmentDetails', 'Assessment details')}
                    </button>
                    <button type="button" onClick={() => setMaterialListModalOpen(true)}
                      className="flex items-center gap-[7px] rounded-[9px] border border-[#D9BBFF] bg-[#F4EEFF] px-[13px] py-[9px] text-[12.5px] font-semibold text-[#3F2D93] hover:opacity-80">
                      ⧉ {t('jobs.materialList', 'Material list')}
                    </button>
                    <button type="button" onClick={openNotesModal}
                      className="flex items-center gap-[7px] rounded-[9px] border border-[#ededed] bg-white px-[13px] py-[9px] text-[12.5px] font-semibold text-[#575757] hover:bg-ui-subtle dark:bg-ui-card dark:border-ui-border">
                      ✎ {t('jobs.notes', 'Notes')}{notesForModal.length > 0 ? ` (${notesForModal.length})` : ''}
                    </button>
                  </div>
                  <div className="grid grid-cols-2">
                    {jobCardSummary.project_manager_name && (
                      <div className="px-[14px] py-[9px] border-b border-r border-[#f2f2f2] dark:border-ui-border">
                        <p className="text-[10.5px] text-[#767676] dark:text-ui-text3">{t('jobs.projectManager', 'Project Manager')}</p>
                        <p className="mt-0.5 text-[12.5px] font-semibold text-ui-text text-pretty">{[jobCardSummary.project_manager_name, jobCardSummary.project_manager_phone].filter(Boolean).join(' · ')}</p>
                      </div>
                    )}
                    {jobCardSummary.pm_assigned_by_name && (
                      <div className="px-[14px] py-[9px] border-b border-[#f2f2f2] dark:border-ui-border">
                        <p className="text-[10.5px] text-[#767676] dark:text-ui-text3">{t('jobs.assignedBy', 'Assigned by')}</p>
                        <p className="mt-0.5 text-[12.5px] font-semibold text-ui-text text-pretty">
                          {jobCardSummary.pm_assigned_by_name}
                          {jobCardSummary.pm_assigned_on ? ` · ${new Date(jobCardSummary.pm_assigned_on).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}` : ''}
                        </p>
                      </div>
                    )}
                    {jobCardSummary.accepted_proposal_number && (
                      <div className="px-[14px] py-[9px] border-b border-r border-[#f2f2f2] dark:border-ui-border">
                        <p className="text-[10.5px] text-[#767676] dark:text-ui-text3">{t('jobs.acceptedProposal', 'Accepted proposal')}</p>
                        <p className="mt-0.5 text-[12.5px] font-semibold text-ui-text text-pretty">
                          {jobCardSummary.accepted_proposal_number}
                          {jobCardSummary.accepted_proposal_date ? ` · accepted ${new Date(jobCardSummary.accepted_proposal_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}` : ''}
                        </p>
                      </div>
                    )}
                    <div className="px-[14px] py-[9px] border-b border-[#f2f2f2] dark:border-ui-border">
                      <p className="text-[10.5px] text-[#767676] dark:text-ui-text3">{t('jobs.contractValue', 'Contract value')}</p>
                      {jobCardSummary.contract_value_restricted
                        ? <p className="mt-0.5 text-[12.5px] text-[#a0a0a0]">Restricted</p>
                        : <p className="mt-0.5 text-[12.5px] font-semibold text-ui-text">{jobCardSummary.contract_value != null ? `$${jobCardSummary.contract_value.toLocaleString()}` : '—'}</p>}
                    </div>
                    {jobCardSummary.schedule?.scheduled_date && (
                      <div className="px-[14px] py-[9px] border-b border-r border-[#f2f2f2] dark:border-ui-border">
                        <p className="text-[10.5px] text-[#767676] dark:text-ui-text3">{t('jobs.scheduledWindow', 'Scheduled window')}</p>
                        <p className="mt-0.5 text-[12.5px] font-semibold text-ui-text text-pretty">
                          {new Date(jobCardSummary.schedule.scheduled_date).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
                          {jobCardSummary.schedule.start_time ? ` · ${jobCardSummary.schedule.start_time}` : ''}
                          {jobCardSummary.schedule.customer_window_note ? `–${jobCardSummary.schedule.customer_window_note}` : ''}
                        </p>
                      </div>
                    )}
                    {jobCardSummary.crew && (
                      <div className="px-[14px] py-[9px] border-b border-[#f2f2f2] dark:border-ui-border">
                        <p className="text-[10.5px] text-[#767676] dark:text-ui-text3">{t('jobs.assignedCrew', 'Assigned crew')}</p>
                        <p className="mt-0.5 text-[12.5px] font-semibold text-ui-text text-pretty">{jobCardSummary.crew.name}</p>
                      </div>
                    )}
                    {jobCardSummary.crew_lead_name && (
                      <div className="px-[14px] py-[9px] border-b border-r border-[#f2f2f2] dark:border-ui-border">
                        <p className="text-[10.5px] text-[#767676] dark:text-ui-text3">{t('jobs.crewLead', 'Crew lead')}</p>
                        <p className="mt-0.5 text-[12.5px] font-semibold text-ui-text text-pretty">{[jobCardSummary.crew_lead_name, jobCardSummary.crew_lead_phone].filter(Boolean).join(' · ')}</p>
                      </div>
                    )}
                    {jobCardSummary.materials_delivery_note && (
                      <div className="px-[14px] py-[9px] border-b border-[#f2f2f2] dark:border-ui-border">
                        <p className="text-[10.5px] text-[#767676] dark:text-ui-text3">{t('jobs.materials', 'Materials')}</p>
                        <p className="mt-0.5 text-[12.5px] font-semibold text-ui-text text-pretty">{jobCardSummary.materials_delivery_note}</p>
                      </div>
                    )}
                    {startedStr && (
                      <div className="px-[14px] py-[9px] border-b border-r border-[#f2f2f2] dark:border-ui-border">
                        <p className="text-[10.5px] text-[#767676] dark:text-ui-text3">{t('jobs.execution', 'Execution')}</p>
                        <p className="mt-0.5 text-[12.5px] font-semibold text-ui-text text-pretty">
                          {`Started ${startedStr} · in progress`}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                </>)}

                {/* SITE BOARD embedded */}
                {siteBoardAttached && <SiteBoardSection engagementId={engagementId} />}
              </div>
            )}

            {/* RECENT UPDATES — SAM-authored summary of site board activity; always visible */}
            {recentUpdatesText && (
              <div className="mt-3 border border-[#D9BBFF] bg-[#F4EEFF] rounded-[10px] px-[14px] py-3 dark:border-brand-light/40 dark:bg-brand-dark/10">
                <p className="text-[10.5px] font-bold tracking-[0.08em] uppercase text-[#4E38B5] dark:text-brand-light">
                  {t('jobs.recentUpdates', 'Recent updates')}
                </p>
                <p className="mt-1 text-[12.5px] text-[#3F2D93] leading-relaxed dark:text-brand-bright">{recentUpdatesText}</p>
              </div>
            )}

            {/* CAPTURED — counts of field evidence captured during execution */}
            {capturedTags && capturedTags.length > 0 && (
              <div className="mt-3 border border-[#ededed] rounded-[10px] px-[14px] py-3 dark:border-ui-border">
                <p className="text-[10.5px] font-bold tracking-[0.08em] uppercase text-[#767676] dark:text-ui-text3">
                  {t('jobs.captured', 'Captured')}
                </p>
                <div className="flex flex-wrap gap-[6px] mt-[9px]">
                  {capturedTags.map((tag, idx) => (
                    <span key={idx} className="bg-[#f2f2f2] text-[#575757] rounded-[6px] px-[9px] py-[3px] text-[11.5px] dark:bg-ui-subtle dark:text-ui-text2">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* JOB EXECUTED — "Execution summary" collapsible (collapsed by default): Job Card + Site Board
          + ACTUALS + COMPLETION EVIDENCE + QA INSPECTION cards outside the collapsible. */}
      {engagementId && state && engagement && jobCardSummary
        && state.current_status_code === 'job_executed' && (() => {
        const d = (engagement.data ?? {}) as Record<string, unknown>;
        const startedAt = typeof d.execution_started_at === 'string' ? d.execution_started_at : null;
        const completedAt = typeof d.execution_completed_at === 'string' ? d.execution_completed_at : null;
        const holdCount = typeof d.execution_hold_count === 'number' ? d.execution_hold_count : null;
        const photoCount = typeof d.execution_photo_count === 'number' ? d.execution_photo_count : null;
        const holdSummary = typeof d.execution_hold_summary === 'string' ? d.execution_hold_summary : null;
        const completionEvidenceTags = Array.isArray(d.execution_completion_evidence_tags) ? (d.execution_completion_evidence_tags as string[]) : null;
        const qaInspectionText = typeof d.execution_qa_inspection_text === 'string'
          ? d.execution_qa_inspection_text
          : t('jobs.qaInspectionDefaultText', 'Pending. Checklist runs final workmanship, cleanup verification, completion photos, the customer walkthrough, outstanding issues and completion documentation. Checklist items are created as tasks.');

        const fmtDt = (iso: string) => new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
        const startedStr = startedAt ? fmtDt(startedAt) : null;
        const completedStr = completedAt ? fmtDt(completedAt) : null;

        const subtitleParts = [
          startedStr && completedStr ? `${startedStr} → ${completedStr}` : startedStr,
          holdCount != null ? `${holdCount} hold${holdCount !== 1 ? 's' : ''}` : null,
          photoCount != null ? `${photoCount} photo${photoCount !== 1 ? 's' : ''}` : null,
        ].filter(Boolean);
        const subtitle = subtitleParts.join(' · ');

        return (
          <div className="border-b border-ui-border px-[18px] py-4">
            <button
              type="button"
              onClick={() => setExecutedSummaryPanelVisible(v => !v)}
              className="w-full text-left flex items-center gap-[10px] border border-[#ededed] bg-[#f7f9fd] rounded-[10px] px-[14px] py-3 hover:bg-ui-hover transition-colors dark:border-ui-border dark:bg-ui-subtle"
            >
              <div className="flex-1 min-w-0">
                <p className="text-[14px] font-bold text-ui-text">
                  {t('jobs.executionSummaryTitle', 'Execution summary')}
                </p>
                {subtitle && <p className="mt-[3px] text-[11.5px] text-ui-text3 truncate">{subtitle}</p>}
              </div>
              <span className="shrink-0 rounded-[6px] border border-[#D9BBFF] bg-white px-2 py-[3px] text-[11px] font-semibold text-[#3F2D93] dark:bg-ui-card">
                {executedSummaryPanelVisible ? t('common.hide', 'Hide') : t('jobs.view', 'View')}
              </span>
              <span className="shrink-0 flex h-[22px] w-[22px] items-center justify-center rounded-full border border-[#ededed] bg-white text-[11px] text-[#575757] dark:bg-ui-card dark:border-ui-border">
                {executedSummaryPanelVisible ? '▴' : '▾'}
              </span>
            </button>

            {executedSummaryPanelVisible && (
              <div className="flex flex-col gap-3 mt-3">
                {/* JOB CARD */}
                {/* Only where the workflow attaches the Job Card (job_card_panel) — same rule SAM reads. */}
                {jobCardAttached && (<>
                <div className="border border-[#ededed] rounded-[10px] overflow-hidden dark:border-ui-border">
                  <div className="flex items-center gap-2 bg-[#f7f9fd] px-[14px] py-[10px] border-b border-[#ededed] dark:bg-ui-subtle dark:border-ui-border">
                    <p className="flex-1 text-[10.5px] font-bold tracking-[0.09em] uppercase text-[#767676] dark:text-ui-text3">
                      {t('jobs.jobCard', 'Job card')}
                    </p>
                    <InvoiceJobCardDownloadButton engagementId={engagementId!} />
                  </div>
                  <div className="flex flex-wrap gap-2 px-[14px] py-3 border-b border-[#ededed] dark:border-ui-border">
                    <button type="button" onClick={() => setJobCardProposalModalOpen(true)}
                      className="flex items-center gap-[7px] rounded-[9px] border border-[#ededed] bg-white px-[13px] py-[9px] text-[12.5px] font-semibold text-[#575757] hover:bg-ui-subtle dark:bg-ui-card dark:border-ui-border">
                      ▤ {t('jobs.proposalDetails', 'Proposal details')}
                    </button>
                    <button type="button" onClick={() => setAssessmentModalOpen(true)}
                      className="flex items-center gap-[7px] rounded-[9px] border border-[#ededed] bg-white px-[13px] py-[9px] text-[12.5px] font-semibold text-[#575757] hover:bg-ui-subtle dark:bg-ui-card dark:border-ui-border">
                      ▤ {t('jobs.assessmentDetails', 'Assessment details')}
                    </button>
                    <button type="button" onClick={() => setMaterialListModalOpen(true)}
                      className="flex items-center gap-[7px] rounded-[9px] border border-[#D9BBFF] bg-[#F4EEFF] px-[13px] py-[9px] text-[12.5px] font-semibold text-[#3F2D93] hover:opacity-80">
                      ⧉ {t('jobs.materialList', 'Material list')}
                    </button>
                    <button type="button" onClick={openNotesModal}
                      className="flex items-center gap-[7px] rounded-[9px] border border-[#ededed] bg-white px-[13px] py-[9px] text-[12.5px] font-semibold text-[#575757] hover:bg-ui-subtle dark:bg-ui-card dark:border-ui-border">
                      ✎ {t('jobs.notes', 'Notes')}{notesForModal.length > 0 ? ` (${notesForModal.length})` : ''}
                    </button>
                  </div>
                  <div className="grid grid-cols-2">
                    {jobCardSummary.project_manager_name && (
                      <div className="px-[14px] py-[9px] border-b border-r border-[#f2f2f2] dark:border-ui-border">
                        <p className="text-[10.5px] text-[#767676] dark:text-ui-text3">{t('jobs.projectManager', 'Project Manager')}</p>
                        <p className="mt-0.5 text-[12.5px] font-semibold text-ui-text text-pretty">{[jobCardSummary.project_manager_name, jobCardSummary.project_manager_phone].filter(Boolean).join(' · ')}</p>
                      </div>
                    )}
                    {jobCardSummary.pm_assigned_by_name && (
                      <div className="px-[14px] py-[9px] border-b border-[#f2f2f2] dark:border-ui-border">
                        <p className="text-[10.5px] text-[#767676] dark:text-ui-text3">{t('jobs.assignedBy', 'Assigned by')}</p>
                        <p className="mt-0.5 text-[12.5px] font-semibold text-ui-text text-pretty">
                          {jobCardSummary.pm_assigned_by_name}
                          {jobCardSummary.pm_assigned_on ? ` · ${new Date(jobCardSummary.pm_assigned_on).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}` : ''}
                        </p>
                      </div>
                    )}
                    {jobCardSummary.accepted_proposal_number && (
                      <div className="px-[14px] py-[9px] border-b border-r border-[#f2f2f2] dark:border-ui-border">
                        <p className="text-[10.5px] text-[#767676] dark:text-ui-text3">{t('jobs.acceptedProposal', 'Accepted proposal')}</p>
                        <p className="mt-0.5 text-[12.5px] font-semibold text-ui-text text-pretty">
                          {jobCardSummary.accepted_proposal_number}
                          {jobCardSummary.accepted_proposal_date ? ` · accepted ${new Date(jobCardSummary.accepted_proposal_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}` : ''}
                        </p>
                      </div>
                    )}
                    <div className="px-[14px] py-[9px] border-b border-[#f2f2f2] dark:border-ui-border">
                      <p className="text-[10.5px] text-[#767676] dark:text-ui-text3">{t('jobs.contractValue', 'Contract value')}</p>
                      {jobCardSummary.contract_value_restricted
                        ? <p className="mt-0.5 text-[12.5px] text-[#a0a0a0]">Restricted</p>
                        : <p className="mt-0.5 text-[12.5px] font-semibold text-ui-text">{jobCardSummary.contract_value != null ? `$${jobCardSummary.contract_value.toLocaleString()}` : '—'}</p>}
                    </div>
                    {jobCardSummary.schedule?.scheduled_date && (
                      <div className="px-[14px] py-[9px] border-b border-r border-[#f2f2f2] dark:border-ui-border">
                        <p className="text-[10.5px] text-[#767676] dark:text-ui-text3">{t('jobs.scheduledWindow', 'Scheduled window')}</p>
                        <p className="mt-0.5 text-[12.5px] font-semibold text-ui-text text-pretty">
                          {new Date(jobCardSummary.schedule.scheduled_date).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
                          {jobCardSummary.schedule.start_time ? ` · ${jobCardSummary.schedule.start_time}` : ''}
                          {jobCardSummary.schedule.customer_window_note ? `–${jobCardSummary.schedule.customer_window_note}` : ''}
                        </p>
                      </div>
                    )}
                    {jobCardSummary.crew && (
                      <div className="px-[14px] py-[9px] border-b border-[#f2f2f2] dark:border-ui-border">
                        <p className="text-[10.5px] text-[#767676] dark:text-ui-text3">{t('jobs.assignedCrew', 'Assigned crew')}</p>
                        <p className="mt-0.5 text-[12.5px] font-semibold text-ui-text text-pretty">{jobCardSummary.crew.name}</p>
                      </div>
                    )}
                    {jobCardSummary.crew_lead_name && (
                      <div className="px-[14px] py-[9px] border-b border-r border-[#f2f2f2] dark:border-ui-border">
                        <p className="text-[10.5px] text-[#767676] dark:text-ui-text3">{t('jobs.crewLead', 'Crew lead')}</p>
                        <p className="mt-0.5 text-[12.5px] font-semibold text-ui-text text-pretty">{[jobCardSummary.crew_lead_name, jobCardSummary.crew_lead_phone].filter(Boolean).join(' · ')}</p>
                      </div>
                    )}
                    {jobCardSummary.materials_delivery_note && (
                      <div className="px-[14px] py-[9px] border-b border-[#f2f2f2] dark:border-ui-border">
                        <p className="text-[10.5px] text-[#767676] dark:text-ui-text3">{t('jobs.materials', 'Materials')}</p>
                        <p className="mt-0.5 text-[12.5px] font-semibold text-ui-text text-pretty">{jobCardSummary.materials_delivery_note}</p>
                      </div>
                    )}
                    {startedStr && (
                      <div className="px-[14px] py-[9px] border-b border-r border-[#f2f2f2] dark:border-ui-border">
                        <p className="text-[10.5px] text-[#767676] dark:text-ui-text3">{t('jobs.execution', 'Execution')}</p>
                        <p className="mt-0.5 text-[12.5px] font-semibold text-ui-text text-pretty">{`Started ${startedStr}`}</p>
                      </div>
                    )}
                    {completedStr && (
                      <div className="px-[14px] py-[9px] border-b border-[#f2f2f2] dark:border-ui-border">
                        <p className="text-[10.5px] text-[#767676] dark:text-ui-text3">{t('jobs.fieldWork', 'Field work')}</p>
                        <p className="mt-0.5 text-[12.5px] font-semibold text-ui-text text-pretty">{`Completed ${completedStr}`}</p>
                      </div>
                    )}
                  </div>
                </div>
                </>)}

                {/* SITE BOARD embedded */}
                {siteBoardAttached && <SiteBoardSection engagementId={engagementId} />}

                {/* ACTUALS */}
                <div className="border border-[#ededed] rounded-[10px] px-[14px] py-3 dark:border-ui-border">
                  <p className="text-[10.5px] font-bold tracking-[0.08em] uppercase text-[#767676] dark:text-ui-text3">
                    {t('jobs.actuals', 'Actuals')}
                  </p>
                  <div className="grid grid-cols-2 gap-x-[14px] gap-y-[9px] mt-[9px]">
                    {startedStr && (
                      <div>
                        <p className="text-[11px] text-[#767676] dark:text-ui-text3">{t('jobs.executionStart', 'Execution start')}</p>
                        <p className="mt-0.5 text-[12.5px] font-semibold text-ui-text">{startedStr}</p>
                      </div>
                    )}
                    {completedStr && (
                      <div>
                        <p className="text-[11px] text-[#767676] dark:text-ui-text3">{t('jobs.executionComplete', 'Execution complete')}</p>
                        <p className="mt-0.5 text-[12.5px] font-semibold text-ui-text">{completedStr}</p>
                      </div>
                    )}
                    {(jobCardSummary.crew || jobCardSummary.crew_lead_name) && (
                      <div>
                        <p className="text-[11px] text-[#767676] dark:text-ui-text3">{t('jobs.crew', 'Crew')}</p>
                        <p className="mt-0.5 text-[12.5px] font-semibold text-ui-text">
                          {[jobCardSummary.crew?.name, jobCardSummary.crew_lead_name].filter(Boolean).join(' · ')}
                        </p>
                      </div>
                    )}
                    {holdSummary && (
                      <div>
                        <p className="text-[11px] text-[#767676] dark:text-ui-text3">{t('jobs.holdHistory', 'Hold history')}</p>
                        <p className="mt-0.5 text-[12.5px] font-semibold text-ui-text">{holdSummary}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* COMPLETION EVIDENCE — tag chips */}
                {completionEvidenceTags && completionEvidenceTags.length > 0 && (
                  <div className="border border-[#ededed] rounded-[10px] px-[14px] py-3 dark:border-ui-border">
                    <p className="text-[10.5px] font-bold tracking-[0.08em] uppercase text-[#767676] dark:text-ui-text3">
                      {t('jobs.completionEvidence', 'Completion evidence')}
                    </p>
                    <div className="flex flex-wrap gap-[6px] mt-[9px]">
                      {completionEvidenceTags.map((tag, idx) => (
                        <span key={idx} className="bg-[#f2f2f2] text-[#575757] rounded-[6px] px-[9px] py-[3px] text-[11.5px] dark:bg-ui-subtle dark:text-ui-text2">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* QA INSPECTION — purple card */}
                <div className="border border-[#D9BBFF] bg-[#F4EEFF] rounded-[10px] px-[14px] py-3 dark:border-brand-light/40 dark:bg-brand-dark/10">
                  <p className="text-[10.5px] font-bold tracking-[0.08em] uppercase text-[#4E38B5] dark:text-brand-light">
                    {t('jobs.qaInspection', 'QA Inspection')}
                  </p>
                  <p className="mt-1 text-[12.5px] text-[#3F2D93] leading-relaxed dark:text-brand-bright">{qaInspectionText}</p>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* QA INSPECTED PANEL — collapsible "QA summary" with JOB CARD + SITE BOARD + QA details + notes. */}
      {engagementId && state && engagement && jobCardSummary
        && state.current_status_code === 'qa_inspected' && (() => {
        const d = (engagement.data ?? {}) as Record<string, unknown>;

        // SAM awareness fields
        const samMsg = typeof d.qa_sam_message === 'string' ? d.qa_sam_message : t('jobs.qaSamDefaultMsg', 'QA has passed. The job is operationally closed and ready to bill.');
        const samTags = Array.isArray(d.qa_sam_tags) ? (d.qa_sam_tags as string[]) : null;

        // QA summary data (from live API, not engagement.data)
        const qaResult = qaSummaryData?.qa_result;
        const inspectedBy = qaSummaryData?.inspected_by_name ?? null;
        const inspectedAt = qaSummaryData?.inspected_at ?? null;
        const qaNotes = qaSummaryData?.reason ?? null;

        // Warranty info from engagement.data (SAM-populated)
        const warrantyText = typeof d.qa_warranty_text === 'string' ? d.qa_warranty_text : null;

        // Format dates for the collapsible subtitle
        const fmtShort = (iso: string | null) => iso
          ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ', '
            + new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
          : null;

        const inspectedStr = fmtShort(inspectedAt);
        const subtitleParts = [
          qaResult === 'pass' ? t('jobs.qaPassed', 'Passed') : qaResult === 'fail' ? t('jobs.qaFailed', 'Failed') : null,
          inspectedStr ? `inspected ${inspectedStr}` : null,
          inspectedBy,
        ].filter(Boolean);

        return (
          <div className="border-b border-ui-border px-[18px] py-4">
            {/* SAM bubble */}
            <div className="flex gap-[11px] items-start border border-[#D9BBFF] bg-[#F4EEFF] rounded-[10px] p-[12px_13px]">
              <span className="w-[26px] h-[26px] shrink-0 rounded-full bg-[#4E38B5] text-white text-[11px] font-bold flex items-center justify-center">
                S
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-[10.5px] font-bold tracking-[0.08em] text-[#4E38B5]">SAM</p>
                <p className="mt-1 text-[13px] text-[#3F2D93] leading-relaxed">{samMsg}</p>
                {samTags && samTags.length > 0 && (
                  <div className="flex flex-wrap gap-[6px] mt-[9px]">
                    {samTags.map((tag, idx) => (
                      <span key={idx} className="bg-white text-[#575757] rounded-[6px] px-[9px] py-[3px] text-[11.5px] font-semibold">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* QA summary collapsible */}
            <div className="mt-4">
              <button
                type="button"
                onClick={() => setQaSummaryPanelVisible(v => !v)}
                className="w-full text-left flex items-center gap-[10px] border border-[#ededed] bg-[#f7f9fd] rounded-[10px] px-[14px] py-3 hover:bg-ui-hover transition-colors dark:border-ui-border dark:bg-ui-subtle"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-[14px] font-bold text-ui-text">{t('jobs.qaSummaryTitle', 'QA summary')}</p>
                  {subtitleParts.length > 0 && (
                    <p className="mt-[3px] text-[11.5px] text-ui-text3 truncate">{subtitleParts.join(' · ')}</p>
                  )}
                </div>
                <span className="shrink-0 rounded-[6px] border border-[#D9BBFF] bg-white px-2 py-[3px] text-[11px] font-semibold text-[#3F2D93] dark:bg-ui-card">
                  {qaSummaryPanelVisible ? t('common.hide', 'Hide') : t('jobs.view', 'View')}
                </span>
                <span className="shrink-0 flex h-[22px] w-[22px] items-center justify-center rounded-full border border-[#ededed] bg-white text-[11px] text-[#575757] dark:bg-ui-card dark:border-ui-border">
                  {qaSummaryPanelVisible ? '▴' : '▾'}
                </span>
              </button>

              {qaSummaryPanelVisible && (
                <div className="flex flex-col gap-3 mt-3">
                  {/* JOB CARD */}
                  {/* Only where the workflow attaches the Job Card (job_card_panel) — same rule SAM reads. */}
                  {jobCardAttached && (<>
                  <div className="border border-[#ededed] rounded-[10px] overflow-hidden dark:border-ui-border">
                    <div className="flex items-center gap-2 bg-[#f7f9fd] px-[14px] py-[10px] border-b border-[#ededed] dark:bg-ui-subtle dark:border-ui-border">
                      <div className="flex-1 min-w-0">
                        <p className="text-[10.5px] font-bold tracking-[0.09em] uppercase text-[#767676] dark:text-ui-text3">
                          {t('jobs.jobCard', 'Job Card')}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setMaterialListModalOpen(true)}
                        className="flex items-center gap-[7px] rounded-[9px] border border-[#D9BBFF] bg-[#F4EEFF] px-[13px] py-[9px] text-[12.5px] font-semibold text-[#3F2D93] hover:opacity-80">
                        ⧉ {t('jobs.materialList', 'Material list')}
                      </button>
                    </div>
                    <JobCardDisplayCard
                      engagementId={engagementId}
                      onOpenProposal={() => { setJobCardProposalModalOpen(true); }}
                      onOpenAssessment={() => { setAssessmentOpenSignal((v) => v + 1); setAssessmentModalOpen(true); }}
                      onOpenNotes={openNotesModal}
                    />
                  </div>
                  </>)}

                  {/* SITE BOARD */}
                  {siteBoardAttached && <SiteBoardSection engagementId={engagementId} />}

                  {/* QA grid card */}
                  <div className="border border-[#ededed] rounded-[10px] px-[14px] py-3 dark:border-ui-border">
                    <p className="text-[10.5px] font-bold tracking-[0.08em] uppercase text-[#767676] dark:text-ui-text3">
                      {t('jobs.qa', 'QA')}
                    </p>
                    <div className="grid grid-cols-2 gap-x-[14px] gap-y-[9px] mt-[9px]">
                      <div>
                        <p className="text-[11px] text-[#767676] dark:text-ui-text3">{t('jobs.result', 'Result')}</p>
                        <p className="mt-0.5 text-[12.5px] font-semibold text-ui-text">
                          {qaResult === 'pass' ? t('jobs.qaPassed', 'Passed') : qaResult === 'fail' ? t('jobs.qaFailed', 'Failed') : '—'}
                        </p>
                      </div>
                      {inspectedBy && (
                        <div>
                          <p className="text-[11px] text-[#767676] dark:text-ui-text3">{t('jobs.inspectedBy', 'Inspected by')}</p>
                          <p className="mt-0.5 text-[12.5px] font-semibold text-ui-text">{inspectedBy}</p>
                        </div>
                      )}
                      {inspectedStr && (
                        <div>
                          <p className="text-[11px] text-[#767676] dark:text-ui-text3">{t('jobs.inspectionDate', 'Inspection date')}</p>
                          <p className="mt-0.5 text-[12.5px] font-semibold text-ui-text">{inspectedStr}</p>
                        </div>
                      )}
                      {warrantyText && (
                        <div>
                          <p className="text-[11px] text-[#767676] dark:text-ui-text3">{t('jobs.warranty', 'Warranty')}</p>
                          <p className="mt-0.5 text-[12.5px] font-semibold text-ui-text">{warrantyText}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* QA NOTES — green card, only when notes exist */}
                  {qaNotes && (
                    <div className="border border-[#B7E0C8] bg-[#EAF7EF] rounded-[10px] px-[14px] py-3 dark:border-green-700/40 dark:bg-green-900/10">
                      <p className="text-[10.5px] font-bold tracking-[0.08em] uppercase text-[#146B45] dark:text-green-400">
                        {t('jobs.qaNotes', 'QA Notes')}
                      </p>
                      <p className="mt-1 text-[12.5px] text-[#146B45] leading-relaxed dark:text-green-300">{qaNotes}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* JOB COMPLETED PANEL — shown at job_completed (after QA passed + complete_job fired).
          Green SAM bubble + collapsible "Job summary" (Job Card + Site Board + QA details). */}
      {engagementId && state && engagement && jobCardSummary
        && state.current_status_code === 'job_completed' && (() => {
        const d = (engagement.data ?? {}) as Record<string, unknown>;

        const samMsg = typeof d.job_completed_sam_message === 'string'
          ? d.job_completed_sam_message
          : t('jobs.jobCompletedSamDefaultMsg', 'Job is complete — QA has passed. Ready to prepare the invoice.');
        const samTags = Array.isArray(d.job_completed_sam_tags) ? (d.job_completed_sam_tags as string[]) : null;

        const completedAt = typeof d.job_completed_at === 'string' ? d.job_completed_at : null;
        const completedBy = typeof d.job_completed_by === 'string' ? d.job_completed_by : null;

        const qaResult = qaSummaryData?.qa_result;
        const inspectedBy = qaSummaryData?.inspected_by_name ?? null;
        const inspectedAt = qaSummaryData?.inspected_at ?? null;
        const qaNotes = qaSummaryData?.reason ?? null;
        const warrantyText = typeof d.qa_warranty_text === 'string' ? d.qa_warranty_text : null;

        const fmtShort = (iso: string | null) => iso
          ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ', '
            + new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
          : null;
        const completedStr = fmtShort(completedAt);
        const inspectedStr = fmtShort(inspectedAt);

        const subtitleParts = [
          completedStr ? `completed ${completedStr}` : null,
          completedBy,
        ].filter(Boolean);

        return (
          <div className="border-b border-ui-border px-[18px] py-4">
            {/* SAM bubble — green for successful completion */}
            <div className="flex gap-[11px] items-start border border-[#B7E0C8] bg-[#EAF7EF] rounded-[10px] p-[12px_13px]">
              <span className="w-[26px] h-[26px] shrink-0 rounded-full bg-[#146B45] text-white text-[11px] font-bold flex items-center justify-center">
                S
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-[10.5px] font-bold tracking-[0.08em] text-[#146B45]">SAM</p>
                <p className="mt-1 text-[13px] text-[#146B45] leading-relaxed">{samMsg}</p>
                {samTags && samTags.length > 0 && (
                  <div className="flex flex-wrap gap-[6px] mt-[9px]">
                    {samTags.map((tag, idx) => (
                      <span key={idx} className="bg-white text-[#575757] rounded-[6px] px-[9px] py-[3px] text-[11.5px] font-semibold">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Job summary collapsible */}
            <div className="mt-4">
              <button
                type="button"
                onClick={() => setJobCompletedPanelVisible(v => !v)}
                className="w-full text-left flex items-center gap-[10px] border border-[#ededed] bg-[#f7f9fd] rounded-[10px] px-[14px] py-3 hover:bg-ui-hover transition-colors dark:border-ui-border dark:bg-ui-subtle"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-[14px] font-bold text-ui-text">{t('jobs.jobSummaryTitle', 'Job summary')}</p>
                  {subtitleParts.length > 0 && (
                    <p className="mt-[3px] text-[11.5px] text-ui-text3 truncate">{subtitleParts.join(' · ')}</p>
                  )}
                </div>
                <span className="shrink-0 rounded-[6px] border border-[#B7E0C8] bg-white px-2 py-[3px] text-[11px] font-semibold text-[#146B45] dark:bg-ui-card">
                  {jobCompletedPanelVisible ? t('common.hide', 'Hide') : t('jobs.view', 'View')}
                </span>
                <span className="shrink-0 flex h-[22px] w-[22px] items-center justify-center rounded-full border border-[#ededed] bg-white text-[11px] text-[#575757] dark:bg-ui-card dark:border-ui-border">
                  {jobCompletedPanelVisible ? '▴' : '▾'}
                </span>
              </button>

              {jobCompletedPanelVisible && (
                <div className="flex flex-col gap-3 mt-3">
                  {/* JOB CARD */}
                  {/* Only where the workflow attaches the Job Card (job_card_panel) — same rule SAM reads. */}
                  {jobCardAttached && (<>
                  <div className="border border-[#ededed] rounded-[10px] overflow-hidden dark:border-ui-border">
                    <div className="flex items-center gap-2 bg-[#f7f9fd] px-[14px] py-[10px] border-b border-[#ededed] dark:bg-ui-subtle dark:border-ui-border">
                      <div className="flex-1 min-w-0">
                        <p className="text-[10.5px] font-bold tracking-[0.09em] uppercase text-[#767676] dark:text-ui-text3">
                          {t('jobs.jobCard', 'Job Card')}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setMaterialListModalOpen(true)}
                        className="flex items-center gap-[7px] rounded-[9px] border border-[#D9BBFF] bg-[#F4EEFF] px-[13px] py-[9px] text-[12.5px] font-semibold text-[#3F2D93] hover:opacity-80">
                        ⧉ {t('jobs.materialList', 'Material list')}
                      </button>
                    </div>
                    <JobCardDisplayCard
                      engagementId={engagementId}
                      onOpenProposal={() => { setJobCardProposalModalOpen(true); }}
                      onOpenAssessment={() => { setAssessmentOpenSignal((v) => v + 1); setAssessmentModalOpen(true); }}
                      onOpenNotes={openNotesModal}
                    />
                  </div>
                  </>)}

                  {/* SITE BOARD */}
                  {siteBoardAttached && <SiteBoardSection engagementId={engagementId} />}

                  {/* QA grid card */}
                  <div className="border border-[#ededed] rounded-[10px] px-[14px] py-3 dark:border-ui-border">
                    <p className="text-[10.5px] font-bold tracking-[0.08em] uppercase text-[#767676] dark:text-ui-text3">
                      {t('jobs.qa', 'QA')}
                    </p>
                    <div className="grid grid-cols-2 gap-x-[14px] gap-y-[9px] mt-[9px]">
                      <div>
                        <p className="text-[11px] text-[#767676] dark:text-ui-text3">{t('jobs.result', 'Result')}</p>
                        <p className="mt-0.5 text-[12.5px] font-semibold text-ui-text">
                          {qaResult === 'pass' ? t('jobs.qaPassed', 'Passed') : qaResult === 'fail' ? t('jobs.qaFailed', 'Failed') : '—'}
                        </p>
                      </div>
                      {inspectedBy && (
                        <div>
                          <p className="text-[11px] text-[#767676] dark:text-ui-text3">{t('jobs.inspectedBy', 'Inspected by')}</p>
                          <p className="mt-0.5 text-[12.5px] font-semibold text-ui-text">{inspectedBy}</p>
                        </div>
                      )}
                      {inspectedStr && (
                        <div>
                          <p className="text-[11px] text-[#767676] dark:text-ui-text3">{t('jobs.inspectionDate', 'Inspection date')}</p>
                          <p className="mt-0.5 text-[12.5px] font-semibold text-ui-text">{inspectedStr}</p>
                        </div>
                      )}
                      {warrantyText && (
                        <div>
                          <p className="text-[11px] text-[#767676] dark:text-ui-text3">{t('jobs.warranty', 'Warranty')}</p>
                          <p className="mt-0.5 text-[12.5px] font-semibold text-ui-text">{warrantyText}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* QA NOTES — green card */}
                  {qaNotes && (
                    <div className="border border-[#B7E0C8] bg-[#EAF7EF] rounded-[10px] px-[14px] py-3 dark:border-green-700/40 dark:bg-green-900/10">
                      <p className="text-[10.5px] font-bold tracking-[0.08em] uppercase text-[#146B45] dark:text-green-400">
                        {t('jobs.qaNotes', 'QA Notes')}
                      </p>
                      <p className="mt-1 text-[12.5px] text-[#146B45] leading-relaxed dark:text-green-300">{qaNotes}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* BILLING SETTLEMENT PANEL — shown wherever the workflow attaches BILLING_MODULE (definition
          module_displays), never by a status list. All figures are real: billable = estimate
          contract value, the rest from invoices. */}
      {engagementId && state && engagement && billingAttached && (
        <BillingSettlementPanel
          engagementId={engagementId}
          invoices={invoices}
          billableAmount={!jobCardSummary?.contract_value_restricted ? jobCardSummary?.contract_value : null}
          isSettled={invoicesSettled(invoices, !jobCardSummary?.contract_value_restricted ? jobCardSummary?.contract_value : null)}
          closedEarly={!!engagement?.closed_early}
          amountsRestricted={amountsRestricted}
          canPrepare={canPrepareInvoice}
          permsLoaded={invoicePermsLoaded}
          onCreateInvoice={() => setInvoiceCreateOpen(true)}
        />
      )}

      {/* PrepareInvoiceModal — mounted when invoiceCreateOpen */}
      {invoiceCreateOpen && engagementId && (
        <PrepareInvoiceModal
          engagementId={engagementId}
          billableAmount={!jobCardSummary?.contract_value_restricted ? (jobCardSummary?.contract_value ?? null) : null}
          previouslyInvoiced={invoices.filter(isActiveInvoice).reduce((s: number, inv: Invoice) => s + inv.amount, 0)}
          onClose={() => setInvoiceCreateOpen(false)}
          onCreated={() => setInvoiceCreateOpen(false)}
        />
      )}

      <div className="space-y-2">
        {/* tasks — shown first, right below the hold/close-early section. The "Tasks in progress"
            pulse for tasks the viewer doesn't own lives up in the status-badge row instead — this
            section only renders at all when there's something it can actually show: either the
            engagement has zero tasks (empty state) or the viewer owns/is assigned to at least one. */}
        {engagementId && tasks !== undefined && (tasks.length === 0 || myTasks.length > 0) && (
          <div className="border-b border-ui-border pb-2">
            <h3 className="px-3 py-1.5 text-xs font-semibold text-ui-text">{t('workflow.tasksTitle', 'Tasks')}</h3>
            {tasks.length === 0 ? (
              <p className="mx-3 text-xs text-ui-text3 px-3 py-3 rounded-lg border border-dashed border-ui-border bg-ui-subtle">
                {t('workflow.tasksEmpty', 'No tasks on this engagement yet — workflow transitions will drop them here.')}
              </p>
            ) : myTasks.length > 0 && (() => {
              const canCollapse = myTasks.length > TASKS_COLLAPSED_LIMIT;
              const visible = tasksExpanded || !canCollapse ? myTasks : myTasks.slice(0, TASKS_COLLAPSED_LIMIT);
              return (
                <div className="space-y-1">
                  {visible.map(task => (
                    <TaskCard
                      key={task.task_id}
                      task={task}
                      compact
                      disabled={task.permission_denied}
                      lockedReason={
                        task.gate_enforced
                          ? t('workflow.gateEnforced', 'Required before advancing the engagement')
                          : task.permission_denied
                            ? t('workflow.noPermission', 'You do not have permission to complete this task')
                            : undefined
                      }
                      onStatusError={msg => setExecuteError(msg)}
                    />
                  ))}
                  {canCollapse && (
                    <button
                      type="button"
                      onClick={() => setTasksExpanded(e => !e)}
                      className="self-start text-xs font-medium text-accent-text hover:underline px-3 py-1"
                    >
                      {tasksExpanded ? t('common.seeLess', 'See less') : t('common.seeMore', `See more (${myTasks.length - TASKS_COLLAPSED_LIMIT})`)}
                    </button>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {/* transitions — transitions blocked because the user lacks the role/authority show as
            informational info boxes (not clickable buttons); gate-blocked ones stay visible with
            their reason/resolution hint since that's a blockage on the engagement, not the user */}
        {engagementId && (() => {
          const allTransitions = transitions ?? [];
          const visibleTransitions = allTransitions.filter((tr) => tr.executable || (
            !tr.permission_denied
            && !(tr.resolution?.action_type === 'open_approval' && rejectedApprovalTypes.has(tr.resolution.approval_type ?? ''))
          ));
          const deniedTransitions = allTransitions.filter((tr) => tr.permission_denied);
          if (!transitionsLoading && visibleTransitions.length === 0 && deniedTransitions.length === 0) return null;
          // Only demote a hold/corrective_loop transition to a secondary pill when a real
          // 'standard' action is ALSO visible alongside it — e.g. Fail QA next to Complete Job at
          // QA Inspected, or Put Job On Hold next to Finish Field Work at Job Execution. When it's
          // the ONLY available action (e.g. Resume Rework alone at QA Rejected), it stays the one
          // primary button — demoting a lone action would leave the row with no primary at all.
          const hasStandardSibling = visibleTransitions.some((t) => t.category === 'standard');
          return (
          <CardSection>
            {transitionsLoading && !transitions ? (
              <div className="flex justify-center py-3"><Spinner size="sm" /></div>
            ) : (
              <div className="space-y-2">
                {/* Permission-denied info boxes — shown for transitions the current user cannot run
                    due to their role. Uses the backend-supplied reason ("Only [role] can do this.")
                    so the message is always accurate regardless of tenant role renaming. */}
                {deniedTransitions.map((tr) => (
                  <div key={`denied-${tr.transition_code}`} className="flex items-start gap-2 rounded-lg border border-ui-border bg-ui-subtle px-3 py-2.5 text-[11px] text-ui-text2">
                    <svg className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-ui-text3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    <p>
                      {tr.reason
                        ? `${tr.reason.replace(/\.$/, '')} — they can run "${tr.action_label}" from this status.`
                        : `Only authorized roles can run "${tr.action_label}" from this status.`}
                    </p>
                  </div>
                ))}
                {rejectionNoticeText && (
                  <p className="rounded-lg border border-danger/40 bg-danger/10 px-3 py-2 text-xs font-medium text-danger-text">{rejectionNoticeText}</p>
                )}
                {visibleTransitions.map((tr) => {
                  const isTerminal = TERMINAL_CATEGORIES.has(tr.category);
                  const isPending = pendingTransitionCode === tr.transition_code;
                  const isSchedulingOpen = schedulingTransition === tr.transition_code;
                  // A 'hold' transition (e.g. put_job_on_hold) sitting alongside a real 'standard'
                  // action (e.g. finish_field_work) must not render as a second stacked primary
                  // button — every other status shows exactly one solid primary action, with
                  // secondary ones as small pills (see the hardcoded Put on hold/Close early pills
                  // further down this file, which this matches the look of, though those are a
                  // separate generic engagement-level mechanism, not a workflow transition).
                  const isSecondaryPill = (tr.category === 'hold' || tr.category === 'corrective_loop') && hasStandardSibling;
                  return (
                  <div key={tr.transition_code} ref={(el) => { transitionRowRefs.current[tr.transition_code] = el; }} className={isSchedulingOpen ? 'rounded-lg border border-accent/25 overflow-hidden' : ''}>
                    {isSecondaryPill ? (
                      <button
                        type="button"
                        onClick={() => clickTransition(tr)}
                        disabled={executing}
                        aria-busy={isPending}
                        className={cn(
                          'inline-flex items-center gap-1 rounded-full border border-amber-300/70 px-2.5 py-1 text-[11px] font-medium text-amber-700 transition-colors dark:border-amber-600/40 dark:text-amber-400',
                          executing ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:bg-amber-50 dark:hover:bg-amber-900/20',
                        )}
                      >
                        {isPending ? t('workflow.transitionInProgress', 'Working…') : tr.action_label}
                      </button>
                    ) : (
                    <button
                      type="button"
                      onClick={() => clickTransition(tr)}
                      disabled={executing}
                      aria-busy={isPending}
                      className={
                        isSchedulingOpen
                          ? cn(
                              'flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-xs font-semibold text-accent-text bg-accent-soft/25 border-b border-accent/15 transition',
                              executing ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:bg-ui-hover',
                            )
                          : cn(
                              'flex w-full items-center justify-between gap-2 rounded-full border px-4 py-2.5 text-left text-xs font-semibold transition active:scale-[0.99]',
                              isTerminal
                                ? 'border-danger/40 bg-danger/10 text-danger-text '
                                : 'btn-sheen border-transparent text-on-primary',
                              executing
                                ? 'cursor-not-allowed opacity-60'
                                : isTerminal
                                  ? 'cursor-pointer hover:border-danger/60 hover:bg-danger/15 hover:shadow-md '
                                  : 'cursor-pointer',
                            )
                      }
                    >
                      <span>{isPending ? t('workflow.transitionInProgress', 'Working…') : tr.action_label}</span>
                      {isPending && (
                        <svg className="h-3.5 w-3.5 flex-shrink-0 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                      )}
                      {!isPending && isSchedulingOpen && (
                        <svg className="h-3.5 w-3.5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                          <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      )}
                    </button>
                    )}
                    {/* Ready-to-run description for specific transitions worth explaining before
                        the click — TRANSITION_READY_HINTS below is a targeted lookup by code, not
                        a generic mechanism, since most transitions are self-explanatory from their
                        label alone. */}
                    {tr.executable && TRANSITION_READY_HINTS[tr.transition_code] && (
                      <p className="mt-1 pl-1 text-xs text-ui-text3">{TRANSITION_READY_HINTS[tr.transition_code]}</p>
                    )}
                    {restrictedTransition && shouldShowRestrictedReason(tr, restrictedTransition) && (
                      <p className="mt-1 pl-1 text-xs text-warning-text">{restrictedReasonText(tr, restrictedTransition)}</p>
                    )}
                    {!tr.executable && tr.resolution && tr.transition_code !== 'schedule_job' && (
                      <div className="mt-1 pl-1">
                        {tr.resolution.action_type === 'update_engagement_data' && resolvingTransition !== tr.transition_code && (
                          <button type="button" className="text-xs text-accent-text hover:underline" onClick={() => openResolveForm(tr)}>
                            {t('workflow.resolveAddDetails', 'Add details')}
                          </button>
                        )}
                        {tr.resolution.action_type === 'add_contact_participant' && engagement?.property_id && engagementId && (
                          <ContactLinkSelect
                            propertyId={engagement.property_id}
                            engagementId={engagementId}
                            onLinked={() => void refetchTransitions()}
                          />
                        )}
                        {tr.resolution.action_type === 'open_media' && (
                          <p className="text-xs text-ui-text3">
                            {t('workflow.resolveOpenMediaHint', 'Add at least one photo for this engagement from the property\'s Media tab, then try again.')}
                          </p>
                        )}
                        {tr.resolution.action_type === 'open_measurement' && (
                          <p className="text-xs text-ui-text3">
                            {/* Prefer the gate's own live reason — the backend already tells us
                                WHY (e.g. "an assessment exists but Existing Roof Layers/Number of
                                Stories still need filling in on the Assessment Summary" once one
                                has been generated), which is far more useful than this generic
                                fallback and stays accurate as that reason changes over time. */}
                            {shortBlockedReason(tr.reason) || t('workflow.resolveOpenMeasurementHint', 'Record a measurement for this property linked to this engagement, then try again.')}
                          </p>
                        )}
                        {tr.resolution.action_type === 'open_tasks' && (
                          <p className="text-xs text-ui-text3">
                            {t('workflow.resolveOpenTasksHint', 'Complete the outstanding task(s) for this engagement, then try again.')}
                          </p>
                        )}
                        {tr.resolution.action_type === 'open_approval' && (
                          <p className="text-xs text-ui-text3">
                            {tr.resolution.required_role_label
                              ? t('workflow.resolveOpenApprovalHintRole', 'Waiting on approval from {{role}} — see Pending Approvals below.', { role: tr.resolution.required_role_label })
                              : t('workflow.resolveOpenApprovalHint', 'Waiting on approval — see Pending Approvals below.')}
                          </p>
                        )}
                      </div>
                    )}
                    {resolvingTransition === tr.transition_code && (
                      <div className="mt-1.5 space-y-1.5 rounded-lg border border-ui-border bg-ui-subtle p-2.5">
                        {(tr.resolution?.fields ?? []).map((field) => (
                          <div key={field}>
                            <label className="mb-0.5 block text-xs text-ui-text3">{humanize(field.replace(/_uploaded$/, ''))}</label>
                            {field.endsWith('_uploaded') ? (
                              <label className="flex items-center gap-1.5 text-xs text-ui-text">
                                <input
                                  type="checkbox"
                                  checked={resolveValues[field] === 'true'}
                                  onChange={(e) => setResolveValues((prev) => ({ ...prev, [field]: e.target.checked ? 'true' : 'false' }))}
                                />
                                {t('workflow.resolveMarkComplete', 'Mark as complete')}
                              </label>
                            ) : field === 'project_manager_id' && engagementId ? (
                              // JOB-FEAT-01 — richer picker (eligible PMs + active workload +
                              // advisory recommendation) in place of the generic role-members
                              // dropdown; see submitResolveData below for the matching write path.
                              <PmAssignmentSelect
                                engagementId={engagementId}
                                value={resolveValues[field] ?? ''}
                                onChange={(v) => setResolveValues((prev) => ({ ...prev, [field]: v }))}
                              />
                            ) : ROLE_LOOKUP_FIELDS[field] ? (
                              <RoleLookupSelect
                                role={ROLE_LOOKUP_FIELDS[field]}
                                value={resolveValues[field] ?? ''}
                                onChange={(v) => setResolveValues((prev) => ({ ...prev, [field]: v }))}
                              />
                            ) : (
                              <input
                                value={resolveValues[field] ?? ''}
                                onChange={(e) => setResolveValues((prev) => ({ ...prev, [field]: e.target.value }))}
                                className="w-full rounded-lg border border-ui-border bg-ui-input px-2.5 py-1.5 text-xs text-ui-text focus:border-ui-ring focus:outline-none focus:ring-1 focus:ring-ui-ring"
                              />
                            )}
                          </div>
                        ))}
                        {resolveError && <p className="text-xs text-danger-text">{resolveError}</p>}
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="sm" onClick={() => { setResolvingTransition(null); setResolveError(null); }}>
                            {t('workflow.cancel', 'Cancel')}
                          </Button>
                          <Button
                            size="sm"
                            loading={savingResolveData}
                            disabled={Object.entries(resolveValues).some(([f, v]) => !f.endsWith('_uploaded') && !v.trim())}
                            onClick={() => void submitResolveData()}
                          >
                            {t('workflow.save', 'Save')}
                          </Button>
                        </div>
                      </div>
                    )}
                    {activeTransition === tr.transition_code && (
                      <div className="mt-1.5 space-y-1.5 rounded-lg border border-ui-border bg-ui-subtle p-2.5">
                        <textarea
                          value={reasonText}
                          onChange={(e) => setReasonText(e.target.value)}
                          placeholder={t('workflow.reasonPlaceholder', 'Reason (required)')}
                          rows={2}
                          className="w-full rounded-lg border border-ui-border bg-ui-input px-2.5 py-1.5 text-xs text-ui-text focus:border-ui-ring focus:outline-none focus:ring-1 focus:ring-ui-ring"
                        />
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="sm" onClick={() => { setActiveTransition(null); setReasonText(''); }}>
                            {t('workflow.cancel', 'Cancel')}
                          </Button>
                          <Button size="sm" loading={executing} disabled={!reasonText.trim()} onClick={confirmReasoned}>
                            {t('workflow.confirmTransition', 'Confirm')}
                          </Button>
                        </div>
                      </div>
                    )}
                    {schedulingTransition === tr.transition_code && engagementId && (() => {
                      const captureKey = tr.resolution?.action_type === 'business_capture' ? tr.resolution.action_key : undefined;
                      const CaptureComponent = captureKey ? BUSINESS_CAPTURE_COMPONENTS[captureKey] : undefined;
                      if (!CaptureComponent) return null;
                      return (
                        <div className="px-2.5">
                          <CaptureComponent
                            engagementId={engagementId}
                            onCancel={() => setSchedulingTransition(null)}
                            onScheduled={() => {
                              setSchedulingTransition(null);
                              void runTransition(tr.transition_code);
                            }}
                          />
                        </div>
                      );
                    })()}
                    {qaFailFormOpen && tr.transition_code === 'fail_qa_rework' && engagementId && (
                      <div className="mt-1.5 rounded-lg border border-brand-dark/25 bg-brand-light/10 p-3">
                        <div className="mb-2 flex items-center justify-between">
                          <p className="text-xs font-bold text-brand-dark">{tr.action_label || humanize(tr.transition_code)}</p>
                          <button
                            type="button"
                            onClick={() => setQaFailFormOpen(false)}
                            aria-label={t('workflow.close', 'Close')}
                            className="text-base leading-none text-ui-text3 hover:text-ui-text"
                          >
                            ×
                          </button>
                        </div>
                        <QaFailForm
                          engagementId={engagementId}
                          onCancel={() => setQaFailFormOpen(false)}
                          onFailed={() => {
                            setQaFailFormOpen(false);
                            refreshPropertyWorkspace();
                            void refetchTransitions();
                          }}
                        />
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            )}
          </CardSection>
          );
        })()}

        {/* JOB-FEAT-05 — "Reassign Crew" while a failed QA is being reworked (Job Execution after
            Fail QA). Reuses the SAME scheduling modal as "Schedule / reschedule Job" —
            reassigning is functionally identical to a reschedule that only changes crew_id, no
            new backend needed. */}
        {engagementId && qaFailedRework && (
          <button
            type="button"
            onClick={() => setSchedulingJob(true)}
            className="mx-3 mb-2 self-start rounded-full border border-ui-border px-2.5 py-1 text-[11px] font-medium text-ui-text2 hover:border-brand-dark hover:bg-brand-light/10"
          >
            {t('jobs.reassignCrew', 'Reassign Crew')}
          </button>
        )}

        {/* Access restriction — hidden from UI; logic kept intact so SAM can still set it */}

        {/* warning-required banner */}
        {warning && (
          <div className="rounded-lg border border-warning/40 bg-warning/10 px-3 py-2 ">
            <p className="text-xs text-warning-text">{warning.message}</p>
            <div className="mt-2 flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setWarning(null)}>{t('workflow.cancel', 'Cancel')}</Button>
              <Button size="sm" loading={executing} onClick={acknowledgeWarning}>
                {t('workflow.acknowledgeContinue', 'Acknowledge & continue')}
              </Button>
            </div>
          </div>
        )}

        {/* blocking governance gate (or closed-early/on-hold) rejection — distinct from a generic
            error since `blockedMessage` is the gate's own failure_message, not a fallback string */}
        {blockedMessage && (
          <div className="rounded-lg border border-danger/40 bg-danger/10 px-3 py-2 ">
            <p className="text-micro font-semibold uppercase tracking-wide text-danger-text">
              {t('workflow.blocked', 'Blocked')}
            </p>
            <p className="mt-0.5 text-xs text-danger-text">{blockedMessage}</p>
          </div>
        )}

        {/* execute error */}
        {executeError && (
          <div className="rounded-lg border border-danger/40 bg-danger/10 px-3 py-2 text-xs text-danger-text ">
            {executeError}
          </div>
        )}

        {/* approval-decided hint pointing back up at the transition — kept outside the
            approvals.length gate below, since approving/rejecting can immediately drop the
            decided request out of that filtered list (resolved_status_code isn't stamped until
            the transition it unblocks actually runs), which would otherwise hide this message
            in the same instant it should appear. */}
        {approvalSuccessMessage && (
          <div className="flex items-start gap-1.5 rounded-lg border border-success/40 bg-success/10 px-3 py-2 ">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="mt-0.5 flex-shrink-0 text-success-text">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            <p className="text-xs font-medium text-success-text">{approvalSuccessMessage}</p>
          </div>
        )}

        {/* pending approvals — only rendered when at least one is present */}
        {approvals.length > 0 && (
          <>
            <CardSection title={t('workflow.pendingApprovals', 'Pending Approvals')}>
              <div className="space-y-2">
                {approvals.map((a) => (
                  <div key={a.approval_request_id} className="rounded-lg border border-ui-border bg-ui-subtle px-3 py-2">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium text-ui-text">{humanize(a.approval_type)}</span>
                      <span className="text-micro uppercase tracking-wide text-ui-text3">{a.status}</span>
                    </div>
                    {a.required_role_label && a.status === 'pending' && (
                      <p className="mt-0.5 text-xs text-ui-text3">
                        {t('workflow.approvalRequires', 'Requires: {{role}}', { role: a.required_role_label })}
                      </p>
                    )}
                    {a.status !== 'pending' ? (
                      <p className="mt-1 text-xs text-ui-text3">
                        {a.decided_at
                          ? (a.status === 'approved'
                              ? t('workflow.approvalDecidedApprovedOn', 'Approved on {{date}}.', { date: fmtDate(a.decided_at) })
                              : t('workflow.approvalDecidedRejectedOn', 'Rejected on {{date}}.', { date: fmtDate(a.decided_at) }))
                          : (a.status === 'approved'
                              ? t('workflow.approvalDecidedApproved', 'Approved.')
                              : t('workflow.approvalDecidedRejected', 'Rejected.'))}
                        {a.decision_comments && ` — ${a.decision_comments}`}
                      </p>
                    ) : !a.can_decide ? (
                      <p className="mt-1 text-xs text-ui-text3">
                        {t('workflow.approvalAwaitingOther', 'Awaiting decision from the assigned approver.')}
                      </p>
                    ) : rejectingId === a.approval_request_id ? (
                      <div className="mt-2 space-y-1.5">
                        <textarea
                          value={rejectComment}
                          onChange={(e) => setRejectComment(e.target.value)}
                          placeholder={t('workflow.rejectCommentPlaceholder', 'Reason (optional)')}
                          rows={2}
                          className="w-full rounded-lg border border-ui-border bg-ui-input px-2.5 py-1.5 text-xs text-ui-text focus:border-ui-ring focus:outline-none focus:ring-1 focus:ring-ui-ring"
                        />
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="sm" onClick={() => { setRejectingId(null); setRejectComment(''); }}>
                            {t('workflow.cancel', 'Cancel')}
                          </Button>
                          <Button variant="destructive" size="sm" loading={deciding} onClick={() => reject(a.approval_request_id)}>
                            {t('workflow.confirmReject', 'Reject')}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-2 flex justify-end gap-2">
                        <Button variant="outline" size="sm" loading={deciding} onClick={() => setRejectingId(a.approval_request_id)}>
                          {t('workflow.reject', 'Reject')}
                        </Button>
                        <Button size="sm" loading={deciding} onClick={() => approve(a.approval_request_id)}>
                          {t('workflow.approve', 'Approve')}
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardSection>
            {approvalError && (
              <div className="rounded-lg border border-danger/40 bg-danger/10 px-3 py-2 text-xs text-danger-text ">
                {approvalError}
              </div>
            )}
          </>
        )}

        {/* Secondary actions while job is on hold: Update Hold Details, Notify Customer, Reschedule */}
        {engagementId && state && state.current_status_code === 'job_execution_on_hold' && !engagement?.closed_early && (
          <div className="flex items-center gap-2 pt-1 flex-wrap">
            <button
              type="button"
              onClick={() => {
                const d = (engagement?.data ?? {}) as Record<string, unknown>;
                setHoldUpdateNotes(typeof d.hold_notes === 'string' ? d.hold_notes : '');
                setHoldUpdateError(null);
                setHoldUpdateEditorOpen(true);
              }}
              className="flex items-center gap-1.5 rounded-[8px] border border-[#ededed] bg-white px-3 py-[7px] text-[12.5px] font-semibold text-[#575757] hover:bg-ui-subtle transition-colors dark:bg-ui-card dark:border-ui-border"
            >
              ✎ {t('jobs.updateHoldDetails', 'Update Hold Details')}
            </button>
            <button
              type="button"
              onClick={() => void submitNotifyCustomerHold()}
              disabled={notifyingCustomer}
              className="flex items-center gap-1.5 rounded-[8px] border border-[#ededed] bg-white px-3 py-[7px] text-[12.5px] font-semibold text-[#575757] hover:bg-ui-subtle transition-colors dark:bg-ui-card dark:border-ui-border disabled:opacity-60"
            >
              ✆ {t('jobs.notifyCustomer', 'Notify Customer')}
            </button>
            <button
              type="button"
              onClick={() => setReschedulingJob(true)}
              className="flex items-center gap-1.5 rounded-[8px] border border-[#ededed] bg-white px-3 py-[7px] text-[12.5px] font-semibold text-[#575757] hover:bg-ui-subtle transition-colors dark:bg-ui-card dark:border-ui-border"
            >
              ↻ {t('workflow.reschedule', 'Reschedule')}
            </button>
            {notifyCustomerError && <p className="w-full text-[11px] text-danger-text">{notifyCustomerError}</p>}
            {notifyCustomerSent && <p className="w-full text-[11px] text-green-700 dark:text-green-400">{t('jobs.customerNotifiedSent', 'Customer notified.')}</p>}
          </div>
        )}

        {/* Put on hold / Close early / Reschedule — for statuses that are not already on hold */}
        {engagementId && state && !engagement?.closed_early && !state.on_hold && state.current_status_code !== 'job_execution_on_hold' && (
          <div className="flex items-center gap-1.5 pt-1 flex-wrap">
            {RESCHEDULE_STATUSES.has(state.current_status_code) && (
              <button
                type="button"
                onClick={() => setReschedulingJob(true)}
                className="flex items-center gap-0.5 rounded-full border border-brand-dark/30 px-2 py-0.5 text-[10px] font-medium text-brand-dark transition-colors hover:bg-brand-light/20 whitespace-nowrap"
              >
                ↻ {t('workflow.reschedule', 'Reschedule')}
              </button>
            )}
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => { setHoldReason(''); setHoldError(null); setHoldEditorOpen(true); }}
              className="border-warning/50 text-warning-text hover:border-warning hover:bg-warning/10"
            >
              <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" className="flex-shrink-0" aria-hidden="true">
                <rect x="6" y="4" width="4" height="16" rx="1.5"/><rect x="14" y="4" width="4" height="16" rx="1.5"/>
              </svg>
              {t('workflow.putOnHold', 'Put on hold')}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => { setCloseReason(''); setCloseEarlyError(null); setCloseEarlyEditorOpen(true); }}
              className="border-danger/40 text-danger-text hover:border-danger hover:bg-danger/10"
            >
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className="flex-shrink-0" aria-hidden="true">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
              {t('workflow.closeEarly', 'Close early')}
            </Button>
          </div>
        )}
        {holdEditorOpen && (
          <div className="mt-1.5 space-y-1.5 rounded-lg border border-ui-border bg-ui-subtle p-2.5">
            <textarea
              value={holdReason}
              onChange={(e) => setHoldReason(e.target.value)}
              placeholder={t('workflow.holdReasonPlaceholder', 'Reason (required)')}
              rows={2}
              className="w-full rounded-lg border border-ui-border bg-ui-input px-2.5 py-1.5 text-xs text-ui-text focus:border-ui-ring focus:outline-none focus:ring-1 focus:ring-ui-ring"
            />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setHoldEditorOpen(false)}>{t('workflow.cancel', 'Cancel')}</Button>
              <Button size="sm" loading={holding} disabled={!holdReason.trim()} onClick={() => void submitPutOnHold()}>{t('workflow.putOnHold', 'Put on hold')}</Button>
            </div>
          </div>
        )}
        {holdError && <p className="text-xs text-danger-text">{holdError}</p>}
        {holdUpdateEditorOpen && (
          <div className="mt-1.5 space-y-1.5 rounded-lg border border-ui-border bg-ui-subtle p-2.5">
            <p className="text-[10.5px] font-semibold text-ui-text">{t('jobs.updateHoldNotes', 'Update hold notes')}</p>
            <textarea
              value={holdUpdateNotes}
              onChange={(e) => setHoldUpdateNotes(e.target.value)}
              placeholder={t('jobs.holdNotesPlaceholder', 'Add or update hold notes…')}
              rows={3}
              className="w-full rounded-lg border border-ui-border bg-ui-input px-2.5 py-1.5 text-xs text-ui-text focus:border-brand-dark focus:outline-none focus:ring-1 focus:ring-brand-dark"
            />
            {holdUpdateError && <p className="text-[11px] text-danger-text">{holdUpdateError}</p>}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setHoldUpdateEditorOpen(false)}>{t('workflow.cancel', 'Cancel')}</Button>
              <Button size="sm" onClick={() => void submitUpdateHoldDetails()}>{t('jobs.saveHoldDetails', 'Save')}</Button>
            </div>
          </div>
        )}
        {closeEarlyEditorOpen && (
          <div className="mt-1.5 space-y-1.5 rounded-lg border border-danger/40 bg-danger/10 p-2.5 ">
            <p className="text-xs text-danger-text">
              {t('workflow.closeEarlyHint', 'Owner only. Permanent — no further transitions will be possible on this engagement.')}
            </p>
            <select
              value={closureType}
              onChange={(e) => setClosureType(e.target.value as 'closed_lost' | 'future_follow_up')}
              className="w-full rounded-lg border border-ui-border bg-ui-input px-2.5 py-1.5 text-xs text-ui-text focus:border-ui-ring focus:outline-none focus:ring-1 focus:ring-ui-ring"
            >
              <option value="closed_lost">{t('workflow.closedLost', 'Closed – Lost')}</option>
              <option value="future_follow_up">{t('workflow.futureFollowUp', 'Future Follow-Up')}</option>
            </select>
            <textarea
              value={closeReason}
              onChange={(e) => setCloseReason(e.target.value)}
              placeholder={t('workflow.closeReasonPlaceholder', 'Reason (required)')}
              rows={2}
              className="w-full rounded-lg border border-ui-border bg-ui-input px-2.5 py-1.5 text-xs text-ui-text focus:border-ui-ring focus:outline-none focus:ring-1 focus:ring-ui-ring"
            />
            {closeEarlyError && <p className="text-xs text-danger-text">{closeEarlyError}</p>}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setCloseEarlyEditorOpen(false)}>{t('workflow.cancel', 'Cancel')}</Button>
              <Button variant="destructive" size="sm" loading={closingEarly} disabled={!closeReason.trim()} onClick={() => void submitCloseEarly()}>
                {t('workflow.closeEarly', 'Close early')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkflowCard;
